// GENERATED FILE — DO NOT EDIT.
// Source of truth: backend/src/shared/caption-layout/index.js
// Regenerate: node backend/scripts/syncSharedToFrontend.js
/**
 * Caption layout — the ONE place that decides how a caption is measured, which font face it
 * renders in, whether it gets a drawn box, and where its words break into lines.
 *
 * Consumed by BOTH renderers so they can never drift apart:
 *   - backend/src/services/assGeneratorService.js (the ffmpeg/libass burn)
 *   - frontend/src/shared/captionLayout.js (the live editor preview — a GENERATED ESM copy of
 *     this file; regenerate with `node backend/scripts/syncSharedToFrontend.js`, and
 *     backend/tests/sharedSync.test.js fails when the copy is stale)
 *
 * Rules for this file: plain CommonJS, zero `require`s, no Node/DOM APIs — it must run
 * unchanged in the browser. Every measurement is in VIDEO pixels (PlayResX/PlayResY space),
 * derived from a 1080-high design canvas via renderScale = videoHeight / 1080.
 */

// libass renders glyphs at ~0.63× the nominal ASS Fontsize (measured: 10 "H" at Fontsize 100
// render 505px wide / 44px tall, vs the TTF's 808px / ~70px). So the on-screen width is well
// below the raw TTF advance. These scales map the Montserrat-TTF charWidths table to libass's
// actual rendered width per font (realRenderEm / MontserratTtfEm), so the drawn box hugs the
// burned text the way the CSS preview hugs its text. Without this the box is sized ~1.6× too wide.
// Values measured from the bundled TTFs: scale = Σ(advance/(usWinAscent+usWinDescent)) over a
// pangram sample ÷ the same sum over the Montserrat-Bold charWidths table (scripts kept the
// original empirical anchors: Montserrat 0.63, Oswald 0.44, Inter render ratio 0.699).
// Lookup is first-match on substring — keep dedicated weight faces BEFORE their base family.
const FONT_WIDTH_SCALE = {
  'montserrat medium': 0.62, 'montserrat extrabold': 0.65, 'montserrat black': 0.66,
  'poppins medium': 0.52, 'poppins black': 0.54, 'lato black': 0.63,
  'archivo black': 0.82, 'bebas neue': 0.47,
  oswald: 0.44, inter: 0.65, anton: 0.43, lato: 0.73, poppins: 0.53, roboto: 0.72, bangers: 0.38,
};
const fontWidthScale = (fontName) => {
  const f = (fontName || '').toLowerCase();
  for (const key in FONT_WIDTH_SCALE) {
    if (f.includes(key)) return FONT_WIDTH_SCALE[key];
  }
  return 0.63; // Montserrat / Poppins / unknown
};

// Real per-character advance ratios (advance / unitsPerEm) read from the bundled Inter TTFs.
// Approximating Inter with the Montserrat table × a single scalar underestimated wide lines by
// up to ~9% (Inter's caps/digits are proportionally wider than Montserrat's), so the drawn box
// came out narrower than the burned text and the side padding collapsed (e.g. Liquid Glass).
// libass sizes a font by its usWinAscent+usWinDescent, so rendered width = em advance × upm/(winAsc+winDesc);
// for Inter that ratio is 0.699.
const INTER_RENDER_RATIO = 0.699;
const INTER_CHAR_WIDTHS = {
  regular: {
    'a': 0.562, 'b': 0.612, 'c': 0.571, 'd': 0.612, 'e': 0.583, 'f': 0.37, 'g': 0.613, 'h': 0.591,
    'i': 0.242, 'j': 0.242, 'k': 0.549, 'l': 0.242, 'm': 0.876, 'n': 0.591, 'o': 0.6, 'p': 0.612,
    'q': 0.612, 'r': 0.376, 's': 0.528, 't': 0.327, 'u': 0.591, 'v': 0.562, 'w': 0.818, 'x': 0.546,
    'y': 0.562, 'z': 0.552, 'A': 0.69, 'B': 0.654, 'C': 0.73, 'D': 0.722, 'E': 0.601, 'F': 0.59,
    'G': 0.746, 'H': 0.743, 'I': 0.269, 'J': 0.571, 'K': 0.672, 'L': 0.565, 'M': 0.903, 'N': 0.753,
    'O': 0.765, 'P': 0.639, 'Q': 0.765, 'R': 0.644, 'S': 0.642, 'T': 0.646, 'U': 0.744, 'V': 0.69,
    'W': 0.985, 'X': 0.682, 'Y': 0.679, 'Z': 0.629, '0': 0.631, '1': 0.407, '2': 0.61, '3': 0.618,
    '4': 0.646, '5': 0.593, '6': 0.62, '7': 0.566, '8': 0.619, '9': 0.62, ' ': 0.281, '.': 0.288,
    ',': 0.288, '!': 0.288, '?': 0.511, ':': 0.288, ';': 0.302, "'": 0.3, '"': 0.466, '`': 0.323,
    '-': 0.46, '_': 0.456, '(': 0.365, ')': 0.365, '/': 0.36, '\\': 0.36, '@': 0.966, '#': 0.633,
    '%': 0.982, '&': 0.644, '+': 0.662, '=': 0.662, '|': 0.333,
  },
  bold: {
    'a': 0.581, 'b': 0.63, 'c': 0.588, 'd': 0.63, 'e': 0.596, 'f': 0.398, 'g': 0.632, 'h': 0.623,
    'i': 0.271, 'j': 0.271, 'k': 0.58, 'l': 0.271, 'm': 0.913, 'n': 0.623, 'o': 0.613, 'p': 0.63,
    'q': 0.63, 'r': 0.407, 's': 0.56, 't': 0.366, 'u': 0.623, 'v': 0.6, 'w': 0.85, 'x': 0.58,
    'y': 0.602, 'z': 0.573, 'A': 0.747, 'B': 0.662, 'C': 0.74, 'D': 0.722, 'E': 0.607, 'F': 0.587,
    'G': 0.75, 'H': 0.747, 'I': 0.281, 'J': 0.584, 'K': 0.719, 'L': 0.565, 'M': 0.932, 'N': 0.762,
    'O': 0.771, 'P': 0.648, 'Q': 0.777, 'R': 0.657, 'S': 0.655, 'T': 0.667, 'U': 0.732, 'V': 0.747,
    'W': 1.038, 'X': 0.738, 'Y': 0.731, 'Z': 0.664, '0': 0.674, '1': 0.431, '2': 0.63, '3': 0.646,
    '4': 0.676, '5': 0.622, '6': 0.649, '7': 0.582, '8': 0.651, '9': 0.649, ' ': 0.237, '.': 0.334,
    ',': 0.334, '!': 0.338, '?': 0.56, ':': 0.334, ';': 0.343, "'": 0.339, '"': 0.552, '`': 0.365,
    '-': 0.468, '_': 0.476, '(': 0.377, ')': 0.377, '/': 0.388, '\\': 0.388, '@': 1.016, '#': 0.649,
    '%': 1.016, '&': 0.672, '+': 0.679, '=': 0.679, '|': 0.372,
  },  // SemiBold (600) is a separate static face ("Inter SemiBold", measured from the bundled
  // Inter-SemiBold.ttf), the Soft Stack template's weight. Same render ratio (0.699).
  semibold: {
    'a': 0.574, 'b': 0.624, 'c': 0.583, 'd': 0.624, 'e': 0.591, 'f': 0.389, 'g': 0.625, 'h': 0.612,
    'i': 0.262, 'j': 0.262, 'k': 0.569, 'l': 0.262, 'm': 0.9, 'n': 0.612, 'o': 0.609, 'p': 0.624,
    'q': 0.624, 'r': 0.397, 's': 0.549, 't': 0.353, 'u': 0.612, 'v': 0.587, 'w': 0.839, 'x': 0.569,
    'y': 0.588, 'z': 0.566, 'A': 0.728, 'B': 0.659, 'C': 0.737, 'D': 0.722, 'E': 0.605, 'F': 0.588,
    'G': 0.749, 'H': 0.746, 'I': 0.277, 'J': 0.58, 'K': 0.703, 'L': 0.565, 'M': 0.922, 'N': 0.759,
    'O': 0.769, 'P': 0.645, 'Q': 0.773, 'R': 0.652, 'S': 0.65, 'T': 0.66, 'U': 0.736, 'V': 0.728,
    'W': 1.02, 'X': 0.72, 'Y': 0.713, 'Z': 0.652, '0': 0.66, '1': 0.423, '2': 0.623, '3': 0.636,
    '4': 0.666, '5': 0.612, '6': 0.64, '7': 0.576, '8': 0.64, '9': 0.64, ' ': 0.252, '.': 0.319,
    ',': 0.319, '!': 0.321, '?': 0.543, ':': 0.319, ';': 0.329, '"': 0.523, '`': 0.351, '-': 0.465,
    '_': 0.469, '(': 0.373, ')': 0.373, '/': 0.379, '\\': 0.379, '@': 0.999, '#': 0.644, '%': 1.004,
    '&': 0.663, '+': 0.673, '=': 0.673, '|': 0.359,
  },

  // ExtraBold (800) and Black (900) are separate static faces ("Inter ExtraBold", "Inter Black"),
  // measured from the bundled Inter-ExtraBold.ttf / Inter-Black.ttf. Same render ratio (0.699).
  extrabold: {
    '0': 0.692, '1': 0.441, '2': 0.638, '3': 0.657, '4': 0.688, '5': 0.634, '6': 0.662, '7': 0.588,
    '8': 0.664, '9': 0.662, 'a': 0.588, 'b': 0.638, 'c': 0.595, 'd': 0.638, 'e': 0.601, 'f': 0.41,
    'g': 0.639, 'h': 0.635, 'i': 0.283, 'j': 0.283, 'k': 0.593, 'l': 0.283, 'm': 0.927, 'n': 0.635,
    'o': 0.619, 'p': 0.638, 'q': 0.638, 'r': 0.42, 's': 0.573, 't': 0.382, 'u': 0.635, 'v': 0.615,
    'w': 0.863, 'x': 0.594, 'y': 0.618, 'z': 0.581, 'A': 0.77, 'B': 0.665, 'C': 0.744, 'D': 0.723,
    'E': 0.61, 'F': 0.585, 'G': 0.752, 'H': 0.749, 'I': 0.286, 'J': 0.59, 'K': 0.738, 'L': 0.565,
    'M': 0.943, 'N': 0.766, 'O': 0.773, 'P': 0.652, 'Q': 0.782, 'R': 0.662, 'S': 0.66, 'T': 0.677,
    'U': 0.727, 'V': 0.77, 'W': 1.059, 'X': 0.761, 'Y': 0.752, 'Z': 0.679, ' ': 0.219, '.': 0.353,
    ',': 0.353, '!': 0.358, '?': 0.579, ':': 0.353, ';': 0.36, "'": 0.354, '"': 0.586, '`': 0.382,
    '-': 0.471, '_': 0.484, '(': 0.382, ')': 0.382, '/': 0.4, '\\': 0.4, '@': 1.036, '#': 0.655,
    '%': 1.029, '&': 0.683, '+': 0.686, '=': 0.686, '|': 0.388,
  },
  black: {
    '0': 0.711, '1': 0.452, '2': 0.646, '3': 0.669, '4': 0.702, '5': 0.646, '6': 0.675, '7': 0.595,
    '8': 0.679, '9': 0.675, 'a': 0.597, 'b': 0.646, 'c': 0.603, 'd': 0.646, 'e': 0.606, 'f': 0.422,
    'g': 0.647, 'h': 0.649, 'i': 0.296, 'j': 0.296, 'k': 0.606, 'l': 0.296, 'm': 0.943, 'n': 0.649,
    'o': 0.625, 'p': 0.646, 'q': 0.646, 'r': 0.434, 's': 0.588, 't': 0.399, 'u': 0.649, 'v': 0.632,
    'w': 0.877, 'x': 0.609, 'y': 0.636, 'z': 0.59, 'A': 0.795, 'B': 0.668, 'C': 0.748, 'D': 0.723,
    'E': 0.613, 'F': 0.584, 'G': 0.754, 'H': 0.75, 'I': 0.291, 'J': 0.596, 'K': 0.759, 'L': 0.565,
    'M': 0.956, 'N': 0.77, 'O': 0.775, 'P': 0.656, 'Q': 0.787, 'R': 0.668, 'S': 0.666, 'T': 0.687,
    'U': 0.722, 'V': 0.795, 'W': 1.082, 'X': 0.786, 'Y': 0.775, 'Z': 0.694, ' ': 0.199, '.': 0.373,
    ',': 0.373, '!': 0.381, '?': 0.601, ':': 0.373, ';': 0.378, "'": 0.372, '"': 0.625, '`': 0.401,
    '-': 0.475, '_': 0.493, '(': 0.388, ')': 0.388, '/': 0.412, '\\': 0.412, '@': 1.059, '#': 0.662,
    '%': 1.044, '&': 0.695, '+': 0.693, '=': 0.693, '|': 0.405,
  },
};

// ── Playfair Display Italic (the Editorial template's serif hero word) ──
// Real advances (advance / unitsPerEm) read from the bundled PlayfairDisplay-Italic.ttf (family
// "Playfair Display", subfamily Italic). Only the italic face is bundled; libass picks it with \i1.
const PLAYFAIR_ITALIC_CHAR_WIDTHS = {
  '0': 0.565, '1': 0.342, '2': 0.503, '3': 0.485, '4': 0.515, '5': 0.459, '6': 0.527, '7': 0.487,
  '8': 0.544, '9': 0.513, 'a': 0.523, 'b': 0.504, 'c': 0.432, 'd': 0.542, 'e': 0.429, 'f': 0.321,
  'g': 0.449, 'h': 0.536, 'i': 0.302, 'j': 0.276, 'k': 0.515, 'l': 0.277, 'm': 0.809, 'n': 0.58,
  'o': 0.488, 'p': 0.551, 'q': 0.507, 'r': 0.426, 's': 0.381, 't': 0.336, 'u': 0.567, 'v': 0.533,
  'w': 0.685, 'x': 0.526, 'y': 0.481, 'z': 0.445, 'A': 0.629, 'B': 0.632, 'C': 0.67, 'D': 0.725,
  'E': 0.61, 'F': 0.566, 'G': 0.705, 'H': 0.755, 'I': 0.341, 'J': 0.636, 'K': 0.675, 'L': 0.586,
  'M': 0.868, 'N': 0.77, 'O': 0.729, 'P': 0.589, 'Q': 0.781, 'R': 0.664, 'S': 0.554, 'T': 0.659,
  'U': 0.682, 'V': 0.632, 'W': 0.902, 'X': 0.699, 'Y': 0.67, 'Z': 0.652, ' ': 0.253, '.': 0.244,
  ',': 0.252, '!': 0.263, '?': 0.438, ':': 0.278, ';': 0.287, "'": 0.187, '"': 0.296, '`': 0.32,
  '-': 0.504, '_': 0.744, '(': 0.361, ')': 0.361, '/': 0.468, '\\': 0.492, '@': 0.848, '#': 0.639,
  '%': 0.766, '&': 0.817, '+': 0.585, '=': 0.663, '|': 0.318,
};
// upm / (usWinAscent + usWinDescent) = 1000 / (1159 + 251).
const PLAYFAIR_RENDER_RATIO = 0.7092;
// LIBASS_FONT_COMPENSATION was calibrated on Montserrat (render ratio 0.6402); Playfair's shorter
// win metrics would burn it ~11% larger than the preview at the same Fontsize (0.6402 / 0.7092).
const PLAYFAIR_SIZE_COMPENSATION = 0.9027;

// ── Kalam (handwritten marker face, Handwritten template) ──
// Real advances read from the bundled kalam-v18-latin-regular.ttf / kalam-v18-latin-700.ttf (one
// RIBBI family "Kalam", so the Bold flag selects the bold face).
const KALAM_CHAR_WIDTHS = {
  regular: {
    '0': 0.5, '1': 0.314, '2': 0.581, '3': 0.571, '4': 0.551, '5': 0.537, '6': 0.556, '7': 0.461,
    '8': 0.602, '9': 0.488, 'a': 0.469, 'b': 0.525, 'c': 0.445, 'd': 0.499, 'e': 0.45, 'f': 0.376,
    'g': 0.475, 'h': 0.536, 'i': 0.238, 'j': 0.254, 'k': 0.469, 'l': 0.245, 'm': 0.796, 'n': 0.533,
    'o': 0.453, 'p': 0.478, 'q': 0.474, 'r': 0.331, 's': 0.426, 't': 0.387, 'u': 0.481, 'v': 0.422,
    'w': 0.673, 'x': 0.427, 'y': 0.469, 'z': 0.44, 'A': 0.608, 'B': 0.637, 'C': 0.599, 'D': 0.645,
    'E': 0.585, 'F': 0.523, 'G': 0.611, 'H': 0.67, 'I': 0.31, 'J': 0.468, 'K': 0.591, 'L': 0.546,
    'M': 0.789, 'N': 0.677, 'O': 0.613, 'P': 0.543, 'Q': 0.641, 'R': 0.584, 'S': 0.531, 'T': 0.535,
    'U': 0.598, 'V': 0.532, 'W': 0.782, 'X': 0.624, 'Y': 0.575, 'Z': 0.62, ' ': 0.401, '.': 0.237,
    ',': 0.228, '!': 0.371, '?': 0.518, ':': 0.246, ';': 0.306, "'": 0.145, '"': 0.331, '`': 0.213,
    '-': 0.459, '_': 0.608, '(': 0.485, ')': 0.504, '/': 0.354, '\\': 0.6, '@': 0.965, '#': 0.656,
    '%': 0.89, '&': 0.704, '+': 0.6, '=': 0.631, '|': 0.368,
  },
  bold: {
    '0': 0.513, '1': 0.302, '2': 0.583, '3': 0.547, '4': 0.545, '5': 0.506, '6': 0.542, '7': 0.472,
    '8': 0.59, '9': 0.506, 'a': 0.51, 'b': 0.567, 'c': 0.489, 'd': 0.539, 'e': 0.488, 'f': 0.435,
    'g': 0.481, 'h': 0.563, 'i': 0.26, 'j': 0.26, 'k': 0.493, 'l': 0.266, 'm': 0.819, 'n': 0.554,
    'o': 0.456, 'p': 0.538, 'q': 0.519, 'r': 0.362, 's': 0.467, 't': 0.451, 'u': 0.486, 'v': 0.422,
    'w': 0.71, 'x': 0.474, 'y': 0.52, 'z': 0.48, 'A': 0.63, 'B': 0.624, 'C': 0.617, 'D': 0.666,
    'E': 0.551, 'F': 0.542, 'G': 0.597, 'H': 0.651, 'I': 0.309, 'J': 0.486, 'K': 0.602, 'L': 0.546,
    'M': 0.756, 'N': 0.631, 'O': 0.619, 'P': 0.549, 'Q': 0.659, 'R': 0.601, 'S': 0.552, 'T': 0.544,
    'U': 0.595, 'V': 0.532, 'W': 0.785, 'X': 0.585, 'Y': 0.561, 'Z': 0.646, ' ': 0.385, '.': 0.237,
    ',': 0.262, '!': 0.478, '?': 0.608, ':': 0.246, ';': 0.306, "'": 0.22, '"': 0.484, '`': 0.258,
    '-': 0.531, '_': 0.619, '(': 0.478, ')': 0.53, '/': 0.282, '\\': 0.6, '@': 1.078, '#': 0.78,
    '%': 0.991, '&': 0.786, '+': 0.6, '=': 0.672, '|': 0.456,
  },
};
// upm / (usWinAscent + usWinDescent) = 1000 / (1063 + 531), same for both faces.
const KALAM_RENDER_RATIO = 0.6274;
// Montserrat anchor (0.6402) / 0.6274: Kalam burns ~2% smaller than the preview otherwise.
const KALAM_SIZE_COMPENSATION = 1.0204;

// ── Caveat Brush (upright brush face, the Handwritten template's hero word) ──
// Real advances read from the bundled caveat-brush-v12-latin-regular.ttf (family "Caveat Brush").
const CAVEAT_BRUSH_CHAR_WIDTHS = {
  '0': 0.447, '1': 0.348, '2': 0.416, '3': 0.426, '4': 0.46, '5': 0.44, '6': 0.47, '7': 0.394,
  '8': 0.454, '9': 0.462, 'a': 0.431, 'b': 0.432, 'c': 0.36, 'd': 0.436, 'e': 0.38, 'f': 0.327,
  'g': 0.401, 'h': 0.426, 'i': 0.227, 'j': 0.235, 'k': 0.416, 'l': 0.24, 'm': 0.588, 'n': 0.444,
  'o': 0.383, 'p': 0.436, 'q': 0.438, 'r': 0.34, 's': 0.34, 't': 0.349, 'u': 0.439, 'v': 0.387,
  'w': 0.527, 'x': 0.405, 'y': 0.411, 'z': 0.408, 'A': 0.467, 'B': 0.482, 'C': 0.443, 'D': 0.534,
  'E': 0.401, 'F': 0.47, 'G': 0.52, 'H': 0.553, 'I': 0.38, 'J': 0.412, 'K': 0.496, 'L': 0.362,
  'M': 0.675, 'N': 0.536, 'O': 0.488, 'P': 0.466, 'Q': 0.485, 'R': 0.493, 'S': 0.432, 'T': 0.477,
  'U': 0.508, 'V': 0.474, 'W': 0.66, 'X': 0.507, 'Y': 0.457, 'Z': 0.496, ' ': 0.212, '.': 0.2,
  ',': 0.203, '!': 0.221, '?': 0.439, ':': 0.207, ';': 0.214, "'": 0.19, '"': 0.35, '`': 0.313,
  '-': 0.407, '_': 0.64, '(': 0.283, ')': 0.301, '/': 0.279, '\\': 0.279, '@': 0.645, '#': 0.553,
  '%': 0.803, '&': 0.49, '+': 0.516, '=': 0.383, '|': 0.252,
};
// upm / (usWinAscent + usWinDescent) = 1000 / (960 + 300).
const CAVEAT_BRUSH_RENDER_RATIO = 0.7937;
// Montserrat anchor (0.6402) / 0.7937: without it Caveat Brush burns ~24% larger than the preview.
const CAVEAT_BRUSH_SIZE_COMPENSATION = 0.8066;

// ── Satisfy (connected script face, Poster template) ──
// Real advances read from the bundled satisfy-v22-latin-regular.ttf (family "Satisfy").
const SATISFY_CHAR_WIDTHS = {
  '0': 0.489, '1': 0.272, '2': 0.448, '3': 0.463, '4': 0.452, '5': 0.444, '6': 0.478, '7': 0.406,
  '8': 0.444, '9': 0.463, 'a': 0.436, 'b': 0.415, 'c': 0.363, 'd': 0.483, 'e': 0.372, 'f': 0.325,
  'g': 0.46, 'h': 0.466, 'i': 0.274, 'j': 0.268, 'k': 0.44, 'l': 0.219, 'm': 0.718, 'n': 0.462,
  'o': 0.394, 'p': 0.451, 'q': 0.463, 'r': 0.406, 's': 0.352, 't': 0.302, 'u': 0.469, 'v': 0.428,
  'w': 0.646, 'x': 0.399, 'y': 0.406, 'z': 0.448, 'A': 0.553, 'B': 0.607, 'C': 0.549, 'D': 0.633,
  'E': 0.523, 'F': 0.586, 'G': 0.612, 'H': 0.649, 'I': 0.537, 'J': 0.501, 'K': 0.497, 'L': 0.492,
  'M': 0.733, 'N': 0.541, 'O': 0.567, 'P': 0.553, 'Q': 0.583, 'R': 0.561, 'S': 0.475, 'T': 0.424,
  'U': 0.574, 'V': 0.504, 'W': 0.817, 'X': 0.579, 'Y': 0.532, 'Z': 0.455, ' ': 0.28, '.': 0.214,
  ',': 0.2, '!': 0.249, '?': 0.413, ':': 0.227, ';': 0.213, "'": 0.113, '"': 0.249, '`': 0.567,
  '-': 0.354, '_': 0.439, '(': 0.234, ')': 0.204, '/': 0.263, '\\': 0.273, '@': 0.648, '#': 0.418,
  '%': 0.574, '&': 0.461, '+': 0.351, '=': 0.374, '|': 0.254,
};
// upm / (usWinAscent + usWinDescent) = 1024 / (957 + 501).
const SATISFY_RENDER_RATIO = 0.7023;
// Montserrat anchor (0.6402) / 0.7023.
const SATISFY_SIZE_COMPENSATION = 0.9116;
// Anton (bundled anton-v27-latin-regular.ttf): upm 2048, usWinAscent 2876, usWinDescent 674, so
// libass renders it at 2048 / 3550 = 0.5769 of the Fontsize, ~11% smaller than the preview
// without this (0.6402 / 0.5769). Widths keep the FONT_WIDTH_SCALE entry.
const ANTON_SIZE_COMPENSATION = 1.1097;

// ── The other bundled Latin families ──
// libass sizes a font so that usWinAscent + usWinDescent spans the Fontsize, so a face with a
// taller win box than Montserrat's (render ratio 0.6402, the LIBASS_FONT_COMPENSATION anchor)
// burns smaller than the preview at the same Fontsize and a shorter one burns larger: Poppins
// -12%, Oswald -9%, Bangers -12%, Inter +9% before this. Compensation is 0.6402 / the face's
// upm / (usWinAscent + usWinDescent), read from the bundled Google-Fonts TTFs. Families with their
// own measured block above (Playfair, Kalam, Caveat Brush, Satisfy, Anton) are not listed; Lato
// and Roboto ship as two different builds under one family name, so they stay uncompensated.
const MONTSERRAT_RENDER_RATIO = 0.6402;
const LATIN_RENDER_RATIO = {
  poppins: 0.5675, oswald: 0.5875, bangers: 0.5692, inter: 0.6992, 'archivo black': 0.7424, 'bebas neue': 0.7692,
};
const latinSizeCompensation = (fontName) => {
  const f = (fontName || '').toLowerCase();
  for (const key in LATIN_RENDER_RATIO) {
    if (f.includes(key)) return Math.round((MONTSERRAT_RENDER_RATIO / LATIN_RENDER_RATIO[key]) * 10000) / 10000;
  }
  return 1;
};

// CSS `line-height: normal` of each caption family, (hhea ascender - descender + lineGap) / upm
// read from the bundled TTFs (the Google-Fonts builds the editor loads). The preview stacks
// karaoke lines by it when the style sets no lineHeight (CaptionContent renderKaraoke), and the
// burn places those lines itself, so it carries the same number (face.normalLineHeight). The
// Indic faces keep theirs in INDIC_SCRIPTS.
const NORMAL_LINE_HEIGHT = {
  montserrat: 1.219, poppins: 1.5, oswald: 1.482, bangers: 1.064, inter: 1.21, lato: 1.2, roboto: 1.172,
  kalam: 1.594, 'caveat brush': 1.26, satisfy: 1.44, anton: 1.505, 'archivo black': 1.088, 'bebas neue': 1.2,
  'playfair display': 1.333,
};
const DEFAULT_NORMAL_LINE_HEIGHT = 1.2;
const normalLineHeightOf = (fontName) => {
  const f = (fontName || '').toLowerCase();
  for (const key in NORMAL_LINE_HEIGHT) {
    if (f.includes(key)) return NORMAL_LINE_HEIGHT[key];
  }
  return DEFAULT_NORMAL_LINE_HEIGHT;
};

// ── Devanagari (Hindi) caption metrics ──
// None of the Latin caption fonts carry Devanagari glyphs (the bundled assets are latin-subset),
// so Devanagari captions are routed to the bundled Noto Sans Devanagari faces (see generateASS).
// Real advances (advance / unitsPerEm) read from NotoSansDevanagari-Regular.ttf. The non-spacing
// matras/virama (ु ू े ै ् ं ँ …) are true zero-advance combining marks; the old defaultWidth
// path counted each as a full character, overestimating Hindi line width by ~50% — mis-sized
// drawn boxes and premature wraps. Spacing matras (ा ि ी ो …) keep their real 0.259 advance.
// Conjunct half-forms shape slightly narrower than the full consonants summed here, so estimates
// lean a touch wide — the safe direction for a drawn box. Latin entries cover Hinglish text.
const NOTO_DEVA_CHAR_WIDTHS = {
  ' ': 0.26, '!': 0.383, '"': 0.502, '#': 0.653, '$': 0.572, '%': 0.828, '&': 0.732, "'": 0.326, '(': 0.354, ')': 0.354,
  '*': 0.607, '+': 0.551, ',': 0.268, '-': 0.34, '.': 0.268, '/': 0.429, '0': 0.551, '1': 0.551, '2': 0.551, '3': 0.551,
  '4': 0.551, '5': 0.551, '6': 0.551, '7': 0.551, '8': 0.551, '9': 0.551, ':': 0.294, ';': 0.294, '<': 0.551, '=': 0.551,
  '>': 0.551, '?': 0.518, '@': 0.899, 'A': 0.639, 'B': 0.65, 'C': 0.632, 'D': 0.73, 'E': 0.556, 'F': 0.519, 'G': 0.728,
  'H': 0.741, 'I': 0.339, 'J': 0.273, 'K': 0.619, 'L': 0.524, 'M': 0.907, 'N': 0.76, 'O': 0.781, 'P': 0.605, 'Q': 0.781,
  'R': 0.622, 'S': 0.549, 'T': 0.556, 'U': 0.731, 'V': 0.6, 'W': 0.93, 'X': 0.586, 'Y': 0.566, 'Z': 0.572, '[': 0.356,
  '\\': 0.429, ']': 0.356, '^': 0.551, '_': 0.411, '`': 0.281, 'a': 0.561, 'b': 0.615, 'c': 0.48, 'd': 0.615, 'e': 0.564,
  'f': 0.344, 'g': 0.615, 'h': 0.618, 'i': 0.258, 'j': 0.258, 'k': 0.534, 'l': 0.258, 'm': 0.935, 'n': 0.618, 'o': 0.605,
  'p': 0.615, 'q': 0.615, 'r': 0.413, 's': 0.479, 't': 0.361, 'u': 0.618, 'v': 0.508, 'w': 0.786, 'x': 0.529, 'y': 0.51,
  'z': 0.47, '{': 0.391, '|': 0.54, '}': 0.376, '~': 0.551, 'ऀ': 0.0, 'ँ': 0.0, 'ं': 0.0, 'ः': 0.202, 'ऄ': 0.764,
  'अ': 0.764, 'आ': 1.022, 'इ': 0.491, 'ई': 0.491, 'उ': 0.548, 'ऊ': 0.785, 'ऋ': 0.853, 'ऌ': 0.699, 'ऍ': 0.553, 'ऎ': 0.553,
  'ए': 0.553, 'ऐ': 0.553, 'ऑ': 1.023, 'ऒ': 1.023, 'ओ': 1.023, 'औ': 1.023, 'क': 0.768, 'ख': 0.818, 'ग': 0.563, 'घ': 0.591,
  'ङ': 0.637, 'च': 0.635, 'छ': 0.701, 'ज': 0.742, 'झ': 0.756, 'ञ': 0.742, 'ट': 0.504, 'ठ': 0.581, 'ड': 0.535, 'ढ': 0.563,
  'ण': 0.723, 'त': 0.57, 'थ': 0.639, 'द': 0.531, 'ध': 0.615, 'न': 0.555, 'ऩ': 0.555, 'प': 0.568, 'फ': 0.771, 'ब': 0.571,
  'भ': 0.703, 'म': 0.598, 'य': 0.58, 'र': 0.409, 'ऱ': 0.409, 'ल': 0.678, 'ळ': 0.76, 'ऴ': 0.76, 'व': 0.556, 'श': 0.68,
  'ष': 0.578, 'स': 0.676, 'ह': 0.531, 'ऺ': 0.0, 'ऻ': 0.259, '़': 0.0, 'ऽ': 0.467, 'ा': 0.259, 'ि': 0.259, 'ी': 0.259,
  'ु': 0.0, 'ू': 0.0, 'ृ': 0.0, 'ॄ': 0.0, 'ॅ': 0.0, 'ॆ': 0.0, 'े': 0.0, 'ै': 0.0, 'ॉ': 0.259, 'ॊ': 0.259,
  'ो': 0.259, 'ौ': 0.259, '्': 0.0, 'ॎ': 0.273, 'ॏ': 0.259, 'ॐ': 0.912, '॑': 0.0, '॒': 0.0, '॓': 0.0, '॔': 0.0,
  'ॕ': 0.0, 'ॖ': 0.0, 'ॗ': 0.0, 'क़': 0.768, 'ख़': 0.818, 'ग़': 0.563, 'ज़': 0.742, 'ड़': 0.535, 'ढ़': 0.563, 'फ़': 0.771,
  'य़': 0.58, 'ॠ': 0.853, 'ॡ': 0.699, 'ॢ': 0.0, 'ॣ': 0.0, '।': 0.379, '॥': 0.599, '०': 0.52, '१': 0.52, '२': 0.52,
  '३': 0.52, '४': 0.52, '५': 0.52, '६': 0.52, '७': 0.52, '८': 0.52, '९': 0.52, '॰': 0.426, 'ॱ': 0.317, 'ॲ': 0.764,
  'ॳ': 0.764, 'ॴ': 1.023, 'ॵ': 1.023, 'ॶ': 0.764, 'ॷ': 0.764, 'ॸ': 0.555, 'ॹ': 0.743, 'ॺ': 0.581, 'ॻ': 0.564, 'ॼ': 0.743,
  'ॽ': 0.469, 'ॾ': 0.541, 'ॿ': 0.571,
};
// libass sizes a font by usWinAscent+usWinDescent → rendered em = Fontsize × upm/(winAsc+winDesc).
const NOTO_DEVA_RENDER_RATIO = 0.5247; // 1000 / (1348 + 558), same for all four faces
// LIBASS_FONT_COMPENSATION (1.55) was calibrated on Montserrat (render ratio 0.6402). Noto
// Devanagari's much taller win metrics would burn Hindi ~18% smaller than the preview at the
// same Fontsize, so Devanagari captions get this extra multiplier (0.6402 / 0.5247).
const NOTO_DEVA_SIZE_COMPENSATION = 1.22;
// Heavier faces are wider than the Regular table above (measured average vs Regular advances).
const notoDevaWidthFactor = (weight) => (weight >= 900 ? 1.13 : weight >= 800 ? 1.1 : weight >= 700 ? 1.08 : 1);

// ── Baloo 2 (Devanagari display face) ──
// The comic templates (Tabahi, Mr Beast) use Bangers, which has zero Devanagari glyphs; routing
// their Hindi captions to plain Noto killed the comic identity. Baloo 2 is a rounded display
// face covering Devanagari AND basic Latin, so mixed Hinglish stays in one family. Metrics read
// from the bundled statics: upm/(usWinAscent+usWinDescent) = 1000/1602.
const BALOO_RENDER_RATIO = 0.6242;
// Same reasoning as NOTO_DEVA_SIZE_COMPENSATION: 0.6402 (Montserrat anchor) / 0.6242.
const BALOO_SIZE_COMPENSATION = 1.03;

// ── Devanagari vertical metrics for boxes drawn around single words ──
// Where a face's line box is centred, in em above the baseline. libass centres a drawn rect on
// the line box, whose baseline sits at the OS/2 win ascent; CSS centres an inline box on the typo
// (hhea) metrics. Both faces set USE_TYPO_METRICS, so every browser and platform agrees.
// Noto Sans Devanagari: win 1348/558, typo 896/-408. Baloo 2: win and typo both 1078/524.
const NOTO_DEVA_WIN_CENTRE_EM = 0.395;
const NOTO_DEVA_TYPO_CENTRE_EM = 0.244;
const BALOO_WIN_CENTRE_EM = 0.277;
const BALOO_TYPO_CENTRE_EM = 0.277;
// Ink extents of a Devanagari word in em (measured on Noto Sans Devanagari Black in Chrome; the
// Regular face runs ~0.04 em lower on top). A plain consonant run stops at the shirorekha (0.67),
// vowel signs, anusvara and the reph rise to ~0.97, candrabindu to 1.07; u/uu/vocalic-r matras
// hang to -0.31, virama stacks and conjuncts to about -0.26, nuktas to -0.2 and the tail of ह to
// -0.11 (the vowel letters इ ई ए ऐ dip about as far). Used to size the active-word pill so marks
// above and below are covered.
const DEVA_INK = { plainTop: 0.70, markTop: 0.97, candraTop: 1.07, plainBottom: 0, hBottom: -0.12, nuktaBottom: -0.21, stackBottom: -0.17, viramaBottom: -0.26, matraBottom: -0.31 };
// Signs and letters whose ink rises above the shirorekha: candrabindu, anusvara, the i/ii/e/ai/o/au
// and candra signs, the reph, ॐ, and the vowel letters ई ऐ ओ औ ऑ ऍ ॲ. अ आ इ उ ऊ ऋ ए stop at the
// headline (measured), so a word like आप keeps the plain top.
const DEVA_ABOVE_MARKS = /[\u0901\u0902\u093F\u0940\u0945-\u094C\u0908\u090D\u0910-\u0914\u0950\u0972]|\u0930\u094D/;
/**
 * Ink top/bottom of `word` in em above the baseline for the Devanagari faces, or null when the
 * word has no Devanagari (a Latin word in a Hindi caption keeps the face's default glyph box).
 */
const devanagariInkExtents = (word) => {
  const t = String(word || '');
  if (!/[\u0900-\u097F]/.test(t)) return null;
  const top = /\u0901/.test(t) ? DEVA_INK.candraTop : DEVA_ABOVE_MARKS.test(t) ? DEVA_INK.markTop : DEVA_INK.plainTop;
  // Virama: a word-final halant or a stack under ट ठ ड ढ छ ङ hangs deepest (ट्ट, ड्ड, छ्व, ङ्क);
  // द and ह stack a little (द्ध, ह्म); a half form before र or य (क्र, क्या, त्र) stays on the
  // baseline; other half-form conjuncts (क्ष, ज्ञ, ब्द) dip slightly, like ह's own tail.
  const bottom = /[\u0941-\u0944\u0962\u0963]/.test(t) ? DEVA_INK.matraBottom
    : /\u094D$|[\u091F-\u0922\u091B\u0919]\u094D/.test(t) ? DEVA_INK.viramaBottom
      : /[\u0926\u0939]\u094D(?!\u092F)/.test(t) ? DEVA_INK.stackBottom
        : /[\u093C\u0929\u0931\u0934\u0958-\u095F]/.test(t) ? DEVA_INK.nuktaBottom
          : /(?<!\u0930)\u094D(?![\u0930\u092F])|[\u0939\u0907\u0908\u090D\u090F\u0910]/.test(t) ? DEVA_INK.hBottom : DEVA_INK.plainBottom;
  return { top, bottom };
};
// Baloo's Devanagari advances measure 0.96–0.98× Noto's per char, so the Noto width tables are
// reused for its Devanagari runs (≤4% lean-wide — the safe direction for a drawn box).

// ── Bengali caption metrics ──
// The Bengali twin of the Devanagari block above. None of the Latin caption fonts carry Bengali
// glyphs, so Bengali captions are routed to the bundled Noto Sans Bengali faces (Baloo Da 2 for
// the comic templates). Real advances (advance / unitsPerEm) read from NotoSansBengali-Regular.ttf
// (v3.011) with opentype.js; the non-spacing marks (ু ূ ৃ ্ ঁ …) are true zero-advance
// combining marks, the pre-base and spacing vowel signs (া ি ী ে ৈ ো ৌ) keep their real
// advance. Latin entries cover mixed Banglish text. The precomposed two-part signs ো (U+09CB)
// and ৌ (U+09CC) are overridden below: the cmap glyph's advance (1.137) is never what renders,
// since shaping decomposes them into ে + া / ে + ৗ (0.361 + 0.266).
const NOTO_BENG_CHAR_WIDTHS = {
  '0': 0.572, '1': 0.572, '2': 0.572, '3': 0.572, '4': 0.572, '5': 0.572, '6': 0.572, '7': 0.572, '8': 0.572, '9': 0.572,
  ' ': 0.26, '!': 0.289, '"': 0.408, '#': 0.646, '$': 0.572, '%': 0.831, '&': 0.732, "'": 0.225, '(': 0.3, ')': 0.3,
  '*': 0.551, '+': 0.572, ',': 0.268, '-': 0.322, '.': 0.268, '/': 0.372, ':': 0.268, ';': 0.268, '<': 0.572, '=': 0.572,
  '>': 0.572, '?': 0.504, '@': 0.899, 'A': 0.639, 'B': 0.65, 'C': 0.632, 'D': 0.73, 'E': 0.556, 'F': 0.519, 'G': 0.728,
  'H': 0.741, 'I': 0.339, 'J': 0.273, 'K': 0.619, 'L': 0.524, 'M': 0.907, 'N': 0.76, 'O': 0.781, 'P': 0.605, 'Q': 0.781,
  'R': 0.622, 'S': 0.549, 'T': 0.556, 'U': 0.731, 'V': 0.6, 'W': 0.93, 'X': 0.586, 'Y': 0.566, 'Z': 0.572, '[': 0.359,
  '\\': 0.372, ']': 0.359, '^': 0.572, '_': 0.444, '`': 0.281, 'a': 0.561, 'b': 0.615, 'c': 0.48, 'd': 0.615, 'e': 0.564,
  'f': 0.344, 'g': 0.615, 'h': 0.618, 'i': 0.258, 'j': 0.258, 'k': 0.534, 'l': 0.258, 'm': 0.935, 'n': 0.618, 'o': 0.605,
  'p': 0.615, 'q': 0.615, 'r': 0.413, 's': 0.479, 't': 0.361, 'u': 0.618, 'v': 0.508, 'w': 0.786, 'x': 0.529, 'y': 0.51,
  'z': 0.47, '{': 0.41, '|': 0.551, '}': 0.41, '~': 0.572, 'ঀ': 0.54, 'ঁ': 0, 'ং': 0.438, 'ঃ': 0.438, 'অ': 0.893,
  'আ': 1.159, 'ই': 0.534, 'ঈ': 0.689, 'উ': 0.712, 'ঊ': 0.8, 'ঋ': 0.846, 'ঌ': 0.609, 'এ': 0.731, 'ঐ': 0.853, 'ও': 0.738,
  'ঔ': 0.874, 'ক': 0.807, 'খ': 0.661, 'গ': 0.646, 'ঘ': 0.621, 'ঙ': 0.715, 'চ': 0.538, 'ছ': 0.609, 'জ': 0.862, 'ঝ': 0.797,
  'ঞ': 0.993, 'ট': 0.547, 'ঠ': 0.541, 'ড': 0.712, 'ঢ': 0.547, 'ণ': 0.62, 'ত': 0.707, 'থ': 0.645, 'দ': 0.553, 'ধ': 0.596,
  'ন': 0.602, 'প': 0.716, 'ফ': 0.833, 'ব': 0.596, 'ভ': 0.701, 'ম': 0.612, 'য': 0.626, 'র': 0.596, 'ল': 0.731, 'শ': 0.681,
  'ষ': 0.626, 'স': 0.623, 'হ': 0.534, '়': 0, 'ঽ': 0.53, 'া': 0.266, 'ি': 0.266, 'ী': 0.266, 'ু': 0, 'ূ': 0,
  'ৃ': 0, 'ৄ': 0, 'ে': 0.361, 'ৈ': 0.361, 'ো': 1.137, 'ৌ': 1.137, '্': 0, 'ৎ': 0.542, 'ৗ': 0.266, 'ড়': 0.712,
  'ঢ়': 0.547, 'য়': 0.626, 'ৠ': 0.846, 'ৡ': 0.692, 'ৢ': 0, 'ৣ': 0, '০': 0.592, '১': 0.592, '২': 0.592, '৩': 0.592,
  '৪': 0.592, '৫': 0.592, '৬': 0.592, '৭': 0.592, '৮': 0.592, '৯': 0.592, 'ৰ': 0.596, 'ৱ': 0.596, '৲': 0.589, '৳': 0.592,
  '৴': 0.589, '৵': 0.602, '৶': 0.679, '৷': 0.267, '৸': 0.602, '৹': 0.483, '৺': 0.676, '৻': 0.311, 'ৼ': 0.537, '৽': 0.426,
  '৾': 0,
};
// Noto Sans Bengali win metrics are 995/408: upm/(winAsc+winDesc) = 1000/1403. Shorter than
// Montserrat's box (0.6402), so at the same Fontsize libass would burn Bengali ~11% LARGER than
// the preview; the compensation is therefore below 1 (0.6402 / 0.7128).
NOTO_BENG_CHAR_WIDTHS['\u09CB'] = NOTO_BENG_CHAR_WIDTHS['\u09C7'] + NOTO_BENG_CHAR_WIDTHS['\u09BE'];
NOTO_BENG_CHAR_WIDTHS['\u09CC'] = NOTO_BENG_CHAR_WIDTHS['\u09C7'] + NOTO_BENG_CHAR_WIDTHS['\u09D7'];
const NOTO_BENG_RENDER_RATIO = 0.7128;
const NOTO_BENG_SIZE_COMPENSATION = 0.898;
// Heavier faces are wider than the Regular table (measured average over the Bengali letters and
// vowel signs plus Latin lowercase vs Regular advances).
const notoBengWidthFactor = (weight) => (weight >= 900 ? 1.07 : weight >= 800 ? 1.06 : weight >= 700 ? 1.05 : 1);
// Baloo Da 2 (Bengali display face for the comic templates): win and typo both 1095/589,
// upm/(winAsc+winDesc) = 1000/1684; compensation 0.6402 / 0.5938. Its advances measure 0.94–1.01×
// Noto Sans Bengali's, so the Noto table is reused for its Bengali runs.
const BALOO_DA_RENDER_RATIO = 0.5938;
const BALOO_DA_SIZE_COMPENSATION = 1.078;
// Line-box centres in em above the baseline (see the Devanagari note). Noto Sans Bengali:
// win 995/408, typo 917/-408. Baloo Da 2: win and typo both 1095/589. Both set USE_TYPO_METRICS.
const NOTO_BENG_WIN_CENTRE_EM = 0.293;
const NOTO_BENG_TYPO_CENTRE_EM = 0.255;
const BALOO_DA_WIN_CENTRE_EM = 0.253;
const BALOO_DA_TYPO_CENTRE_EM = 0.253;
// Ink extents of a Bengali word in em (glyph bounding boxes of Noto Sans Bengali Black, read with
// opentype.js). A plain consonant run stops at the matra (headline) at 0.635; the i/ii/ai/au
// signs, the reph, candrabindu and the tall letters ট ঠ ই ঈ উ ঊ ঐ ঔ rise to ~0.92. The u/uu/
// vocalic-r signs hang to -0.29, stacked conjuncts to about -0.12 on average (-0.28 at the 10th
// percentile), and the tails of র হ ছ ই ং to about -0.1. Used to size the active-word pill.
const BENG_INK = { plainTop: 0.66, markTop: 0.93, plainBottom: 0, tailBottom: -0.11, stackBottom: -0.2, matraBottom: -0.29 };
// Signs and letters whose ink rises above the headline: candrabindu, the i/ii/ai/au signs (the
// two-part ো ৌ arrive decomposed as ে + া / ে + ৗ, so ৗ counts), the reph (র + virama before a
// consonant) and the tall vowel letters. The decomposed pair for ো has nothing above the line.
const BENG_ABOVE_MARKS = /[\u0981\u09BF\u09C0\u09C8\u09CC\u09D7\u099F\u09A0\u0987\u0988\u0989\u098A\u0990\u0994]|\u09B0\u09CD(?=[\u0995-\u09B9])/;
/**
 * Ink top/bottom of `word` in em above the baseline for the Bengali faces, or null when the word
 * has no Bengali (a Latin word in a Bengali caption keeps the face's default glyph box).
 */
const bengaliInkExtents = (word) => {
  const t = String(word || '');
  if (!/[\u0980-\u09FF]/.test(t)) return null;
  const top = BENG_ABOVE_MARKS.test(t) ? BENG_INK.markTop : BENG_INK.plainTop;
  // u/uu/vocalic-r signs hang deepest; a virama that is not a reph or a ya/ra-phala stacks the
  // next consonant below; হ ছ ই জ, the anusvara and a plain র (not a reph above, not a ra-phala
  // attached to the base) have short tails.
  const bottom = /[\u09C1-\u09C4\u09E2\u09E3]/.test(t) ? BENG_INK.matraBottom
    : /(?<!\u09B0)\u09CD(?![\u09B0\u09AF])/.test(t) ? BENG_INK.stackBottom
      : /(?<!\u09CD)\u09B0(?!\u09CD)|[\u09B9\u099B\u0987\u099C\u0982]/.test(t) ? BENG_INK.tailBottom : BENG_INK.plainBottom;
  return { top, bottom };
};

// ── Telugu caption metrics ──
// The third Indic script, on the Bengali scheme: Telugu captions route to the bundled Noto Sans
// Telugu faces (Baloo Tammudu 2 for the comic templates). Real advances (advance / unitsPerEm)
// read from NotoSansTelugu-Regular.ttf (v2.005) with opentype.js. The above-base vowel signs
// (ి ీ ె ే ై ొ ో ౌ), the virama/pollu and the length marks are true zero-advance combining marks;
// ా ు ూ ృ ౄ are spacing signs with their real advance. Latin entries cover mixed Tenglish text.
const NOTO_TEL_CHAR_WIDTHS = {
  '0': 0.572, '1': 0.572, '2': 0.572, '3': 0.572, '4': 0.572, '5': 0.572, '6': 0.572, '7': 0.572, '8': 0.572,
  '9': 0.572, ' ': 0.283, '!': 0.298, '"': 0.456, '#': 0.646, '$': 0.572, '%': 0.831, '&': 0.732, "'": 0.28,
  '(': 0.362, ')': 0.362, '*': 0.551, '+': 0.572, ',': 0.268, '-': 0.322, '.': 0.268, '/': 0.436, ':': 0.268,
  ';': 0.268, '<': 0.572, '=': 0.572, '>': 0.572, '?': 0.471, '@': 0.899, 'A': 0.639, 'B': 0.65, 'C': 0.632,
  'D': 0.73, 'E': 0.556, 'F': 0.519, 'G': 0.728, 'H': 0.741, 'I': 0.339, 'J': 0.273, 'K': 0.619, 'L': 0.524,
  'M': 0.907, 'N': 0.76, 'O': 0.781, 'P': 0.605, 'Q': 0.781, 'R': 0.622, 'S': 0.549, 'T': 0.556, 'U': 0.731,
  'V': 0.6, 'W': 0.93, 'X': 0.586, 'Y': 0.566, 'Z': 0.572, '[': 0.344, '\\': 0.436, ']': 0.344, '^': 0.572,
  '_': 0.444, '`': 0.281, 'a': 0.561, 'b': 0.615, 'c': 0.48, 'd': 0.615, 'e': 0.564, 'f': 0.344, 'g': 0.615,
  'h': 0.618, 'i': 0.258, 'j': 0.258, 'k': 0.534, 'l': 0.258, 'm': 0.935, 'n': 0.618, 'o': 0.605, 'p': 0.615,
  'q': 0.615, 'r': 0.413, 's': 0.479, 't': 0.361, 'u': 0.618, 'v': 0.508, 'w': 0.786, 'x': 0.529, 'y': 0.51,
  'z': 0.47, '{': 0.388, '|': 0.551, '}': 0.388, '~': 0.572, 'ఀ': 0, 'ఁ': 0.298, 'ం': 0.507, 'ః': 0.296, 'ఄ': 0,
  'అ': 0.8, 'ఆ': 0.798, 'ఇ': 0.689, 'ఈ': 0.994, 'ఉ': 0.798, 'ఊ': 1.095, 'ఋ': 1.442, 'ఌ': 0.882, 'ఎ': 0.711,
  'ఏ': 0.711, 'ఐ': 0.727, 'ఒ': 0.731, 'ఓ': 0.731, 'ఔ': 0.731, 'క': 0.522, 'ఖ': 0.755, 'గ': 0.583, 'ఘ': 1.058,
  'ఙ': 0.731, 'చ': 0.76, 'ఛ': 0.76, 'జ': 0.731, 'ఝ': 1.286, 'ఞ': 0.979, 'ట': 0.772, 'ఠ': 0.593, 'డ': 0.74, 'ఢ': 0.74,
  'ణ': 0.826, 'త': 0.778, 'థ': 0.706, 'ద': 0.706, 'ధ': 0.706, 'న': 0.702, 'ప': 0.711, 'ఫ': 0.711, 'బ': 0.75,
  'భ': 0.75, 'మ': 1.058, 'య': 1.286, 'ర': 0.593, 'ఱ': 0.822, 'ల': 0.709, 'ళ': 0.645, 'ఴ': 0.812, 'వ': 0.711,
  'శ': 0.55, 'ష': 0.713, 'స': 0.702, 'హ': 1.033, '఼': 0, 'ఽ': 0.59, 'ా': 0.26, 'ి': 0, 'ీ': 0, 'ు': 0.346,
  'ూ': 0.668, 'ృ': 0.4, 'ౄ': 0.656, 'ె': 0, 'ే': 0, 'ై': 0, 'ొ': 0, 'ో': 0, 'ౌ': 0, '్': 0, 'ౕ': 0, 'ౖ': 0,
  'ౘ': 0.76, 'ౙ': 0.731, 'ౚ': 0.716, 'ౝ': 0.611, 'ౠ': 1.764, 'ౡ': 1.227, 'ౢ': 0, 'ౣ': 0.324, '౦': 0.675, '౧': 0.665,
  '౨': 0.773, '౩': 0.546, '౪': 0.686, '౫': 0.711, '౬': 0.698, '౭': 0.573, '౮': 0.803, '౯': 0.698, '౷': 1.044,
  '౸': 0.622, '౹': 0.273, '౺': 0.489, '౻': 0.693, '౼': 0.61, '౽': 0.598, '౾': 0.598, '౿': 0.742,
};
// Noto Sans Telugu win metrics are 995/483: upm/(winAsc+winDesc) = 1000/1478. Shorter than
// Montserrat's box (0.6402), so the compensation is below 1 (0.6402 / 0.6766), like Bengali.
const NOTO_TEL_RENDER_RATIO = 0.6766;
const NOTO_TEL_SIZE_COMPENSATION = 0.946;
// Heavier faces are wider than the Regular table (measured average over the Telugu letters and
// vowel signs plus Latin lowercase vs Regular advances: Bold 1.086, ExtraBold 1.116, Black 1.151).
const notoTelWidthFactor = (weight) => (weight >= 900 ? 1.15 : weight >= 800 ? 1.12 : weight >= 700 ? 1.09 : 1);
// Baloo Tammudu 2 (Telugu display face for the comic templates): win and typo both 1177/1119, a
// very tall box (upm/(winAsc+winDesc) = 1000/2296), so libass draws it small and the compensation
// is 0.6402 / 0.4355. Its glyph advances measure 1.05× (Regular), 1.11× (Bold) and 1.14×
// (ExtraBold) Noto Sans Telugu's Regular table, but shaped words in Chrome come out a further
// 7% wider (wider Latin, spacing vowel signs), so the Noto table is reused with these factors
// (median estimate/real 1.00 on 26 test words at each weight).
const BALOO_TAMMUDU_RENDER_RATIO = 0.4355;
const BALOO_TAMMUDU_SIZE_COMPENSATION = 1.47;
const balooTammuduWidthFactor = (weight) => (weight >= 800 ? 1.22 : weight >= 700 ? 1.19 : 1.13);
// Line-box centres in em above the baseline (see the Devanagari note). Noto Sans Telugu:
// win 995/483, typo 869/-483. Baloo Tammudu 2: win and typo both 1177/1119. Both set USE_TYPO_METRICS.
const NOTO_TEL_WIN_CENTRE_EM = 0.256;
const NOTO_TEL_TYPO_CENTRE_EM = 0.193;
const BALOO_TAMMUDU_WIN_CENTRE_EM = 0.029;
const BALOO_TAMMUDU_TYPO_CENTRE_EM = 0.029;
// Ink extents of a Telugu word in em (glyph bounding boxes of Noto Sans Telugu Black, read with
// opentype.js). A consonant with its talakattu (the check mark on top) reaches 0.734; the
// ి ె ై ొ ౌ signs and ఎ reach ~0.72-0.80, the ీ ే ో signs, the pollu/virama, ఏ and the
// candrabindu ~0.87. Below the baseline: ృ ౄ and the ai length mark ౖ hang to -0.37/-0.41, the
// subscript consonants (vottulu, virama + consonant) to -0.41 on average (-0.47 at the 10th
// percentile), and the tails of ఖ ఘ ఛ ఝ ఞ ఢ థ ధ ఫ భ ష ఇ to about -0.14. ు ూ attach at the side
// and add nothing below. Used to size the active-word pill.
const TEL_INK = { plainTop: 0.74, midTop: 0.80, markTop: 0.87, plainBottom: -0.01, tailBottom: -0.14, matraBottom: -0.40, subBottom: -0.42 };
// Baloo Tammudu 2 draws the same shapes ~1.2× larger on its em (క 0.869 vs 0.734, ృ -0.487 vs -0.411).
const BALOO_TAMMUDU_INK_SCALE = 1.2;
// The virama counts only as a pollu (word-final or before ZWNJ): inside a conjunct it is not drawn.
const TEL_HIGH_MARKS = /[\u0C40\u0C47\u0C4A\u0C00\u0C55\u0C0F]|\u0C4D(?![\u0C15-\u0C39])/;
const TEL_MID_MARKS = /[\u0C3F\u0C46\u0C48\u0C4B\u0C4C\u0C0E]/;
/**
 * Ink top/bottom of `word` in em above the baseline for the Telugu faces, or null when the word
 * has no Telugu (a Latin word in a Telugu caption keeps the face's default glyph box). `display`
 * scales for Baloo Tammudu 2.
 */
const teluguInkExtents = (word, display = false) => {
  const t = String(word || '');
  if (!/[\u0C00-\u0C7F]/.test(t)) return null;
  const top = TEL_HIGH_MARKS.test(t) ? TEL_INK.markTop : TEL_MID_MARKS.test(t) ? TEL_INK.midTop : TEL_INK.plainTop;
  // ృ ౄ and ై (decomposed to ె + ౖ under NFD, so ౖ counts) hang deepest; a virama directly
  // before a consonant puts that consonant below as a vottu (virama + ZWNJ is a pollu, nothing
  // below); the tailed letters sit between.
  const bottom = /[\u0C43\u0C44\u0C48\u0C56\u0C62\u0C63]/.test(t) ? TEL_INK.matraBottom
    : /\u0C4D[\u0C15-\u0C39]/.test(t) ? TEL_INK.subBottom
      : /[\u0C16\u0C18\u0C1B\u0C1D\u0C1E\u0C22\u0C25\u0C27\u0C2B\u0C2D\u0C37\u0C07]/.test(t) ? TEL_INK.tailBottom : TEL_INK.plainBottom;
  const k = display ? BALOO_TAMMUDU_INK_SCALE : 1;
  return { top: top * k, bottom: bottom * k };
};

// ── Tamil caption metrics ──
// The fourth Indic script. Tamil captions route to the bundled Noto Sans Tamil faces (Baloo
// Thambi 2 for the comic templates). Real advances (advance / unitsPerEm) read from
// NotoSansTamil-Regular.ttf (v2.004) with opentype.js. Tamil has no conjunct stacking: clusters
// are written with the visible pulli, so every consonant keeps its full advance. The pre-base
// signs ெ ே ை are spacing glyphs drawn before the consonant; ீ and the pulli are marks.
const NOTO_TAM_CHAR_WIDTHS = {
  '0': 0.572, '1': 0.572, '2': 0.572, '3': 0.572, '4': 0.572, '5': 0.572, '6': 0.572, '7': 0.572, '8': 0.572,
  '9': 0.572, ' ': 0.26, '!': 0.269, '"': 0.426, '#': 0.646, '$': 0.572, '%': 0.831, '&': 0.732, "'": 0.25,
  '(': 0.321, ')': 0.321, '*': 0.551, '+': 0.572, ',': 0.25, '-': 0.322, '.': 0.268, '/': 0.393, ':': 0.268,
  ';': 0.268, '<': 0.572, '=': 0.572, '>': 0.572, '?': 0.445, '@': 0.899, 'A': 0.639, 'B': 0.65, 'C': 0.632,
  'D': 0.73, 'E': 0.556, 'F': 0.519, 'G': 0.728, 'H': 0.741, 'I': 0.339, 'J': 0.273, 'K': 0.619, 'L': 0.524,
  'M': 0.907, 'N': 0.76, 'O': 0.781, 'P': 0.605, 'Q': 0.781, 'R': 0.622, 'S': 0.549, 'T': 0.556, 'U': 0.731,
  'V': 0.6, 'W': 0.93, 'X': 0.586, 'Y': 0.566, 'Z': 0.572, '[': 0.318, '\\': 0.393, ']': 0.323, '^': 0.572,
  '_': 0.444, '`': 0.281, 'a': 0.561, 'b': 0.615, 'c': 0.48, 'd': 0.615, 'e': 0.564, 'f': 0.344, 'g': 0.615,
  'h': 0.618, 'i': 0.258, 'j': 0.258, 'k': 0.534, 'l': 0.258, 'm': 0.935, 'n': 0.618, 'o': 0.605, 'p': 0.615,
  'q': 0.615, 'r': 0.413, 's': 0.479, 't': 0.361, 'u': 0.618, 'v': 0.508, 'w': 0.786, 'x': 0.529, 'y': 0.51,
  'z': 0.47, '{': 0.369, '|': 0.551, '}': 0.369, '~': 0.572, 'ஂ': 0.352, 'ஃ': 0.896, 'அ': 1.121, 'ஆ': 1.313,
  'இ': 1.126, 'ஈ': 0.707, 'உ': 1.067, 'ஊ': 1.54, 'எ': 0.801, 'ஏ': 0.801, 'ஐ': 1.022, 'ஒ': 0.985, 'ஓ': 0.985,
  'ஔ': 2.051, 'க': 0.825, 'ங': 1.044, 'ச': 0.723, 'ஜ': 1.022, 'ஞ': 1.21, 'ட': 0.693, 'ண': 1.648, 'த': 0.825,
  'ந': 0.789, 'ன': 1.234, 'ப': 0.778, 'ம': 0.858, 'ய': 0.963, 'ர': 0.64, 'ற': 0.869, 'ல': 1.013, 'ள': 1.07,
  'ழ': 0.858, 'வ': 1.044, 'ஶ': 1.172, 'ஷ': 1.345, 'ஸ': 1.285, 'ஹ': 1.619, 'ா': 0.64, 'ி': 0.262, 'ீ': 0, 'ு': 0.502,
  'ூ': 0.844, 'ெ': 0.901, 'ே': 0.72, 'ை': 1.16, 'ொ': 1.707, 'ோ': 1.51, 'ௌ': 2.15, '்': 0, 'ௐ': 1.141, 'ௗ': 1.07,
  '௦': 0.65, '௧': 0.825, '௨': 1.067, '௩': 0.985, '௪': 0.941, '௫': 1.051, '௬': 1.006, '௭': 0.801, '௮': 1.121,
  '௯': 1.155, '௰': 0.875, '௱': 0.887, '௲': 0.965, '௳': 0.985, '௴': 1.268, '௵': 2.223, '௶': 1.027, '௷': 1.328,
  '௸': 1.965, '௹': 1.05, '௺': 1.214,
};
// The two-part signs ொ ோ ௌ shape as ெ/ே + ா or ெ + ௗ, which is narrower than their precomposed
// cmap glyphs (1.541 vs 1.707 for ொ, measured in Chrome); and the u/uu signs fuse with the
// consonant into a ligature whose width is nothing like consonant + sign (கு 1.062 vs
// 0.825 + 0.502), so those take a per-consonant table measured from shaped text.
NOTO_TAM_CHAR_WIDTHS['\u0BCA'] = NOTO_TAM_CHAR_WIDTHS['\u0BC6'] + NOTO_TAM_CHAR_WIDTHS['\u0BBE'];
NOTO_TAM_CHAR_WIDTHS['\u0BCB'] = NOTO_TAM_CHAR_WIDTHS['\u0BC7'] + NOTO_TAM_CHAR_WIDTHS['\u0BBE'];
NOTO_TAM_CHAR_WIDTHS['\u0BCC'] = NOTO_TAM_CHAR_WIDTHS['\u0BC6'] + NOTO_TAM_CHAR_WIDTHS['\u0BD7'];
const TAM_U_LIGATURE_WIDTHS = { 'க': 1.062, 'ங': 1.044, 'ச': 0.867, 'ஞ': 1.226, 'ட': 1.015, 'ண': 1.841, 'த': 1.091, 'ந': 1.058, 'ப': 0.778, 'ம': 1.132, 'ய': 0.963, 'ர': 1.051, 'ல': 1.26, 'வ': 1.044, 'ழ': 1.132, 'ள': 1.483, 'ற': 1.135, 'ன': 1.426, 'ஜ': 1.524, 'ஷ': 1.833, 'ஸ': 1.787, 'ஹ': 2.121, 'ஶ': 1.674 };
const TAM_UU_LIGATURE_WIDTHS = { 'க': 1.332, 'ங': 1.214, 'ச': 1.195, 'ஞ': 1.589, 'ட': 0.902, 'ண': 2.205, 'த': 1.455, 'ந': 1.423, 'ப': 0.947, 'ம': 1.232, 'ய': 1.132, 'ர': 1.152, 'ல': 1.624, 'வ': 1.214, 'ழ': 1.232, 'ள': 1.602, 'ற': 1.499, 'ன': 1.79, 'ஜ': 1.866, 'ஷ': 2.175, 'ஸ': 2.129, 'ஹ': 2.463, 'ஶ': 2.016 };
// Noto Sans Tamil win metrics are 870/370: upm/(winAsc+winDesc) = 1000/1240, a short box, so
// libass draws it large and the compensation is well below 1 (0.6402 / 0.8065).
const NOTO_TAM_RENDER_RATIO = 0.8065;
const NOTO_TAM_SIZE_COMPENSATION = 0.794;
// Heavier faces vs the Regular table (shaped words in Chrome: Bold 1.10, ExtraBold 1.12, Black 1.15).
const notoTamWidthFactor = (weight) => (weight >= 900 ? 1.15 : weight >= 800 ? 1.12 : weight >= 700 ? 1.10 : 1);
// Baloo Thambi 2 (Tamil display face for the comic templates): win and typo both 1078/486,
// upm/(winAsc+winDesc) = 1000/1564, compensation 0.6402 / 0.6394. Shaped words measure 0.945×
// (Regular), 0.99× (Bold) and 1.01× (ExtraBold) the Noto table, so it is reused with those.
const BALOO_THAMBI_RENDER_RATIO = 0.6394;
const BALOO_THAMBI_SIZE_COMPENSATION = 1.001;
const balooThambiWidthFactor = (weight) => (weight >= 800 ? 1.01 : weight >= 700 ? 0.99 : 0.95);
// Line-box centres in em above the baseline. Both faces have win == typo metrics, so the pill
// needs no lift on either: Noto Sans Tamil 870/-370, Baloo Thambi 2 1078/-486.
const NOTO_TAM_WIN_CENTRE_EM = 0.25;
const NOTO_TAM_TYPO_CENTRE_EM = 0.25;
const BALOO_THAMBI_WIN_CENTRE_EM = 0.296;
const BALOO_THAMBI_TYPO_CENTRE_EM = 0.296;
// Ink extents of a Tamil word in em (glyph bounding boxes of Noto Sans Tamil Black, read with
// opentype.js). Tamil letters are flat-topped at 0.58; the pulli, the i/ii/e/ee/o/oo/au signs,
// இ and ஃ rise to 0.85. Below the baseline: ஆ இ ஐ ஒ ஓ ஔ ஜ ஞ த ந ற ழ ஹ ஷ hang to about -0.32,
// ஏ ர to -0.21, அ to -0.15; the rest sit on the line. Baloo Thambi 2 draws the same shapes
// within a few percent, so one rule serves both.
const TAM_INK = { plainTop: 0.58, markTop: 0.85, plainBottom: 0, shortBottom: -0.15, midBottom: -0.21, deepBottom: -0.32 };
const TAM_ABOVE_MARKS = /[\u0BCD\u0BBF\u0BC0\u0BC6\u0BC7\u0BCA\u0BCB\u0BCC\u0B87\u0B83]/;
const TAM_DEEP = /[\u0B86\u0B87\u0B90\u0B92\u0B93\u0B94\u0B9C\u0B9E\u0BA4\u0BA8\u0BB1\u0BB4\u0BB9\u0BB7\u0BD0]/;
const TAM_MID = /[\u0B8F\u0BB0]/;
const TAM_SHORT = /\u0B85/;
/**
 * Ink top/bottom of `word` in em above the baseline for the Tamil faces, or null when the word
 * has no Tamil (a Latin word in a Tamil caption keeps the face's default glyph box).
 */
const tamilInkExtents = (word) => {
  const t = String(word || '');
  if (!/[\u0B80-\u0BFF]/.test(t)) return null;
  const top = TAM_ABOVE_MARKS.test(t) ? TAM_INK.markTop : TAM_INK.plainTop;
  const bottom = TAM_DEEP.test(t) ? TAM_INK.deepBottom : TAM_MID.test(t) ? TAM_INK.midBottom : TAM_SHORT.test(t) ? TAM_INK.shortBottom : TAM_INK.plainBottom;
  return { top, bottom };
};
const TAM_U_SIGN = 0x0bc1;
const TAM_UU_SIGN = 0x0bc2;
const isTamConsonant = (cp) => cp >= 0x0b95 && cp <= 0x0bb9;

// ── Gujarati caption metrics ──
// The fifth Indic script. Gujarati is Devanagari without the headline: every code point sits
// 0x180 below its Devanagari twin and conjuncts shape the same way (half forms, a zero-width reph
// for a leading ર્, a rakar for a trailing ્ર). Captions route to the bundled Noto Sans Gujarati
// faces (Baloo Bhai 2 for the comic templates). Real advances (advance / unitsPerEm) read from
// NotoSansGujarati-Regular.ttf (v2.106) with opentype.js; the non-spacing signs (ુ ૂ ૃ ે ૈ ્ ં ઁ) are
// zero-advance marks, the spacing matras (ા િ ી ો ૌ) keep their 0.265 advance.
const NOTO_GUJ_CHAR_WIDTHS = {
  '0': 0.572, '1': 0.572, '2': 0.572, '3': 0.572, '4': 0.572, '5': 0.572, '6': 0.572, '7': 0.572, '8': 0.572,
  '9': 0.572, ' ': 0.26, '!': 0.269, '"': 0.408, '#': 0.646, '$': 0.572, '%': 0.831, '&': 0.732, "'": 0.225,
  '(': 0.3, ')': 0.3, '*': 0.551, '+': 0.572, ',': 0.268, '-': 0.322, '.': 0.268, '/': 0.372, ':': 0.268, ';': 0.268,
  '<': 0.572, '=': 0.572, '>': 0.572, '?': 0.434, '@': 0.899, 'A': 0.639, 'B': 0.65, 'C': 0.632, 'D': 0.73,
  'E': 0.556, 'F': 0.519, 'G': 0.728, 'H': 0.741, 'I': 0.339, 'J': 0.273, 'K': 0.619, 'L': 0.524, 'M': 0.907,
  'N': 0.76, 'O': 0.781, 'P': 0.605, 'Q': 0.781, 'R': 0.622, 'S': 0.549, 'T': 0.556, 'U': 0.731, 'V': 0.6, 'W': 0.93,
  'X': 0.586, 'Y': 0.566, 'Z': 0.572, '[': 0.329, '\\': 0.372, ']': 0.329, '^': 0.572, '_': 0.444, '`': 0.281,
  'a': 0.561, 'b': 0.615, 'c': 0.48, 'd': 0.615, 'e': 0.564, 'f': 0.344, 'g': 0.615, 'h': 0.618, 'i': 0.258,
  'j': 0.258, 'k': 0.534, 'l': 0.258, 'm': 0.935, 'n': 0.618, 'o': 0.605, 'p': 0.615, 'q': 0.615, 'r': 0.413,
  's': 0.479, 't': 0.361, 'u': 0.618, 'v': 0.508, 'w': 0.786, 'x': 0.529, 'y': 0.51, 'z': 0.47, '{': 0.38,
  '|': 0.551, '}': 0.38, '~': 0.572, 'ઁ': 0, 'ં': 0, 'ઃ': 0.248, 'અ': 0.883, 'આ': 1.148, 'ઇ': 0.634, 'ઈ': 0.634,
  'ઉ': 0.625, 'ઊ': 0.888, 'ઋ': 0.831, 'ઌ': 0.697, 'ઍ': 0.883, 'એ': 0.883, 'ઐ': 0.883, 'ઑ': 1.148, 'ઓ': 1.148,
  'ઔ': 1.148, 'ક': 0.511, 'ખ': 0.746, 'ગ': 0.654, 'ઘ': 0.62, 'ઙ': 0.49, 'ચ': 0.66, 'છ': 0.738, 'જ': 0.822,
  'ઝ': 0.698, 'ઞ': 0.692, 'ટ': 0.451, 'ઠ': 0.517, 'ડ': 0.481, 'ઢ': 0.516, 'ણ': 0.821, 'ત': 0.572, 'થ': 0.625,
  'દ': 0.467, 'ધ': 0.606, 'ન': 0.546, 'પ': 0.568, 'ફ': 0.511, 'બ': 0.734, 'ભ': 0.809, 'મ': 0.594, 'ય': 0.594,
  'ર': 0.445, 'લ': 0.651, 'ળ': 0.75, 'વ': 0.577, 'શ': 0.677, 'ષ': 0.578, 'સ': 0.711, 'હ': 0.633, '઼': 0, 'ઽ': 0.471,
  'ા': 0.265, 'િ': 0.265, 'ી': 0.265, 'ુ': 0, 'ૂ': 0, 'ૃ': 0, 'ૄ': 0, 'ૅ': 0, 'ે': 0, 'ૈ': 0, 'ૉ': 0.265, 'ો': 0.265,
  'ૌ': 0.265, '્': 0, 'ૐ': 0.89, 'ૠ': 0.895, 'ૡ': 0.697, 'ૢ': 0, 'ૣ': 0, '૦': 0.551, '૧': 0.551, '૨': 0.551,
  '૩': 0.551, '૪': 0.551, '૫': 0.551, '૬': 0.551, '૭': 0.551, '૮': 0.551, '૯': 0.551, '૰': 0.383, '૱': 0.819,
  'ૹ': 0.822, 'ૺ': 0, 'ૻ': 0, 'ૼ': 0, '૽': 0, '૾': 0, '૿': 0,
};
// Gujarati half forms keep a different share of the full letter per consonant (ક loses only its
// right stem, 0.436 of 0.511; પ shrinks to 0.297 of 0.568; હ to 0.212), so the width of a
// consonant before virama + consonant comes from this table, measured in Chrome as the width of
// C + ્ + ય minus ય. છ ટ ઙ have no half form and keep the explicit virama; દ never has one and
// fuses with its partner instead (see the ligature table).
const GUJ_HALF_WIDTHS = {
  'ક': 0.436, 'ખ': 0.459, 'ગ': 0.406, 'ઘ': 0.374, 'ઙ': 0.439, 'ચ': 0.406, 'છ': 0.718, 'જ': 0.721, 'ઝ': 0.617,
  'ઞ': 0.492, 'ટ': 0.391, 'ઠ': 0.469, 'ડ': 0.42, 'ઢ': 0.471, 'ણ': 0.581, 'ત': 0.353, 'થ': 0.364, 'દ': 0.467,
  'ધ': 0.351, 'ન': 0.342, 'પ': 0.297, 'ફ': 0.437, 'બ': 0.49, 'ભ': 0.54, 'મ': 0.333, 'ય': 0.326, 'લ': 0.439,
  'ળ': 0.468, 'વ': 0.383, 'શ': 0.431, 'ષ': 0.389, 'સ': 0.481, 'હ': 0.212,
};
// Conjuncts drawn as one glyph (or stacked), whose width is nothing like half + full: the whole
// C1 + ્ + C2 cluster takes this advance (Chrome-shaped, Noto Sans Gujarati Regular).
const GUJ_LIGATURE_WIDTHS = {
  'ક્ષ': 0.717, 'જ્ઞ': 0.69, 'ત્ત': 0.669, 'ન્ન': 0.611, 'ગ્ન': 0.678, 'શ્ર': 0.72,
  'દ્દ': 0.467, 'દ્ધ': 0.716, 'દ્ય': 0.606, 'દ્વ': 0.629, 'દ્મ': 0.704, 'દ્ભ': 0.581,
  'ટ્ટ': 0.456, 'ટ્ઠ': 0.456, 'ઠ્ઠ': 0.511, 'ડ્ડ': 0.475, 'ડ્ઢ': 0.475, 'ઙ્ક': 0.502,
  'હ્મ': 0.805, 'હ્ન': 0.64, 'હ્લ': 0.679, 'હ્વ': 0.659, 'ષ્ટ': 0.743, 'ષ્ઠ': 0.824,
  'શ્વ': 0.863, 'શ્ન': 0.733, 'શ્ચ': 0.893, 'ન્દ': 0.775, 'ઞ્જ': 0.805, 'ઞ્ચ': 0.809, 'દ્ગ': 0.49, 'ક્ક': 0.893,
  // A three-consonant cluster that fuses as a whole (checked before the pairs).
  'ત્ત્વ': 0.992,
};
// રુ is a ligature (the hook sits beside ર, 0.684 vs 0.445); રૂ is not.
const GUJ_RU_WIDTH = 0.684;
// Noto Sans Gujarati win metrics are 1076/408: upm/(winAsc+winDesc) = 1000/1484, so the
// compensation is a touch below 1 (0.6402 / 0.6739).
const NOTO_GUJ_RENDER_RATIO = 0.6739;
const NOTO_GUJ_SIZE_COMPENSATION = 0.950;
// Heavier faces vs the Regular table (shaped words in Chrome: Bold 1.07, ExtraBold 1.095, Black 1.12).
const notoGujWidthFactor = (weight) => (weight >= 900 ? 1.12 : weight >= 800 ? 1.095 : weight >= 700 ? 1.07 : 1);
// Baloo Bhai 2 (Gujarati display face for the comic templates): win and typo both 1078/544,
// upm/(winAsc+winDesc) = 1000/1622, compensation 0.6402 / 0.6165. Shaped words measure 1.02×
// (Regular), 1.07× (Bold) and 1.085× (ExtraBold) the Noto estimate, so the table is reused with those.
const BALOO_BHAI_RENDER_RATIO = 0.6165;
const BALOO_BHAI_SIZE_COMPENSATION = 1.038;
const balooBhaiWidthFactor = (weight) => (weight >= 800 ? 1.085 : weight >= 700 ? 1.07 : 1.02);
// Line-box centres in em above the baseline (see the Devanagari note). Noto Sans Gujarati:
// win 1076/408, typo 896/-408 (so the pill needs the same kind of lift as Devanagari). Baloo
// Bhai 2: win and typo both 1078/544. Both set USE_TYPO_METRICS.
const NOTO_GUJ_WIN_CENTRE_EM = 0.334;
const NOTO_GUJ_TYPO_CENTRE_EM = 0.244;
const BALOO_BHAI_WIN_CENTRE_EM = 0.267;
const BALOO_BHAI_TYPO_CENTRE_EM = 0.267;
// Ink extents of a Gujarati word in em (Chrome-shaped text, Noto Sans Gujarati Black and Baloo
// Bhai 2 ExtraBold). With no shirorekha a plain consonant run stops at 0.60 (0.62 in Baloo); the
// i/ii/e/ai/o/au/candra signs, anusvara, candrabindu, the reph and the vowel letters ઈ ઉ ઊ ઍ એ ઐ
// ઑ ઓ ઔ rise to 0.90 (Baloo: 0.91, ૈ ૌ 0.99, candrabindu and reph 0.95), ઇ to 0.76 (Baloo 0.84),
// ધ and the દ્ય દ્મ ligatures to 0.67. Below the baseline: ૄ ૢ ૣ hang to -0.41 (Baloo -0.45),
// ુ ૂ ૃ, a word-final virama and ઙ્ક to -0.27 (Baloo -0.31, હું -0.37), the stacked ટ ઠ ડ ઢ
// conjuncts and દ્ભ to -0.26 (Baloo -0.28, દ્ભ -0.42), a nukta and ૡ to -0.22, ફ to -0.20,
// ણ, the દ્દ દ્ધ દ્વ દ્ર and હ્X conjuncts to about -0.19, ઌ to -0.11, જ્ઞ ક્ષ ગ્ન to -0.08.
const GUJ_INK = {
  plainTop: 0.60, dhaTop: 0.67, iTop: 0.76, markTop: 0.90, highTop: 0.90, aiTop: 0.90,
  plainBottom: 0, gnaBottom: -0.08, jnyaBottom: -0.08, kshaBottom: -0.08, laBottom: -0.11, haBottom: 0, naBottom: -0.19,
  faBottom: -0.20, nuktaBottom: -0.22, stackBottom: -0.26, dbhaBottom: -0.26, matraBottom: -0.27, huBottom: -0.27, deepBottom: -0.41,
};
const BALOO_BHAI_INK = {
  plainTop: 0.62, dhaTop: 0.67, iTop: 0.84, markTop: 0.91, highTop: 0.95, aiTop: 0.99,
  plainBottom: -0.01, gnaBottom: -0.01, jnyaBottom: -0.20, kshaBottom: -0.16, laBottom: -0.08, haBottom: -0.12, naBottom: -0.16,
  faBottom: -0.21, nuktaBottom: -0.15, stackBottom: -0.28, dbhaBottom: -0.42, matraBottom: -0.31, huBottom: -0.37, deepBottom: -0.45,
};
const GUJ_ABOVE_MARKS = /[\u0A81\u0A82\u0ABF\u0AC0\u0AC5\u0AC7-\u0AC9\u0ACB\u0ACC\u0A88\u0A89\u0A8A\u0A8D\u0A8F-\u0A91\u0A93\u0A94\u0AD0]|\u0AB0\u0ACD(?=[\u0A95-\u0AB9])/;
const GUJ_HIGH_MARKS = /\u0A81|\u0AB0\u0ACD(?=[\u0A95-\u0AB9])/;
const GUJ_STACK = /[\u0A9F-\u0AA2]\u0ACD[\u0A9F-\u0AA2]|\u0AE1|\u0AB7\u0ACD\u0A9F\u0ACD\u0AB0/;
const GUJ_NA_CLASS = /\u0AA3|\u0AA6\u0ACD[\u0AA6\u0AA7\u0AB5\u0AB0]|\u0AB9\u0ACD[\u0AAE\u0AA8\u0AAF\u0AB5\u0AB0]|\u0AB9\u0AC3/;
/**
 * Ink top/bottom of `word` in em above the baseline for the Gujarati faces, or null when the
 * word has no Gujarati (a Latin word in a Gujarati caption keeps the face's default glyph box).
 * One cascade for both faces; `display` picks the Baloo Bhai 2 numbers (same keys, so a branch
 * can never read a key the other table lacks).
 */
const gujaratiInkExtents = (word, display = false) => {
  const t = String(word || '');
  if (!/[\u0A80-\u0AFF]/.test(t)) return null;
  const k = display ? BALOO_BHAI_INK : GUJ_INK;
  const top = /[\u0AC8\u0ACC]/.test(t) ? k.aiTop
    : GUJ_HIGH_MARKS.test(t) ? k.highTop
      : GUJ_ABOVE_MARKS.test(t) ? k.markTop
        : /\u0A87/.test(t) ? k.iTop
          : /\u0AA7|\u0AA6\u0ACD[\u0AAF\u0AAE]/.test(t) ? k.dhaTop : k.plainTop;
  const bottom = /[\u0AC4\u0AE2\u0AE3]/.test(t) ? k.deepBottom
    : /\u0AA6\u0ACD\u0AAD/.test(t) ? k.dbhaBottom
      : /\u0AB9[\u0AC1\u0AC2]/.test(t) ? k.huBottom
        : /[\u0AC1-\u0AC3]|\u0ACD$|\u0A99\u0ACD\u0A95/.test(t) ? k.matraBottom
          : GUJ_STACK.test(t) ? k.stackBottom
            : /\u0ABC/.test(t) ? k.nuktaBottom
              : /\u0AAB|\u0AB9\u0ACD\u0AB2/.test(t) ? k.faBottom
                : /\u0A9C\u0ACD\u0A9E/.test(t) ? k.jnyaBottom
                  : /\u0A95\u0ACD\u0AB7/.test(t) ? k.kshaBottom
                    : GUJ_NA_CLASS.test(t) ? k.naBottom
                      : /\u0A97\u0ACD\u0AA8/.test(t) ? k.gnaBottom
                        : /\u0AB9/.test(t) ? k.haBottom
                          : /\u0A8C/.test(t) ? k.laBottom : k.plainBottom;
  return { top, bottom };
};
const GUJ_VIRAMA = 0x0acd;
const GUJ_RA = 0x0ab0;
const GUJ_U_SIGN = 0x0ac1;
const isGujConsonant = (cp) => (cp >= 0x0a95 && cp <= 0x0ab9) || cp === 0x0af9;

// ── Arabic script (Urdu) caption metrics ──
// The first right-to-left, cursive script: none of the Latin caption fonts carry Arabic glyphs, so
// Urdu captions route to the bundled Noto Sans Arabic faces (Baloo Bhaijaan 2, the Arabic member
// of the Baloo family, for the comic templates). A letter has up to four shapes (isolated,
// initial, medial, final) with different advances, so the tables carry one advance per form,
// measured in Chrome at 100px (a zero-width joiner forces each form): [isolated, initial, medial,
// final] per letter, and [isolated, final] for the lam-alef ligatures لا لآ لأ لإ, which fuse into
// one glyph. Digits, punctuation, the harakat (zero-advance marks) and the faces' own Latin
// glyphs (mixed English words render in the same family) are plain numbers like every other
// table; estimateTextWidth picks the form from the neighbours (arabicFormIndex).
const NOTO_ARAB_CHAR_WIDTHS = {
  '0': 0.572, '1': 0.572, '2': 0.572, '3': 0.572, '4': 0.572, '5': 0.572, '6': 0.572, '7': 0.572, '8': 0.572,
  '9': 0.572, 'ـ': 0.27, ' ': 0.26, '،': 0.328, '؛': 0.344, '؟': 0.434, '۔': 0.299, '٪': 0.473, '٫': 0.245,
  '٬': 0.245, '۝': 1.37, 'ٰ': 0, 'ّ': 0, 'َ': 0, 'ُ': 0, 'ِ': 0, 'ْ': 0, 'ٔ': 0, 'ٕ': 0, 'ٓ': 0, 'ٖ': 0, 'ٗ': 0,
  '٘': 0, '٠': 0.572, '١': 0.572, '٢': 0.572, '٣': 0.572, '٤': 0.572, '٥': 0.572, '٦': 0.572, '٧': 0.572, '٨': 0.572,
  '٩': 0.572, '۰': 0.572, '۱': 0.572, '۲': 0.572, '۳': 0.572, '۴': 0.572, '۵': 0.572, '۶': 0.572, '۷': 0.572,
  '۸': 0.572, '۹': 0.572, '!': 0.269, '"': 0.408, '#': 0.646, '$': 0.572, '%': 0.831, '&': 0.732, "'": 0.225,
  '(': 0.3, ')': 0.3, '*': 0.551, '+': 0.572, ',': 0.268, '-': 0.322, '.': 0.268, '/': 0.372, ':': 0.268, ';': 0.268,
  '<': 0.572, '=': 0.572, '>': 0.572, '?': 0.434, '@': 0.899, 'A': 0.639, 'B': 0.65, 'C': 0.632, 'D': 0.73,
  'E': 0.556, 'F': 0.519, 'G': 0.728, 'H': 0.741, 'I': 0.339, 'J': 0.273, 'K': 0.619, 'L': 0.524, 'M': 0.907,
  'N': 0.76, 'O': 0.781, 'P': 0.605, 'Q': 0.781, 'R': 0.622, 'S': 0.549, 'T': 0.556, 'U': 0.731, 'V': 0.6, 'W': 0.93,
  'X': 0.586, 'Y': 0.566, 'Z': 0.572, '[': 0.329, '\\': 0.372, ']': 0.329, '^': 0.572, '_': 0.444, '`': 0.281,
  'a': 0.561, 'b': 0.615, 'c': 0.48, 'd': 0.615, 'e': 0.564, 'f': 0.344, 'g': 0.615, 'h': 0.618, 'i': 0.258,
  'j': 0.258, 'k': 0.534, 'l': 0.258, 'm': 0.935, 'n': 0.618, 'o': 0.605, 'p': 0.615, 'q': 0.615, 'r': 0.413,
  's': 0.479, 't': 0.361, 'u': 0.618, 'v': 0.508, 'w': 0.786, 'x': 0.529, 'y': 0.51, 'z': 0.47, '{': 0.38,
  '|': 0.551, '}': 0.38, '~': 0.572,
  'ء': [0.411, 0.411, 0.411, 0.411], 'آ': [0.235, 0.235, 0.289, 0.289], 'أ': [0.235, 0.235, 0.289, 0.289],
  'ؤ': [0.447, 0.447, 0.472, 0.472], 'إ': [0.238, 0.238, 0.291, 0.291], 'ئ': [0.76, 0.269, 0.343, 0.736],
  'ا': [0.238, 0.238, 0.291, 0.291], 'ب': [0.993, 0.269, 0.343, 1.093], 'ة': [0.405, 0.405, 0.486, 0.486],
  'ت': [0.993, 0.299, 0.373, 1.093], 'ث': [0.993, 0.299, 0.373, 1.093], 'ج': [0.639, 0.589, 0.637, 0.649],
  'ح': [0.639, 0.589, 0.637, 0.649], 'خ': [0.639, 0.589, 0.637, 0.649], 'د': [0.477, 0.477, 0.532, 0.532],
  'ذ': [0.477, 0.477, 0.532, 0.532], 'ر': [0.367, 0.367, 0.396, 0.396], 'ز': [0.367, 0.367, 0.396, 0.396],
  'س': [1.209, 0.784, 0.85, 1.269], 'ش': [1.209, 0.784, 0.85, 1.269], 'ص': [1.306, 0.903, 0.949, 1.352],
  'ض': [1.306, 0.903, 0.949, 1.352], 'ط': [0.788, 0.677, 0.71, 0.831], 'ظ': [0.788, 0.677, 0.71, 0.831],
  'ع': [0.507, 0.503, 0.521, 0.531], 'غ': [0.507, 0.503, 0.521, 0.531], 'ف': [0.983, 0.447, 0.495, 1.098],
  'ق': [0.774, 0.447, 0.495, 0.808], 'ك': [0.882, 0.412, 0.508, 0.922], 'ل': [0.695, 0.26, 0.3, 0.73],
  'م': [0.484, 0.525, 0.578, 0.562], 'ن': [0.685, 0.269, 0.343, 0.73], 'ه': [0.672, 0.574, 0.507, 0.486],
  'و': [0.447, 0.447, 0.472, 0.472], 'ى': [0.76, 0.269, 0.343, 0.736], 'ي': [0.76, 0.299, 0.373, 0.736],
  'ٱ': [0.235, 0.235, 0.289, 0.289], 'ٲ': [0.235, 0.235, 0.289, 0.289], 'ٳ': [0.238, 0.238, 0.291, 0.291],
  'ٴ': [0.411, 0.411, 0.411, 0.411], 'ٵ': [0.649, 0.649, 0.702, 0.702], 'ٶ': [0.858, 0.858, 0.883, 0.883],
  'ٷ': [0.858, 0.858, 0.883, 0.883], 'ٸ': [1.171, 0.68, 0.754, 1.147], 'ٹ': [0.993, 0.299, 0.373, 1.093],
  'ٺ': [0.993, 0.269, 0.343, 1.093], 'ٻ': [0.993, 0.269, 0.343, 1.093], 'ټ': [0.993, 0.299, 0.373, 1.093],
  'ٽ': [0.993, 0.299, 0.373, 1.093], 'پ': [0.993, 0.299, 0.373, 1.093], 'ٿ': [0.993, 0.299, 0.373, 1.093],
  'ڀ': [0.993, 0.299, 0.373, 1.093], 'ځ': [0.639, 0.589, 0.637, 0.649], 'ڂ': [0.639, 0.589, 0.637, 0.649],
  'ڃ': [0.659, 0.589, 0.637, 0.719], 'ڄ': [0.639, 0.589, 0.637, 0.649], 'څ': [0.639, 0.589, 0.637, 0.649],
  'چ': [0.659, 0.589, 0.637, 0.719], 'ڇ': [0.659, 0.589, 0.637, 0.719], 'ڈ': [0.477, 0.477, 0.532, 0.532],
  'ډ': [0.477, 0.477, 0.532, 0.532], 'ڊ': [0.477, 0.477, 0.532, 0.532], 'ڋ': [0.477, 0.477, 0.532, 0.532],
  'ڌ': [0.477, 0.477, 0.532, 0.532], 'ڍ': [0.477, 0.477, 0.532, 0.532], 'ڎ': [0.477, 0.477, 0.532, 0.532],
  'ڏ': [0.477, 0.477, 0.532, 0.532], 'ڐ': [0.477, 0.477, 0.532, 0.532], 'ڑ': [0.367, 0.367, 0.396, 0.396],
  'ڒ': [0.367, 0.367, 0.396, 0.396], 'ړ': [0.367, 0.367, 0.396, 0.396], 'ڔ': [0.367, 0.367, 0.396, 0.396],
  'ڕ': [0.367, 0.367, 0.396, 0.396], 'ږ': [0.367, 0.367, 0.396, 0.396], 'ڗ': [0.367, 0.367, 0.396, 0.396],
  'ژ': [0.367, 0.367, 0.396, 0.396], 'ڙ': [0.367, 0.367, 0.396, 0.396], 'ښ': [1.209, 0.784, 0.85, 1.269],
  'ڛ': [1.209, 0.784, 0.85, 1.269], 'ڜ': [1.209, 0.784, 0.85, 1.269], 'ڝ': [1.306, 0.903, 0.949, 1.352],
  'ڞ': [1.306, 0.903, 0.949, 1.352], 'ڟ': [0.788, 0.677, 0.71, 0.831], 'ڠ': [0.507, 0.503, 0.521, 0.531],
  'ڡ': [0.983, 0.447, 0.495, 1.098], 'ڢ': [0.983, 0.447, 0.495, 1.098], 'ڣ': [0.983, 0.447, 0.495, 1.098],
  'ڤ': [0.983, 0.447, 0.495, 1.098], 'ڥ': [0.983, 0.447, 0.495, 1.098], 'ڦ': [0.983, 0.447, 0.495, 1.098],
  'ڧ': [0.774, 0.447, 0.495, 0.808], 'ڨ': [0.774, 0.447, 0.495, 0.808], 'ک': [0.877, 0.412, 0.508, 0.975],
  'ڪ': [1.018, 0.763, 0.838, 1.091], 'ګ': [0.877, 0.412, 0.508, 0.975], 'ڬ': [0.882, 0.412, 0.508, 0.922],
  'ڭ': [0.882, 0.412, 0.508, 0.922], 'ڮ': [0.882, 0.412, 0.508, 0.922], 'گ': [0.877, 0.412, 0.508, 0.975],
  'ڰ': [0.877, 0.412, 0.508, 0.975], 'ڱ': [0.877, 0.412, 0.508, 0.975], 'ڲ': [0.877, 0.412, 0.508, 0.975],
  'ڳ': [0.877, 0.412, 0.508, 0.975], 'ڴ': [0.877, 0.412, 0.508, 0.975], 'ڵ': [0.695, 0.26, 0.3, 0.73],
  'ڶ': [0.695, 0.26, 0.3, 0.73], 'ڷ': [0.695, 0.26, 0.3, 0.73], 'ڸ': [0.695, 0.26, 0.3, 0.73],
  'ڹ': [0.685, 0.269, 0.343, 0.73], 'ں': [0.685, 0.269, 0.343, 0.73], 'ڻ': [0.685, 0.299, 0.373, 0.73],
  'ڼ': [0.685, 0.269, 0.343, 0.73], 'ڽ': [0.685, 0.299, 0.373, 0.73], 'ھ': [0.672, 0.574, 0.507, 0.614],
  'ڿ': [0.659, 0.589, 0.637, 0.719], 'ۀ': [0.405, 0.405, 0.486, 0.486], 'ہ': [0.405, 0.269, 0.438, 0.479],
  'ۂ': [0.405, 0.269, 0.438, 0.479], 'ۃ': [0.405, 0.405, 0.479, 0.479], 'ۄ': [0.447, 0.447, 0.472, 0.472],
  'ۅ': [0.447, 0.447, 0.472, 0.472], 'ۆ': [0.447, 0.447, 0.472, 0.472], 'ۇ': [0.447, 0.447, 0.472, 0.472],
  'ۈ': [0.447, 0.447, 0.472, 0.472], 'ۉ': [0.447, 0.447, 0.472, 0.472], 'ۊ': [0.447, 0.447, 0.472, 0.472],
  'ۋ': [0.447, 0.447, 0.472, 0.472], 'ی': [0.76, 0.299, 0.373, 0.736], 'ۍ': [0.76, 0.76, 0.736, 0.736],
  'ێ': [0.76, 0.299, 0.373, 0.736], 'ۏ': [0.447, 0.447, 0.472, 0.472], 'ې': [0.76, 0.269, 0.343, 0.736],
  'ۑ': [0.76, 0.299, 0.373, 0.736], 'ے': [0.942, 0.942, 0.549, 0.549], 'ۓ': [0.942, 0.942, 0.549, 0.549],
  'ە': [0.405, 0.405, 0.486, 0.486], 'ۮ': [0.477, 0.477, 0.532, 0.532], 'ۯ': [0.367, 0.367, 0.396, 0.396],
  'ۺ': [1.209, 0.784, 0.85, 1.269], 'ۻ': [1.306, 0.903, 0.949, 1.352], 'ۼ': [0.507, 0.503, 0.521, 0.531],
  'ۿ': [0.672, 0.574, 0.507, 0.614], 'لا': [0.582, 0.599], 'لآ': [0.582, 0.599], 'لأ': [0.582, 0.599],
  'لإ': [0.582, 0.599],
};
const NOTO_ARAB_INK_CODES = {
  'آ': 'TTTTFFFF', 'أ': 'XXXXFFFF', 'ؤ': 'HHHHMMMM', 'إ': 'HHHHMMMM', 'ئ': 'STHSMFFM', 'ا': 'HHHHFFFF',
  'ب': 'SSSSMMMM', 'ة': 'TTTTFFFF', 'ت': 'MHMMFFFF', 'ث': 'HTHHFFFF', 'ج': 'MSSMDMMD', 'ح': 'MSSMDFFD',
  'خ': 'THHTDFFD', 'د': 'MMMMFFFF', 'ذ': 'HHHHFFFF', 'ر': 'SSSSMMMM', 'ز': 'MMMMMMMM', 'س': 'SSSSMFFM',
  'ش': 'TTTTMFFM', 'ص': 'SSSSMFFM', 'ض': 'HHHHMFFM', 'ط': 'HHHHFFFF', 'ظ': 'HHHHFFFF', 'ع': 'MSSSDFFD',
  'غ': 'THHHDFFD', 'ف': 'TTHHFFFF', 'ق': 'HTHMMFFM', 'ك': 'HHHHFFFF', 'ل': 'HHHHMFFM', 'م': 'SSSSDFFD',
  'ن': 'MHMMMFFM', 'ه': 'MMSMFFMF', 'و': 'SSSSMMMM', 'ى': 'SSSSMFFM', 'ي': 'SSSSDMMD', 'ٱ': 'XXXXFFFF',
  'ٲ': 'XXXXFFFF', 'ٳ': 'HHHHMMMM', 'ٴ': 'MMMMFFFF', 'ٵ': 'HHHHFFFF', 'ٶ': 'MMMMMMMM', 'ٷ': 'HHHHMMMM',
  'ٸ': 'MMMMMFFM', 'ٹ': 'HTHHFFFF', 'ٺ': 'HTTHFFFF', 'ٻ': 'SSSSDDDD', 'ټ': 'MHMMFFFF', 'ٽ': 'HTHHFFFF',
  'پ': 'SSSSDDDD', 'ٿ': 'HTHHFFFF', 'ڀ': 'SSSSDDDD', 'ځ': 'TTTTDFFD', 'ڂ': 'XTTXDFFD', 'ڃ': 'MSSMDMMD',
  'ڄ': 'MSSMDDDD', 'څ': 'TTTTDFFD', 'چ': 'MSSMDDDD', 'ڇ': 'MSSMDDDD', 'ڈ': 'TTTTFFFF', 'ډ': 'MMMMFFFF',
  'ڊ': 'MMMMMMMM', 'ڋ': 'TTTTMMMM', 'ڌ': 'HHHHFFFF', 'ڍ': 'MMMMMMMM', 'ڎ': 'TTTTFFFF', 'ڏ': 'TTTTFFFF',
  'ڐ': 'TTTTFFFF', 'ڑ': 'TTTTMMMM', 'ڒ': 'HHHHMMMM', 'ړ': 'SSSSMMMM', 'ڔ': 'SSSSDDDD', 'ڕ': 'SSSSDDDD',
  'ږ': 'SSSSDDDD', 'ڗ': 'MMMMMMMM', 'ژ': 'TTTTMMMM', 'ڙ': 'HHHHMMMM', 'ښ': 'MMMMMMMM', 'ڛ': 'SSSSDDDD',
  'ڜ': 'TTTTDDDD', 'ڝ': 'SSSSMMMM', 'ڞ': 'TTTTMFFM', 'ڟ': 'TTTTFFFF', 'ڠ': 'XTTTDFFD', 'ڡ': 'MMSSFFFF',
  'ڢ': 'MMSSMMMM', 'ڣ': 'TTHHMMMM', 'ڤ': 'XXTTFFFF', 'ڥ': 'MMSSDDDD', 'ڦ': 'XXTTFFFF', 'ڧ': 'HTHMMFFM',
  'ڨ': 'TXTTMFFM', 'ک': 'HHHHFFFF', 'ڪ': 'MMMMFFFF', 'ګ': 'HHHHFFFF', 'ڬ': 'TTTTFFFF', 'ڭ': 'TTTTFFFF',
  'ڮ': 'HHHHDDDD', 'گ': 'TTTTFFFF', 'ڰ': 'TTTTFFFF', 'ڱ': 'XXXXFFFF', 'ڲ': 'TTTTMMMM', 'ڳ': 'TTTTDDDD',
  'ڴ': 'XXXXFFFF', 'ڵ': 'XXXXMFFM', 'ڶ': 'XXXXMFFM', 'ڷ': 'XXXXMFFM', 'ڸ': 'HHHHXDDX', 'ڹ': 'MHMMDMMD',
  'ں': 'SSSSMFFM', 'ڻ': 'HTHHMFFM', 'ڼ': 'MHMMDFFD', 'ڽ': 'HSSHMDDM', 'ھ': 'MMSSFFMM', 'ڿ': 'THHTDDDD',
  'ۀ': 'TTXXFFFF', 'ہ': 'MSSSFMMF', 'ۂ': 'TTMHFMMF', 'ۃ': 'TTMMFFFF', 'ۄ': 'SSSSMMMM', 'ۅ': 'SSSSMMMM',
  'ۆ': 'HHHHMMMM', 'ۇ': 'HHHHMMMM', 'ۈ': 'HHHHMMMM', 'ۉ': 'HHHHMMMM', 'ۊ': 'MMMMMMMM', 'ۋ': 'TTTTMMMM',
  'ی': 'SSSSMMMM', 'ۍ': 'SSSSMMMM', 'ێ': 'STHSMMMM', 'ۏ': 'MMMMMMMM', 'ې': 'SSSSXDDX', 'ۑ': 'SSSSXDDX',
  'ے': 'SSSSFFMM', 'ۓ': 'TTSSFFMM', 'ە': 'MMMMFFFF', 'ۮ': 'TTTTFFFF', 'ۯ': 'HHHHMMMM', 'ۺ': 'TTTTMMMM',
  'ۻ': 'HHHHMMMM', 'ۼ': 'THHHDMMD', 'ۿ': 'TTTTFFMM',
};
// Noto Sans Arabic win metrics are 1431/738: upm/(winAsc+winDesc) = 1000/2169, a tall box, so
// libass burns it small at a given Fontsize and the compensation is 0.6402 / 0.4610.
const NOTO_ARAB_RENDER_RATIO = 0.4610;
const NOTO_ARAB_SIZE_COMPENSATION = 1.389;
// Heavier faces vs the Regular table (mean over every letter form in Chrome: Bold 1.098,
// ExtraBold 1.132, Black 1.172; the Latin glyphs grow less, 1.0 / 1.045 / 1.097).
const notoArabWidthFactor = (weight) => (weight >= 900 ? 1.17 : weight >= 800 ? 1.13 : weight >= 700 ? 1.10 : 1);
// The Latin glyphs of the face grow less than the letters with weight, and an Urdu caption is
// full of English words, so the estimate table a heavy weight measures with scales its Latin,
// digit and punctuation entries down by the ratio of the two (weightedArabicTable).
const notoArabLatinWidthFactor = (weight) => (weight >= 900 ? 1.097 : weight >= 800 ? 1.045 : 1);
// Line-box centres in em above the baseline (see the Devanagari note): win 1431/738, typo
// 1374/-738, USE_TYPO_METRICS set.
const NOTO_ARAB_WIN_CENTRE_EM = 0.347;
const NOTO_ARAB_TYPO_CENTRE_EM = 0.318;
const BALOO_BHAIJAAN_CHAR_WIDTHS = {
  '0': 0.583, '1': 0.357, '2': 0.508, '3': 0.506, '4': 0.548, '5': 0.538, '6': 0.55, '7': 0.458, '8': 0.569,
  '9': 0.552, 'ـ': 0.201, ' ': 0.2, '،': 0.225, '؛': 0.196, '؟': 0.499, '۔': 0.406, '٪': 0.463, '٫': 0.243,
  '٬': 0.228, '۝': 0.864, 'ٰ': 0, 'ّ': 0, 'َ': 0, 'ُ': 0, 'ِ': 0, 'ْ': 0, 'ٔ': 0, 'ٕ': 0, 'ٓ': 0, 'ٖ': 0, 'ٗ': 0,
  '٘': 0, '٠': 0.298, '١': 0.269, '٢': 0.555, '٣': 0.617, '٤': 0.499, '٥': 0.578, '٦': 0.545, '٧': 0.582, '٨': 0.598,
  '٩': 0.546, '۰': 0.298, '۱': 0.269, '۲': 0.555, '۳': 0.617, '۴': 0.65, '۵': 0.67, '۶': 0.545, '۷': 0.535,
  '۸': 0.598, '۹': 0.546, '!': 0.25, '"': 0.27, '#': 0.667, '$': 0.522, '%': 0.772, '&': 0.637, "'": 0.139,
  '(': 0.254, ')': 0.254, '*': 0.299, '+': 0.548, ',': 0.145, '-': 0.389, '.': 0.141, '/': 0.305, ':': 0.189,
  ';': 0.202, '<': 0.473, '=': 0.518, '>': 0.46, '?': 0.459, '@': 1, 'A': 0.592, 'B': 0.599, 'C': 0.588, 'D': 0.652,
  'E': 0.531, 'F': 0.517, 'G': 0.639, 'H': 0.654, 'I': 0.262, 'J': 0.385, 'K': 0.566, 'L': 0.501, 'M': 0.78,
  'N': 0.66, 'O': 0.689, 'P': 0.567, 'Q': 0.69, 'R': 0.589, 'S': 0.523, 'T': 0.523, 'U': 0.649, 'V': 0.583,
  'W': 0.803, 'X': 0.591, 'Y': 0.55, 'Z': 0.557, '[': 0.268, '\\': 0.341, ']': 0.269, '^': 0.463, '_': 0.465,
  '`': 0.243, 'a': 0.499, 'b': 0.549, 'c': 0.478, 'd': 0.547, 'e': 0.519, 'f': 0.399, 'g': 0.537, 'h': 0.551,
  'i': 0.241, 'j': 0.243, 'k': 0.487, 'l': 0.246, 'm': 0.816, 'n': 0.55, 'o': 0.553, 'p': 0.546, 'q': 0.546,
  'r': 0.382, 's': 0.447, 't': 0.372, 'u': 0.55, 'v': 0.495, 'w': 0.729, 'x': 0.482, 'y': 0.489, 'z': 0.469,
  '{': 0.281, '|': 0.251, '}': 0.281, '~': 0.448,
  'ء': [0.581, 0.581, 0.581, 0.581], 'آ': [0.25, 0.25, 0.25, 0.25], 'أ': [0.25, 0.25, 0.25, 0.25],
  'ؤ': [0.455, 0.455, 0.462, 0.462], 'إ': [0.25, 0.25, 0.25, 0.25], 'ئ': [0.715, 0.299, 0.301, 0.68],
  'ا': [0.25, 0.25, 0.25, 0.25], 'ب': [0.847, 0.299, 0.301, 0.901], 'ة': [0.476, 0.476, 0.455, 0.455],
  'ت': [0.847, 0.299, 0.293, 0.901], 'ث': [0.847, 0.299, 0.293, 0.901], 'ج': [0.588, 0.622, 0.67, 0.658],
  'ح': [0.588, 0.622, 0.67, 0.658], 'خ': [0.588, 0.622, 0.67, 0.658], 'د': [0.518, 0.518, 0.553, 0.553],
  'ذ': [0.518, 0.518, 0.553, 0.553], 'ر': [0.364, 0.364, 0.384, 0.384], 'ز': [0.364, 0.364, 0.384, 0.384],
  'س': [1.065, 0.692, 0.708, 1.081], 'ش': [1.065, 0.692, 0.69, 1.081], 'ص': [1.179, 0.813, 0.85, 1.216],
  'ض': [1.179, 0.813, 0.832, 1.216], 'ط': [0.696, 0.657, 0.694, 0.733], 'ظ': [0.696, 0.657, 0.694, 0.733],
  'ع': [0.486, 0.487, 0.571, 0.564], 'غ': [0.486, 0.487, 0.571, 0.564], 'ف': [0.855, 0.436, 0.476, 0.846],
  'ق': [0.704, 0.436, 0.476, 0.672], 'ك': [0.758, 0.424, 0.427, 0.768], 'ل': [0.643, 0.256, 0.254, 0.643],
  'م': [0.58, 0.552, 0.592, 0.619], 'ن': [0.636, 0.299, 0.301, 0.655], 'ه': [0.476, 0.571, 0.442, 0.455],
  'و': [0.455, 0.455, 0.462, 0.462], 'ى': [0.715, 0.715, 0.715, 0.68], 'ي': [0.715, 0.299, 0.301, 0.68],
  'ٱ': [0.25, 0.25, 0.25, 0.25], 'ٲ': [0.292, 0.292, 0.292, 0.292], 'ٳ': [0.292, 0.292, 0.292, 0.292],
  'ٴ': [0, 0, 0, 0], 'ٵ': [0.423, 0.423, 0.423, 0.423], 'ٶ': [0.528, 0.528, 0.528, 0.528],
  'ٷ': [0.488, 0.488, 0.488, 0.488], 'ٸ': [0.641, 0.641, 0.641, 0.641], 'ٹ': [0.847, 0.299, 0.293, 0.901],
  'ٺ': [0.64, 0.64, 0.64, 0.64], 'ٻ': [0.64, 0.64, 0.64, 0.64], 'ټ': [0.64, 0.64, 0.64, 0.64],
  'ٽ': [0.64, 0.64, 0.64, 0.64], 'پ': [0.847, 0.299, 0.293, 0.901], 'ٿ': [0.64, 0.64, 0.64, 0.64],
  'ڀ': [0.64, 0.64, 0.64, 0.64], 'ځ': [0.584, 0.584, 0.584, 0.584], 'ڂ': [0.584, 0.584, 0.584, 0.584],
  'ڃ': [0.584, 0.584, 0.584, 0.584], 'ڄ': [0.584, 0.584, 0.584, 0.584], 'څ': [0.584, 0.584, 0.584, 0.584],
  'چ': [0.588, 0.622, 0.67, 0.658], 'ڇ': [0.584, 0.584, 0.584, 0.584], 'ڈ': [0.518, 0.518, 0.553, 0.553],
  'ډ': [0.408, 0.408, 0.408, 0.408], 'ڊ': [0.408, 0.408, 0.408, 0.408], 'ڋ': [0.408, 0.408, 0.408, 0.408],
  'ڌ': [0.408, 0.408, 0.408, 0.408], 'ڍ': [0.408, 0.408, 0.408, 0.408], 'ڎ': [0.408, 0.408, 0.408, 0.408],
  'ڏ': [0.408, 0.408, 0.408, 0.408], 'ڐ': [0.408, 0.408, 0.408, 0.408], 'ڑ': [0.364, 0.364, 0.384, 0.384],
  'ڒ': [0.408, 0.408, 0.408, 0.408], 'ړ': [0.408, 0.408, 0.408, 0.408], 'ڔ': [0.408, 0.408, 0.408, 0.408],
  'ڕ': [0.408, 0.408, 0.408, 0.408], 'ږ': [0.408, 0.408, 0.408, 0.408], 'ڗ': [0.408, 0.408, 0.408, 0.408],
  'ژ': [0.364, 0.364, 0.384, 0.384], 'ڙ': [0.408, 0.408, 0.408, 0.408], 'ښ': [0.913, 0.913, 0.913, 0.913],
  'ڛ': [0.913, 0.913, 0.913, 0.913], 'ڜ': [0.913, 0.913, 0.913, 0.913], 'ڝ': [1.11, 1.11, 1.11, 1.11],
  'ڞ': [1.11, 1.11, 1.11, 1.11], 'ڟ': [0.597, 0.597, 0.597, 0.597], 'ڠ': [0.584, 0.584, 0.584, 0.584],
  'ڡ': [0.784, 0.784, 0.784, 0.784], 'ڢ': [0.784, 0.784, 0.784, 0.784], 'ڣ': [0.784, 0.784, 0.784, 0.784],
  'ڤ': [0.855, 0.436, 0.476, 0.846], 'ڥ': [0.784, 0.784, 0.784, 0.784], 'ڦ': [0.784, 0.784, 0.784, 0.784],
  'ڧ': [0.548, 0.548, 0.548, 0.548], 'ڨ': [0.548, 0.548, 0.548, 0.548], 'ک': [0.828, 0.424, 0.427, 0.836],
  'ڪ': [0.704, 0.704, 0.704, 0.704], 'ګ': [0.704, 0.704, 0.704, 0.704], 'ڬ': [0.545, 0.545, 0.545, 0.545],
  'ڭ': [0.545, 0.545, 0.545, 0.545], 'ڮ': [0.545, 0.545, 0.545, 0.545], 'گ': [0.828, 0.424, 0.427, 0.836],
  'ڰ': [0.704, 0.704, 0.704, 0.704], 'ڱ': [0.704, 0.704, 0.704, 0.704], 'ڲ': [0.704, 0.704, 0.704, 0.704],
  'ڳ': [0.704, 0.704, 0.704, 0.704], 'ڴ': [0.704, 0.704, 0.704, 0.704], 'ڵ': [0.475, 0.475, 0.475, 0.475],
  'ڶ': [0.475, 0.475, 0.475, 0.475], 'ڷ': [0.475, 0.475, 0.475, 0.475], 'ڸ': [0.475, 0.475, 0.475, 0.475],
  'ڹ': [0.534, 0.534, 0.534, 0.534], 'ں': [0.636, 0.636, 0.636, 0.655], 'ڻ': [0.534, 0.534, 0.534, 0.534],
  'ڼ': [0.534, 0.534, 0.534, 0.534], 'ڽ': [0.534, 0.534, 0.534, 0.534], 'ھ': [0.651, 0.571, 0.46, 0.521],
  'ڿ': [0.584, 0.584, 0.584, 0.584], 'ۀ': [0.476, 0.476, 0.455, 0.455], 'ہ': [0.476, 0.299, 0.433, 0.514],
  'ۂ': [0.476, 0.476, 0.476, 0.514], 'ۃ': [0.476, 0.476, 0.514, 0.514], 'ۄ': [0.393, 0.393, 0.393, 0.393],
  'ۅ': [0.393, 0.393, 0.393, 0.393], 'ۆ': [0.393, 0.393, 0.393, 0.393], 'ۇ': [0.393, 0.393, 0.393, 0.393],
  'ۈ': [0.393, 0.393, 0.393, 0.393], 'ۉ': [0.393, 0.393, 0.393, 0.393], 'ۊ': [0.393, 0.393, 0.393, 0.393],
  'ۋ': [0.393, 0.393, 0.393, 0.393], 'ی': [0.715, 0.299, 0.301, 0.68], 'ۍ': [0.714, 0.714, 0.714, 0.714],
  'ێ': [0.641, 0.641, 0.641, 0.641], 'ۏ': [0.393, 0.393, 0.393, 0.393], 'ې': [0.641, 0.641, 0.641, 0.641],
  'ۑ': [0.641, 0.641, 0.641, 0.641], 'ے': [0.796, 0.796, 0.557, 0.557], 'ۓ': [0.796, 0.796, 0.557, 0.557],
  'ە': [0.476, 0.476, 0.455, 0.455], 'ۮ': [0.847, 0.847, 0.901, 0.901], 'ۯ': [0.408, 0.408, 0.408, 0.408],
  'ۺ': [0.913, 0.913, 0.913, 0.913], 'ۻ': [1.11, 1.11, 1.11, 1.11], 'ۼ': [0.584, 0.584, 0.584, 0.584],
  'ۿ': [0.52, 0.52, 0.52, 0.52], 'لا': [0.528, 0.549], 'لآ': [0.528, 0.549], 'لأ': [0.528, 0.549],
  'لإ': [0.528, 0.549],
};
const BALOO_BHAIJAAN_INK_CODES = {
  'آ': 'TTTTFFFF', 'أ': 'XXXXFFFF', 'ؤ': 'HHHHMMMM', 'إ': 'MMMMMMMM', 'ئ': 'MHHMMFFM', 'ا': 'MMMMFFFF',
  'ب': 'SSSSMMMM', 'ة': 'HHHHFFFF', 'ت': 'MMMMFFFF', 'ث': 'HTHHFFFF', 'ج': 'SSSSMMMM', 'ح': 'SSSSMFFM',
  'خ': 'MMMMMFFM', 'ذ': 'HHHHFFFF', 'ر': 'SSSSMMMM', 'ز': 'MMMMMMMM', 'س': 'SSSSMFFM', 'ش': 'HHHHMFFM',
  'ص': 'SSSSMFFM', 'ض': 'MMMMMFFM', 'ط': 'MMMMFFFF', 'ظ': 'MMMMFFFF', 'ع': 'SSSSDFFD', 'غ': 'HHHHDFFD',
  'ف': 'TTHHFFFF', 'ق': 'MTHMMFFM', 'ك': 'MMMMFFFF', 'ل': 'MMMMMFFM', 'م': 'SSSSDFFD', 'ن': 'SMMSMFFM',
  'ه': 'SMSSFFMF', 'و': 'SSSSMMMM', 'ى': 'SSSSMMMM', 'ي': 'SSSSDMMD', 'ٱ': 'TTTTFFFF', 'ٲ': 'HHHHFFFF',
  'ٳ': 'HHHHMMMM', 'ٴ': 'HHHHFFFF', 'ٵ': 'HHHHFFFF', 'ٶ': 'HHHHMMMM', 'ٷ': 'HHHHMMMM', 'ٸ': 'HHHHMMMM',
  'ٹ': 'HTHHFFFF', 'ٺ': 'MMMMFFFF', 'ٻ': 'SSSSMMMM', 'ٽ': 'MMMMFFFF', 'پ': 'SSSSDDDD', 'ٿ': 'MMMMFFFF',
  'ڀ': 'SSSSMMMM', 'ځ': 'HHHHDDDD', 'ڂ': 'TTTTMMMM', 'ڃ': 'SSSSMMMM', 'ڄ': 'SSSSMMMM', 'څ': 'TTTTMMMM',
  'چ': 'SSSSMDDM', 'ڇ': 'SSSSMMMM', 'ڈ': 'TTTTFFFF', 'ڊ': 'SSSSMMMM', 'ڋ': 'HHHHMMMM', 'ڌ': 'HHHHFFFF',
  'ڍ': 'SSSSMMMM', 'ڎ': 'HHHHFFFF', 'ڏ': 'HHHHFFFF', 'ڐ': 'HHHHFFFF', 'ڑ': 'HHHHMMMM', 'ڒ': 'MMMMMMMM',
  'ړ': 'SSSSMMMM', 'ڔ': 'SSSSDDDD', 'ڕ': 'SSSSDDDD', 'ږ': 'MMMMDDDD', 'ڗ': 'MMMMMMMM', 'ژ': 'HHHHMMMM',
  'ڙ': 'HHHHMMMM', 'ښ': 'MMMMMMMM', 'ڛ': 'SSSSMMMM', 'ڜ': 'HHHHMMMM', 'ڝ': 'SSSSMMMM', 'ڞ': 'HHHHMMMM',
  'ڟ': 'HHHHFFFF', 'ڠ': 'HHHHMMMM', 'ڢ': 'SSSSMMMM', 'ڣ': 'HHHHMMMM', 'ڤ': 'XXTTFFFF', 'ڥ': 'SSSSMMMM',
  'ڦ': 'TTTTFFFF', 'ڧ': 'HHHHMMMM', 'ڨ': 'HHHHMMMM', 'ک': 'MMMMFFFF', 'ڪ': 'MMMMFFFF', 'ګ': 'MMMMFFFF',
  'ڬ': 'HHHHFFFF', 'ڭ': 'TTTTFFFF', 'ڮ': 'HHHHMMMM', 'گ': 'TTTTFFFF', 'ڰ': 'HHHHFFFF', 'ڱ': 'HHHHFFFF',
  'ڲ': 'HHHHFFFF', 'ڳ': 'MMMMMMMM', 'ڴ': 'HHHHFFFF', 'ڵ': 'HHHHMMMM', 'ڶ': 'TTTTMMMM', 'ڷ': 'XXXXMMMM',
  'ڸ': 'HHHHDDDD', 'ڹ': 'MMMMDDDD', 'ں': 'SSSSMMMM', 'ڻ': 'HHHHMMMM', 'ڼ': 'MMMMMMMM', 'ڽ': 'HHHHMMMM',
  'ھ': 'MMSSFFMM', 'ڿ': 'HHHHMMMM', 'ۀ': 'TTTTFFFF', 'ہ': 'SSSSFMMF', 'ۂ': 'TTTMFFFF', 'ۃ': 'HHSSFFFF',
  'ۄ': 'SSSSMMMM', 'ۅ': 'SSSSMMMM', 'ۆ': 'HHHHMMMM', 'ۇ': 'HHHHMMMM', 'ۈ': 'HHHHMMMM', 'ۉ': 'HHHHMMMM',
  'ۊ': 'HHHHMMMM', 'ۋ': 'TTTTMMMM', 'ی': 'SSSSMMMM', 'ۍ': 'SSSSMMMM', 'ێ': 'MMMMMMMM', 'ۏ': 'HHHHMMMM',
  'ې': 'SSSSDDDD', 'ۑ': 'SSSSDDDD', 'ے': 'SSSSFFMM', 'ۓ': 'TTSSFFMM', 'ۯ': 'MMMMMMMM', 'ۺ': 'HHHHMMMM',
  'ۻ': 'HHHHMMMM', 'ۼ': 'HHHHMMMM', 'ۿ': 'TTTTFFFF',
};
// Baloo Bhaijaan 2: win and typo both 1080/632, upm/(winAsc+winDesc) = 1000/1712, compensation
// 0.6402 / 0.5841. Its own shaped advances are tabled above (the design is nothing like Noto's);
// heavier faces: Bold 1.119, ExtraBold 1.13 over the letter forms.
const BALOO_BHAIJAAN_RENDER_RATIO = 0.5841;
const BALOO_BHAIJAAN_SIZE_COMPENSATION = 1.096;
const balooBhaijaanWidthFactor = (weight) => (weight >= 800 ? 1.13 : weight >= 700 ? 1.12 : 1);
const balooBhaijaanLatinWidthFactor = (weight) => (weight >= 800 ? 1.058 : weight >= 700 ? 1.044 : 1);
// An Arabic face's estimate table for `weight`: the Regular table with every plain-number entry
// (Latin, digits, punctuation, marks) scaled by latinFactor / arabicFactor, so that after the
// face's Arabic width factor is applied to the whole line the Latin words come out at their own
// measured growth. Regular (ratio 1) is the table itself; the others are built once and cached.
const weightedArabicTables = new Map();
const weightedArabicTable = (table, ratio) => {
  if (Math.abs(ratio - 1) < 1e-6) return table;
  const key = `${ratio}`;
  let byRatio = weightedArabicTables.get(table);
  if (!byRatio) { byRatio = new Map(); weightedArabicTables.set(table, byRatio); }
  if (!byRatio.has(key)) {
    const out = {};
    for (const k of Object.keys(table)) out[k] = Array.isArray(table[k]) ? table[k] : Math.round(table[k] * ratio * 1000) / 1000;
    byRatio.set(key, out);
  }
  return byRatio.get(key);
};
const BALOO_BHAIJAAN_WIN_CENTRE_EM = 0.224;
const BALOO_BHAIJAAN_TYPO_CENTRE_EM = 0.224;
// Ink extents of an Urdu word in em, from canvas measureText on Noto Sans Arabic Black and Baloo
// Bhaijaan 2 ExtraBold with a tatweel on each joining side. Every letter form falls into a top
// bucket (X: the tallest letters with a mark drawn on them, ڱ ڷ أ; T: dotted or marked tall
// letters, ش غ ڈ گ آ; H: the ascenders ا ل ک ط and the dotted teeth in their initial form; M:
// ت ن ع ه; S: the flat letters ب س م و ی) and a bottom bucket (X: ې ۑ ڸ; D: the bowls of ج ح ع
// م and the dotted پ; M: the tails of ر و ی ے ن ل, the dot of a joined ب or ی; F: on the
// baseline), coded per letter as four top letters then four bottom letters in form order
// (isolated, initial, medial, final); a letter missing from the codes is flat and short. Each
// bucket's value is the extreme measured inside it, so the pill always covers the ink (the flat
// bucket keeps the baseline: only the Pashto ټ ډ dip to -0.16 and they are not worth every flat
// word's box).
const NOTO_ARAB_INK = { top: { X: 1.16, T: 0.95, H: 0.8, M: 0.69, S: 0.54 }, bottom: { X: -0.64, D: -0.48, M: -0.33, F: -0.02 }, markTop: 0.85, stackTop: 1.2, quranTop: 1.5, belowBottom: -0.35 };
const BALOO_BHAIJAAN_INK = { top: { X: 1.03, T: 0.95, H: 0.79, M: 0.69, S: 0.55 }, bottom: { X: -0.65, D: -0.5, M: -0.35, F: -0.02 }, markTop: 0.72, stackTop: 1.26, quranTop: 0.75, belowBottom: -0.35 };
// Harakat and other marks above the letters (shadda, fatha, damma, sukun, tanween, hamza, madda,
// the superscript alef), the shadda + superscript alef stack of اللّٰہ, the Quranic annotation
// signs, and the marks below (kasra, kasratan, hamza below).
const ARAB_ABOVE_MARKS = /[\u064B\u064C\u064E\u064F\u0651\u0652\u0653\u0654\u0657\u0658\u0670\u0610-\u0614\u06DF-\u06E2\u06E4\u06E7\u06EB\u06EC]/;
const ARAB_STACK_MARKS = /\u0651\u0670|\u0670\u0651/;
const ARAB_QURAN_MARKS = /[\u0615\u06D6-\u06DC\u06E8]/;
const ARAB_BELOW_MARKS = /[\u064D\u0650\u0655\u0656\u06E3\u06EA\u06ED]/;
// Letters that join only to the letter before them (Unicode joining type R): the next letter
// starts a new stroke. Everything else with a four-form entry joins on both sides (type D); ء ٴ
// (tabled, but Unicode type U) and anything outside the tables join nothing; the tatweel and ZWJ
// join both sides.
const ARAB_RIGHT_JOINING = new Set(Array.from('آأؤإاةدذرزوٱٲٳٵٶٷڈډڊڋڌڍڎڏڐڑڒړڔڕږڗژڙۀۃۄۅۆۇۈۉۊۋۍۏےۓەۮۯ'));
const isArabTransparent = (cp) => (cp >= 0x0610 && cp <= 0x061a) || (cp >= 0x064b && cp <= 0x065f) || cp === 0x0670
  || (cp >= 0x06d6 && cp <= 0x06dc) || (cp >= 0x06df && cp <= 0x06e4) || cp === 0x06e7 || cp === 0x06e8 || (cp >= 0x06ea && cp <= 0x06ed);
const arabJoinClass = (ch, table) => {
  const cp = ch.codePointAt(0);
  if (cp === 0x0640 || cp === 0x200d) return 'C';
  if (cp === 0x0621 || cp === 0x0674 || !Array.isArray(table[ch])) return 'U';
  return ARAB_RIGHT_JOINING.has(ch) ? 'R' : 'D';
};
// Index of the nearest non-mark character before (step -1) or after (step 1) chars[i], or -1.
const arabNeighbour = (chars, i, step) => {
  let k = i + step;
  while (k >= 0 && k < chars.length && isArabTransparent(chars[k].codePointAt(0))) k += step;
  return k >= 0 && k < chars.length ? k : -1;
};
/**
 * The joining form the letter at chars[i] takes between its neighbours, as an index into the
 * face's four-advance entry: 0 isolated, 1 initial, 2 medial, 3 final. A right-joining letter's
 * initial and medial entries equal its isolated and final ones, so the rule needs no special case
 * for the letter itself, only for the letter before it.
 */
const arabicFormIndex = (chars, i, table) => {
  const p = arabNeighbour(chars, i, -1);
  const n = arabNeighbour(chars, i, 1);
  const prevJoins = p >= 0 && /[DC]/.test(arabJoinClass(chars[p], table));
  const nextJoins = n >= 0 && /[DRC]/.test(arabJoinClass(chars[n], table));
  return prevJoins ? (nextJoins ? 2 : 3) : (nextJoins ? 1 : 0);
};
// The lam-alef ligature starting at chars[i] (a lam): { width: [isolated, final], at: the alef's
// index } or null when no alef follows.
const arabicLamAlef = (chars, i, table) => {
  const n = arabNeighbour(chars, i, 1);
  if (n < 0) return null;
  const w = table['\u0644' + chars[n]];
  return Array.isArray(w) && w.length === 2 ? { width: w, at: n } : null;
};
/**
 * Ink top/bottom of `word` in em above the baseline for the Arabic faces, or null when the word
 * has no Arabic (a Latin word in an Urdu caption keeps the face's default glyph box). `display`
 * picks the Baloo Bhaijaan 2 codes and buckets.
 */
const arabicInkExtents = (word, display = false) => {
  const t = String(word || '');
  if (!/[ؠ-يٮ-ۓەۮۯۺ-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/.test(t)) return null;
  const table = display ? BALOO_BHAIJAAN_CHAR_WIDTHS : NOTO_ARAB_CHAR_WIDTHS;
  const codes = display ? BALOO_BHAIJAAN_INK_CODES : NOTO_ARAB_INK_CODES;
  const k = display ? BALOO_BHAIJAAN_INK : NOTO_ARAB_INK;
  const chars = Array.from(t);
  let top = k.top.S;
  let bottom = k.bottom.F;
  for (let i = 0; i < chars.length; i++) {
    if (!Array.isArray(table[chars[i]])) continue;
    const code = codes[chars[i]] || 'SSSSFFFF';
    const f = arabicFormIndex(chars, i, table);
    top = Math.max(top, k.top[code[f]]);
    bottom = Math.min(bottom, k.bottom[code[4 + f]]);
  }
  if (ARAB_ABOVE_MARKS.test(t)) top = Math.max(top, k.markTop);
  if (ARAB_STACK_MARKS.test(t)) top = Math.max(top, k.stackTop);
  if (ARAB_QURAN_MARKS.test(t)) top = Math.max(top, k.quranTop);
  if (ARAB_BELOW_MARKS.test(t)) bottom = Math.min(bottom, k.belowBottom);
  return { top, bottom };
};

// ── Indic script registry ──
// One entry per script we caption in: how to detect it, the Noto face it routes to (and its
// weight faces), the Baloo sibling the comic templates use instead, and the measured numbers
// each face needs (advance table, libass render ratio, size compensation, line-box centres,
// weight width factor) plus the script's glyph box and ink-extent rule for boxes and pills.
// resolveFontFace, captionScript, the preview's font stack and the burn all read this table,
// so a new script is one entry here plus its romaniser and prompts.
const INDIC_SCRIPTS = {
  devanagari: {
    label: 'Devanagari', block: [0x0900, 0x097f], range: /[\u0900-\u097F]/g, supportsFlag: 'supportsDevanagari',
    // normalLineHeight: the face's CSS `line-height: normal` (hhea), see NORMAL_LINE_HEIGHT.
    glyphBox: 0.50, inkExtents: devanagariInkExtents,
    noto: { key: 'noto sans devanagari', family: 'Noto Sans Devanagari', faces: { 800: 'Noto Sans Devanagari ExtraBold', 900: 'Noto Sans Devanagari Black' },
      charTable: NOTO_DEVA_CHAR_WIDTHS, renderRatio: NOTO_DEVA_RENDER_RATIO, sizeCompensation: NOTO_DEVA_SIZE_COMPENSATION, widthFactor: notoDevaWidthFactor,
      winCentre: NOTO_DEVA_WIN_CENTRE_EM, typoCentre: NOTO_DEVA_TYPO_CENTRE_EM, normalLineHeight: 1.304 },
    display: { key: 'baloo 2', family: 'Baloo 2', faces: { 800: 'Baloo 2 ExtraBold' },
      charTable: NOTO_DEVA_CHAR_WIDTHS, renderRatio: BALOO_RENDER_RATIO, sizeCompensation: BALOO_SIZE_COMPENSATION, widthFactor: notoDevaWidthFactor,
      winCentre: BALOO_WIN_CENTRE_EM, typoCentre: BALOO_TYPO_CENTRE_EM, inkScale: 1, normalLineHeight: 1.602 },
  },
  bengali: {
    label: 'Bengali', block: [0x0980, 0x09ff], range: /[\u0980-\u09FF]/g, supportsFlag: 'supportsBengali',
    glyphBox: 0.55, inkExtents: bengaliInkExtents,
    noto: { key: 'noto sans bengali', family: 'Noto Sans Bengali', faces: { 800: 'Noto Sans Bengali ExtraBold', 900: 'Noto Sans Bengali Black' },
      charTable: NOTO_BENG_CHAR_WIDTHS, renderRatio: NOTO_BENG_RENDER_RATIO, sizeCompensation: NOTO_BENG_SIZE_COMPENSATION, widthFactor: notoBengWidthFactor,
      winCentre: NOTO_BENG_WIN_CENTRE_EM, typoCentre: NOTO_BENG_TYPO_CENTRE_EM, normalLineHeight: 1.325 },
    display: { key: 'baloo da 2', family: 'Baloo Da 2', faces: { 800: 'Baloo Da 2 ExtraBold' },
      charTable: NOTO_BENG_CHAR_WIDTHS, renderRatio: BALOO_DA_RENDER_RATIO, sizeCompensation: BALOO_DA_SIZE_COMPENSATION, widthFactor: notoBengWidthFactor,
      winCentre: BALOO_DA_WIN_CENTRE_EM, typoCentre: BALOO_DA_TYPO_CENTRE_EM, inkScale: 1, normalLineHeight: 1.684 },
  },
  telugu: {
    label: 'Telugu', block: [0x0c00, 0x0c7f], range: /[\u0C00-\u0C7F]/g, supportsFlag: 'supportsTelugu',
    glyphBox: 0.62, inkExtents: teluguInkExtents,
    noto: { key: 'noto sans telugu', family: 'Noto Sans Telugu', faces: { 800: 'Noto Sans Telugu ExtraBold', 900: 'Noto Sans Telugu Black' },
      charTable: NOTO_TEL_CHAR_WIDTHS, renderRatio: NOTO_TEL_RENDER_RATIO, sizeCompensation: NOTO_TEL_SIZE_COMPENSATION, widthFactor: notoTelWidthFactor,
      winCentre: NOTO_TEL_WIN_CENTRE_EM, typoCentre: NOTO_TEL_TYPO_CENTRE_EM, normalLineHeight: 1.352 },
    display: { key: 'baloo tammudu 2', family: 'Baloo Tammudu 2', faces: { 800: 'Baloo Tammudu 2 ExtraBold' },
      charTable: NOTO_TEL_CHAR_WIDTHS, renderRatio: BALOO_TAMMUDU_RENDER_RATIO, sizeCompensation: BALOO_TAMMUDU_SIZE_COMPENSATION, widthFactor: balooTammuduWidthFactor,
      winCentre: BALOO_TAMMUDU_WIN_CENTRE_EM, typoCentre: BALOO_TAMMUDU_TYPO_CENTRE_EM, inkScale: BALOO_TAMMUDU_INK_SCALE, normalLineHeight: 2.296 },
  },
  tamil: {
    label: 'Tamil', block: [0x0b80, 0x0bff], range: /[\u0B80-\u0BFF]/g, supportsFlag: 'supportsTamil',
    // Tamil ink is tall for its short win box (0.85 em marks over 0.8065 em per Fontsize) and
    // deep letters are common, so the box gets 0.70 (the preview's pillFaceMetrics mirrors it).
    glyphBox: 0.70, inkExtents: tamilInkExtents,
    noto: { key: 'noto sans tamil', family: 'Noto Sans Tamil', faces: { 800: 'Noto Sans Tamil ExtraBold', 900: 'Noto Sans Tamil Black' },
      charTable: NOTO_TAM_CHAR_WIDTHS, renderRatio: NOTO_TAM_RENDER_RATIO, sizeCompensation: NOTO_TAM_SIZE_COMPENSATION, widthFactor: notoTamWidthFactor,
      winCentre: NOTO_TAM_WIN_CENTRE_EM, typoCentre: NOTO_TAM_TYPO_CENTRE_EM, normalLineHeight: 1.24 },
    display: { key: 'baloo thambi 2', family: 'Baloo Thambi 2', faces: { 800: 'Baloo Thambi 2 ExtraBold' },
      charTable: NOTO_TAM_CHAR_WIDTHS, renderRatio: BALOO_THAMBI_RENDER_RATIO, sizeCompensation: BALOO_THAMBI_SIZE_COMPENSATION, widthFactor: balooThambiWidthFactor,
      winCentre: BALOO_THAMBI_WIN_CENTRE_EM, typoCentre: BALOO_THAMBI_TYPO_CENTRE_EM, inkScale: 1, normalLineHeight: 1.564 },
  },
  gujarati: {
    label: 'Gujarati', block: [0x0a80, 0x0aff], range: /[\u0A80-\u0AFF]/g, supportsFlag: 'supportsGujarati',
    // 0.90 em marks over a 0.6739 em-per-Fontsize box land at ~0.61 (Devanagari 0.97 × 0.5247 ≈ 0.50).
    glyphBox: 0.61, inkExtents: gujaratiInkExtents,
    noto: { key: 'noto sans gujarati', family: 'Noto Sans Gujarati', faces: { 800: 'Noto Sans Gujarati ExtraBold', 900: 'Noto Sans Gujarati Black' },
      charTable: NOTO_GUJ_CHAR_WIDTHS, renderRatio: NOTO_GUJ_RENDER_RATIO, sizeCompensation: NOTO_GUJ_SIZE_COMPENSATION, widthFactor: notoGujWidthFactor,
      winCentre: NOTO_GUJ_WIN_CENTRE_EM, typoCentre: NOTO_GUJ_TYPO_CENTRE_EM, normalLineHeight: 1.304 },
    display: { key: 'baloo bhai 2', family: 'Baloo Bhai 2', faces: { 800: 'Baloo Bhai 2 ExtraBold' },
      charTable: NOTO_GUJ_CHAR_WIDTHS, renderRatio: BALOO_BHAI_RENDER_RATIO, sizeCompensation: BALOO_BHAI_SIZE_COMPENSATION, widthFactor: balooBhaiWidthFactor,
      winCentre: BALOO_BHAI_WIN_CENTRE_EM, typoCentre: BALOO_BHAI_TYPO_CENTRE_EM, inkScale: 1, normalLineHeight: 1.622 },
  },
  arabic: {
    // Urdu. rtl: the caption runs right to left (layoutCaption mirrors the words on each line,
    // the burn sets the ASS base direction). The block is the Arabic block proper; the range also
    // catches the supplement and presentation forms a transcript could carry.
    label: 'Arabic', block: [0x0600, 0x06ff], range: /[ؠ-يٮ-ۓەۮۯۺ-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/g, supportsFlag: 'supportsArabic', rtl: true,
    // A typical word spans 0.80 em up and 0.48 em down, 1.28 em over a 0.4610 em-per-Fontsize box.
    glyphBox: 0.55, inkExtents: arabicInkExtents,
    noto: { key: 'noto sans arabic', family: 'Noto Sans Arabic', faces: { 800: 'Noto Sans Arabic ExtraBold', 900: 'Noto Sans Arabic Black' },
      charTable: NOTO_ARAB_CHAR_WIDTHS, renderRatio: NOTO_ARAB_RENDER_RATIO, sizeCompensation: NOTO_ARAB_SIZE_COMPENSATION, widthFactor: notoArabWidthFactor,
      weightTable: (w) => weightedArabicTable(NOTO_ARAB_CHAR_WIDTHS, notoArabLatinWidthFactor(w) / notoArabWidthFactor(w)),
      winCentre: NOTO_ARAB_WIN_CENTRE_EM, typoCentre: NOTO_ARAB_TYPO_CENTRE_EM, normalLineHeight: 2.112 },
    display: { key: 'baloo bhaijaan 2', family: 'Baloo Bhaijaan 2', faces: { 800: 'Baloo Bhaijaan 2 ExtraBold' },
      charTable: BALOO_BHAIJAAN_CHAR_WIDTHS, renderRatio: BALOO_BHAIJAAN_RENDER_RATIO, sizeCompensation: BALOO_BHAIJAAN_SIZE_COMPENSATION, widthFactor: balooBhaijaanWidthFactor,
      weightTable: (w) => weightedArabicTable(BALOO_BHAIJAAN_CHAR_WIDTHS, balooBhaijaanLatinWidthFactor(w) / balooBhaijaanWidthFactor(w)),
      winCentre: BALOO_BHAIJAAN_WIN_CENTRE_EM, typoCentre: BALOO_BHAIJAAN_TYPO_CENTRE_EM, inkScale: 1, normalLineHeight: 1.712 },
  },
};
const INDIC_SCRIPT_IDS = Object.keys(INDIC_SCRIPTS);

// Devanagari shaping compresses conjuncts: a consonant before virama+consonant renders as a
// half form ≈ 0.56× its full advance; a leading र् becomes a zero-width reph mark above the
// next consonant; a trailing ्र (rakar) attaches below the previous consonant (which keeps its
// full form). Summing raw advances without this overestimates conjunct-heavy words by 10–20%,
// leaving visible extra side padding inside drawn boxes.
const DEVA_VIRAMA = 0x094d;
const DEVA_RA = 0x0930;
const DEVA_HALF_FORM = 0.56;
const isDevaConsonant = (cp) => (cp >= 0x0915 && cp <= 0x0939) || (cp >= 0x0958 && cp <= 0x095f);
// Bengali shaping: a consonant before virama+consonant usually forms a stacked or fused conjunct
// whose advance is about ONE consonant's (measured median 0.83 em vs 0.82 for ক in Noto Sans
// Bengali Black), so the first consonant nearly vanishes from the width; a leading র্ becomes a
// zero-width reph above; a trailing ্র (ra-phala) or ্য (ya-phala) attaches to the previous
// consonant, which keeps its full form, and adds only a little width itself.
const BENG_VIRAMA = 0x09cd;
const BENG_RA = 0x09b0;
const BENG_YA = 0x09af;
const BENG_STACK_FORM = 0.15;
const BENG_PHALA_WIDTH = 0.2;
const isBengConsonant = (cp) => (cp >= 0x0995 && cp <= 0x09b9) || (cp >= 0x09dc && cp <= 0x09df) || cp === 0x09ce;
// Telugu shaping is the reverse of Bengali: the FIRST consonant of a conjunct keeps its full
// form and advance, and the consonant AFTER the virama becomes a subscript (vottu) drawn below
// it with zero advance (every *subscripttelu glyph in Noto Sans Telugu advances 0). The only
// spacing subscript is the ya-vattu (్య), a post-base hook ≈ 0.5 em. A virama followed by ZWNJ
// (loanwords like సబ్‌స్క్రైబ్) is a pollu, so the next consonant keeps its full width.
const TEL_VIRAMA = 0x0c4d;
const TEL_YA = 0x0c2f;
const TEL_POSTBASE_WIDTH = 0.5;
const isTelConsonant = (cp) => (cp >= 0x0c15 && cp <= 0x0c39) || (cp >= 0x0c58 && cp <= 0x0c5a);

/**
 * Estimate the on-screen (libass-rendered) pixel width of a text string.
 * Per-character ratios are the real Montserrat-Bold advance widths (advance / unitsPerEm) read
 * from Montserrat-Bold.ttf; `scale` converts that raw advance to libass's actual rendered width
 * (libass shrinks glyphs vs the nominal Fontsize — see FONT_WIDTH_SCALE).
 * @param {string} text - Plain text (no ASS tags)
 * @param {number} fontSize - Font size in pixels
 * @param {boolean} isBold - Whether the font is bold
 * @param {number} scale - libass render scale for the font (see fontWidthScale)
 * @returns {number} Estimated width in pixels
 */
const estimateTextWidth = (text, fontSize, isBold = false, scale = 0.63, charTable = null) => {
  if (!text) return 0;
  // Exact per-font table (already weight-specific): sum real advances × libass render ratio.
  if (charTable) {
    const chars = Array.from(text);
    let total = 0;
    for (let i = 0; i < chars.length; i++) {
      const cp = chars[i].codePointAt(0);
      // Format characters (ZWNJ/ZWJ in Telugu and Bengali loanwords, ZWSP, soft hyphen, BOM) are
      // invisible and must not fall through to the 0.6 em default for unmapped glyphs.
      if (cp === 0x200c || cp === 0x200d || cp === 0x200b || cp === 0x00ad || cp === 0xfeff) continue;
      // ?? not ||: zero-advance combining marks (Devanagari matras/virama) are legitimate 0s.
      let a = charTable[chars[i]] ?? 0.6;
      if (Array.isArray(a)) {
        // An Arabic letter: its advance is the joining form the neighbours give it; a lam before
        // an alef fuses into the lam-alef ligature (isolated, or final after a joining letter).
        const form = arabicFormIndex(chars, i, charTable);
        const lig = cp === 0x0644 ? arabicLamAlef(chars, i, charTable) : null;
        if (lig) { a = lig.width[form >= 2 ? 1 : 0]; i = lig.at; } else a = a[form];
      } else if (isDevaConsonant(cp)) {
        const n1 = i + 1 < chars.length ? chars[i + 1].codePointAt(0) : 0;
        const n2 = i + 2 < chars.length ? chars[i + 2].codePointAt(0) : 0;
        const p1 = i >= 1 ? chars[i - 1].codePointAt(0) : 0;
        const p2 = i >= 2 ? chars[i - 2].codePointAt(0) : 0;
        if (n1 === DEVA_VIRAMA && isDevaConsonant(n2)) {
          // Half form before a conjunct partner; र् instead becomes a zero-width reph, and a
          // consonant before ्र (rakar) keeps its full form.
          a = cp === DEVA_RA ? 0 : (n2 === DEVA_RA ? a : a * DEVA_HALF_FORM);
        } else if (cp === DEVA_RA && p1 === DEVA_VIRAMA && isDevaConsonant(p2)) {
          a = 0; // rakar: renders as a stroke attached below the previous consonant
        }
      } else if (isBengConsonant(cp)) {
        const n1 = i + 1 < chars.length ? chars[i + 1].codePointAt(0) : 0;
        const n2 = i + 2 < chars.length ? chars[i + 2].codePointAt(0) : 0;
        const p1 = i >= 1 ? chars[i - 1].codePointAt(0) : 0;
        const p2 = i >= 2 ? chars[i - 2].codePointAt(0) : 0;
        if (n1 === BENG_VIRAMA && isBengConsonant(n2)) {
          // Reph, or the base of a ra/ya-phala (full form), or the top of a stacked conjunct.
          a = cp === BENG_RA ? 0 : ((n2 === BENG_RA || n2 === BENG_YA) ? a : a * BENG_STACK_FORM);
        } else if ((cp === BENG_RA || cp === BENG_YA) && p1 === BENG_VIRAMA && isBengConsonant(p2)) {
          a = BENG_PHALA_WIDTH; // ra-phala / ya-phala attached to the previous consonant
        }
      } else if (isTelConsonant(cp)) {
        const p1 = i >= 1 ? chars[i - 1].codePointAt(0) : 0;
        const p2 = i >= 2 ? chars[i - 2].codePointAt(0) : 0;
        if (p1 === TEL_VIRAMA && isTelConsonant(p2)) {
          a = cp === TEL_YA ? TEL_POSTBASE_WIDTH : 0; // vottu below the previous consonant
        }
      } else if (isTamConsonant(cp)) {
        // Tamil u/uu ligatures: the consonant takes the measured fused width, the sign adds nothing.
        const n1 = i + 1 < chars.length ? chars[i + 1].codePointAt(0) : 0;
        if (n1 === TAM_U_SIGN && TAM_U_LIGATURE_WIDTHS[chars[i]] !== undefined) { a = TAM_U_LIGATURE_WIDTHS[chars[i]]; i += 1; }
        else if (n1 === TAM_UU_SIGN && TAM_UU_LIGATURE_WIDTHS[chars[i]] !== undefined) { a = TAM_UU_LIGATURE_WIDTHS[chars[i]]; i += 1; }
      } else if (isGujConsonant(cp)) {
        // Gujarati conjuncts shape like Devanagari's: a per-consonant half form before virama +
        // consonant (or a fused ligature for the pairs that draw as one glyph), a zero-width reph
        // for a leading ર્, a full consonant before ્ર with the rakar itself adding nothing; રુ fuses.
        const n1 = i + 1 < chars.length ? chars[i + 1].codePointAt(0) : 0;
        const n2 = i + 2 < chars.length ? chars[i + 2].codePointAt(0) : 0;
        const p1 = i >= 1 ? chars[i - 1].codePointAt(0) : 0;
        const p2 = i >= 2 ? chars[i - 2].codePointAt(0) : 0;
        if (n1 === GUJ_VIRAMA && isGujConsonant(n2)) {
          const lig3 = GUJ_LIGATURE_WIDTHS[chars[i] + chars[i + 1] + chars[i + 2]];
          const lig5 = i + 4 < chars.length ? GUJ_LIGATURE_WIDTHS[chars.slice(i, i + 5).join('')] : undefined;
          if (lig5 !== undefined) { a = lig5; i += 4; }
          else if (lig3 !== undefined) { a = lig3; i += 2; }
          else if (cp === GUJ_RA) a = 0;
          else if (n2 !== GUJ_RA) a = GUJ_HALF_WIDTHS[chars[i]] ?? a * DEVA_HALF_FORM;
        } else if (cp === GUJ_RA && p1 === GUJ_VIRAMA && isGujConsonant(p2)) {
          a = 0; // rakar
        } else if (cp === GUJ_RA && n1 === GUJ_U_SIGN) {
          a = GUJ_RU_WIDTH;
        }
      }
      total += a * fontSize;
    }
    return Math.round(total * scale);
  }
  // Real Montserrat-Bold advance ratios (fraction of em). Regular is ~5% narrower (boldFactor).
  const charWidths = {
    'a': 0.62, 'b': 0.69, 'c': 0.59, 'd': 0.69, 'e': 0.63, 'f': 0.39, 'g': 0.70, 'h': 0.69,
    'i': 0.30, 'j': 0.31, 'k': 0.66, 'l': 0.30, 'm': 1.05, 'n': 0.69, 'o': 0.66, 'p': 0.69,
    'q': 0.69, 'r': 0.43, 's': 0.53, 't': 0.44, 'u': 0.69, 'v': 0.60, 'w': 0.94, 'x': 0.60,
    'y': 0.60, 'z': 0.54,
    'A': 0.77, 'B': 0.77, 'C': 0.72, 'D': 0.83, 'E': 0.67, 'F': 0.64, 'G': 0.77, 'H': 0.81,
    'I': 0.33, 'J': 0.54, 'K': 0.74, 'L': 0.60, 'M': 0.96, 'N': 0.81, 'O': 0.84, 'P': 0.73,
    'Q': 0.84, 'R': 0.74, 'S': 0.64, 'T': 0.62, 'U': 0.79, 'V': 0.75, 'W': 1.16, 'X': 0.71,
    'Y': 0.68, 'Z': 0.67,
    '0': 0.68, '1': 0.39, '2': 0.59, '3': 0.59, '4': 0.69, '5': 0.60, '6': 0.64, '7': 0.62,
    '8': 0.66, '9': 0.64,
    ' ': 0.28, '.': 0.26, ',': 0.26, '!': 0.29, '?': 0.59, ':': 0.26, ';': 0.26, "'": 0.23,
    '"': 0.44, '`': 0.60, '-': 0.39, '_': 0.50, '(': 0.36, ')': 0.36, '/': 0.39, '\\': 0.39,
    '@': 1.03, '#': 0.72, '%': 0.88, '&': 0.73, '+': 0.60, '=': 0.60, '|': 0.31,
  };
  const defaultWidth = 0.66; // ≈ avg Montserrat-Bold advance, for unmapped glyphs
  const boldFactor = isBold ? 1.0 : 0.95; // table is bold metrics; regular is ~5% narrower

  let totalWidth = 0;
  for (const ch of text) {
    const ratio = charWidths[ch] || defaultWidth;
    totalWidth += ratio * fontSize;
  }
  return Math.round(totalWidth * boldFactor * scale);
};


/**
 * Legacy template ids used to be special-cased by name in both renderers. Map them to the
 * declarative flags they stood for, so templates are pure data and new ones never need code.
 *   bubble-style   → activeWordPill (a dark pill/outline behind the spoken word)
 *   double-trouble → unspoken words always take secondaryColor (default #AAAAAA)
 * Idempotent; safe to call on already-normalized styles.
 */
const normalizeCaptionStyle = (style) => {
  if (!style || typeof style !== 'object') return style || {};
  const out = { ...style };
  if (out.id === 'bubble-style' && out.activeWordPill === undefined) {
    out.activeWordPill = true;
  }
  if (out.id === 'double-trouble' && !out.secondaryColor) {
    out.secondaryColor = '#AAAAAA';
  }
  // A picker action token is never a font. Fall back to the default family instead of asking the
  // browser or libass for a font called "__add_font__".
  if (typeof out.fontFamily === 'string' && out.fontFamily.startsWith('__')) out.fontFamily = 'Montserrat';
  // Word tiles (Chalk) draw every word on its own tile, which a behind hero (drawn big behind the
  // block, on no line) would have no tile for. The tiles win: the hero sits on its line.
  if (out.wordTiles && out.heroBehind) out.heroBehind = false;
  return out;
};

const transcriptText = (transcript) => (Array.isArray(transcript) ? transcript : [])
  .map(s => (s && (s.text || (s.words || []).map(w => w.word).join(' '))) || '')
  .join(' ');
/** True when any caption text contains Devanagari (drives the font-face routing below). */
const hasDevanagariText = (transcript) => /[ऀ-ॿ]/.test(transcriptText(transcript));
/** True when any caption text contains Bengali script. */
const hasBengaliText = (transcript) => /[\u0980-\u09FF]/.test(transcriptText(transcript));
/** True when any caption text contains Telugu script. */
const hasTeluguText = (transcript) => /[\u0C00-\u0C7F]/.test(transcriptText(transcript));
/** True when any caption text contains Tamil script. */
const hasTamilText = (transcript) => /[\u0B80-\u0BFF]/.test(transcriptText(transcript));
/** True when any caption text contains Gujarati script. */
const hasGujaratiText = (transcript) => /[\u0A80-\u0AFF]/.test(transcriptText(transcript));
/** True when any caption text contains Arabic script (Urdu). */
const hasArabicText = (transcript) => /[ؠ-يٮ-ۓەۮۯۺ-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/.test(transcriptText(transcript));
/**
 * The Indic script a transcript's captions render in (an INDIC_SCRIPTS id) or null. With more
 * than one present (a stray Hindi word in a Tamil video) the script with the most letters wins,
 * so the whole caption keeps one family; libass falls back per glyph for the rest.
 */
const captionScript = (transcript) => {
  const text = transcriptText(transcript);
  let best = null;
  let bestCount = 0;
  for (const id of INDIC_SCRIPT_IDS) {
    const count = (text.match(INDIC_SCRIPTS[id].range) || []).length;
    if (count > bestCount) { best = id; bestCount = count; }
  }
  return best;
};
/**
 * Normalise the script argument the routing functions take: a script id, or the legacy boolean
 * `hasDevanagari` (true = Devanagari, false = none).
 */
const indicScript = (v) => (v === true ? 'devanagari' : (typeof v === 'string' && INDIC_SCRIPTS[v]) ? v : null);
/** True when captions in this script run right to left (the Arabic script: Urdu). */
const isRtlScript = (v) => { const id = indicScript(v); return !!(id && INDIC_SCRIPTS[id].rtl); };

// ── Right-to-left word order ──
// A right-to-left caption's words are drawn right to left, but a run of Latin words inside it
// keeps its own left-to-right order, a number read after a Latin word belongs to that run
// (Unicode bidi W7), a punctuation-only word between two Latin words joins them (N1), and a
// number after an Urdu word stands as an island of its own: the Unicode bidi algorithm at word
// level. libass and the browser apply that algorithm to a whole line of text; the per-word boxes
// both renderers draw (the pill, the word tiles, a stagger line's spans) are positioned from this
// order instead, so they land on the words.
const LATIN_LETTER_RE = /[A-Za-z\u00C0-\u024F]/;
// Arabic-script LETTERS only: the block's digits and punctuation (،؟۔) are not strong characters
// and must not make a Roman Urdu caption count as Urdu script.
const ARABIC_LETTER_RE = /[ؠ-يٮ-ۓەۮۯۺ-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
// 'R' for an Arabic-script word, 'L' for a Latin one, 'N' for a number (ASCII or the extended
// Arabic-Indic digits Urdu uses, class EN), 'A' for the Arabic-Indic digits ٠-٩ (class AN, always
// an island), 'O' for a word of neutrals only (punctuation, symbols).
const wordBidiClass = (text) => {
  const t = String(text || '');
  if (ARABIC_LETTER_RE.test(t)) return 'R';
  if (LATIN_LETTER_RE.test(t)) return 'L';
  if (/[\u0660-\u0669]/.test(t)) return 'A';
  return /[0-9\u06F0-\u06F9]/.test(t) ? 'N' : 'O';
};
/**
 * The word indices of one right-to-left line in the order they appear left to right. `indices`
 * are the line's word indices in reading (logical) order, `texts` the display text per index.
 */
const visualLineOrder = (indices, texts) => {
  const cls = indices.map((idx) => wordBidiClass(texts[idx]));
  // W7: a number whose nearest preceding strong word is Latin reads with it (the paragraph
  // itself is right-to-left, so a number with nothing strong before it stays an island).
  let lastStrong = 'R';
  cls.forEach((c, k) => {
    if (c === 'N' && lastStrong === 'L') cls[k] = 'L';
    else if (c === 'L' || c === 'R') lastStrong = c;
  });
  // N1/N2: a neutral word between two Latin words reads with them; otherwise it follows the
  // paragraph (numbers count as right-to-left here, unless W7 already made them Latin).
  const strongAt = (k, step) => {
    for (let j = k + step; j >= 0 && j < cls.length; j += step) if (cls[j] !== 'O') return cls[j] === 'L' ? 'L' : 'R';
    return 'R';
  };
  cls.forEach((c, k) => { if (c === 'O') cls[k] = strongAt(k, -1) === 'L' && strongAt(k, 1) === 'L' ? 'L' : 'R'; });
  const out = [];
  let run = [];
  const flush = () => { if (run.length) { out.push(...run); run = []; } };
  // Walk the reading order backwards: the last word read is the leftmost. A Latin run keeps its
  // reading order, so it is collected and emitted whole where it ends.
  for (let k = indices.length - 1; k >= 0; k--) {
    if (cls[k] === 'L') run.unshift(indices[k]);
    else { flush(); out.push(indices[k]); }
  }
  flush();
  return out;
};

// libass renders glyphs noticeably smaller than the browser does for the same nominal size:
// a libass "Fontsize" produces a cap height of ~0.44× the value, while CSS `font-size` yields
// ~0.70×. So a caption that looks right in the editor preview burned ~0.63× too small. Scale the
// burn font size up by this ratio so the exported text matches the preview (WYSIWYG). Everything
// downstream (box width/height estimates, GLYPH_H) keys off the resulting size, so it scales together.
const LIBASS_FONT_COMPENSATION = 1.55;

// ── Non-RIBBI font-weight resolution ──
// The bundled base families are RIBBI: they only carry Regular (400) and Bold (700), so ASS
// can only express those via the Bold flag. Any other weight (Medium 500, ExtraBold 800,
// Black 900) is routed to a dedicated bundled face whose FAMILY NAME carries the weight
// (Montserrat-Medium.ttf → "Montserrat Medium", Poppins-Black.ttf → "Poppins Black", …).
// When such a face is used we turn the Bold flag OFF so libass doesn't faux-embolden an
// already-correct glyph. Keep in sync with CAPTION_FONTS (frontend/src/utils/constants.js)
// and STATIC_FACES (backend/scripts/downloadFonts.js).
const WEIGHT_FACES = {
  montserrat: { 500: 'Montserrat Medium', 800: 'Montserrat ExtraBold', 900: 'Montserrat Black' },
  poppins: { 500: 'Poppins Medium', 900: 'Poppins Black' },
  lato: { 900: 'Lato Black' },
  inter: { 600: 'Inter SemiBold', 800: 'Inter ExtraBold', 900: 'Inter Black' },
  'noto sans devanagari': { 800: 'Noto Sans Devanagari ExtraBold', 900: 'Noto Sans Devanagari Black' },
  'baloo 2': { 800: 'Baloo 2 ExtraBold' },
  'noto sans bengali': { 800: 'Noto Sans Bengali ExtraBold', 900: 'Noto Sans Bengali Black' },
  'baloo da 2': { 800: 'Baloo Da 2 ExtraBold' },
  'noto sans telugu': { 800: 'Noto Sans Telugu ExtraBold', 900: 'Noto Sans Telugu Black' },
  'baloo tammudu 2': { 800: 'Baloo Tammudu 2 ExtraBold' },
  'noto sans tamil': { 800: 'Noto Sans Tamil ExtraBold', 900: 'Noto Sans Tamil Black' },
  'baloo thambi 2': { 800: 'Baloo Thambi 2 ExtraBold' },
  'noto sans gujarati': { 800: 'Noto Sans Gujarati ExtraBold', 900: 'Noto Sans Gujarati Black' },
  'baloo bhai 2': { 800: 'Baloo Bhai 2 ExtraBold' },
  'noto sans arabic': { 800: 'Noto Sans Arabic ExtraBold', 900: 'Noto Sans Arabic Black' },
  'baloo bhaijaan 2': { 800: 'Baloo Bhaijaan 2 ExtraBold' },
};

/**
 * Resolve the face a caption actually renders in, plus the width-estimation parameters for it.
 * @param {object} style - caption style (fontFamily/fontName, bold/Bold, fontWeight)
 * @param {string|boolean|null} script - the caption script ('devanagari' | 'bengali' | 'telugu', see captionScript;
 *   the legacy boolean hasDevanagari is accepted)
 * @returns {{ baseFontName, fontName, finalBold, requestedWeight, useWeightFace, isDevaFace,
 *             isBalooFace, isDevaScriptFace, isBengFace, isBalooDaFace, isBengScriptFace, isTelFace,
 *             isBalooTammuduFace, isTelScriptFace, isIndicFace,
 *             bold, estCharTable, estFontScale, sizeCompensation }}
 *   fontName        — the family name to put in the ASS Style / \fn (weight-specific face when routed)
 *   finalBold       — '-1' | '0' from the boolean `bold` (legacy string `Bold` otherwise)
 *   bold            — whether to MEASURE as bold (bold flag or a dedicated weight face)
 *   sizeCompensation— extra Fontsize multiplier for faces libass renders smaller (Devanagari)
 */
// ── User-uploaded fonts ──
// style.customFonts is a registry of uploaded font files: [{ family, fontName, weight, italic,
// supportsDevanagari, metrics: { renderRatio, sizeCompensation, baselineDrop, capCenterDrop,
// charTable } }], measured from the file at upload (backend fontService). When the style's font
// family is one of them, the face comes from these metrics, so the preview and the burn wrap
// and size the text identically without a hand-tuned table. Hindi text on a font without
// Devanagari glyphs (or Bengali / Telugu text on one without those glyphs) falls through to the
// normal Noto routing below.
const pickCustomFont = (s, script) => {
  const list = Array.isArray(s.customFonts) ? s.customFonts : null;
  const fam = typeof s.fontFamily === 'string' ? s.fontFamily.trim().toLowerCase() : '';
  if (!list || !fam) return null;
  const faces = list.filter((f) => f && typeof f.family === 'string' && f.family.trim().toLowerCase() === fam && f.metrics);
  if (!faces.length) return null;
  if (script && INDIC_SCRIPTS[script] && !faces.some((f) => f[INDIC_SCRIPTS[script].supportsFlag])) return null;
  const wantBold = s.bold === true || s.Bold === '-1';
  const wantWeight = (s.fontWeight !== undefined && s.fontWeight !== null && !isNaN(parseInt(s.fontWeight)))
    ? parseInt(s.fontWeight) : (wantBold ? 700 : 400);
  const wantItalic = !!s.italic;
  const score = (f) => Math.abs((f.weight || 400) - wantWeight) + (!!f.italic === wantItalic ? 0 : 1000);
  return faces.reduce((best, f) => (score(f) < score(best) ? f : best), faces[0]);
};

const customFace = (f, s) => {
  const m = f.metrics || {};
  const weight = f.weight || 400;
  // libass matches on the file's legacy family name (name ID 1). A RIBBI family ("Brand Sans"
  // Regular/Bold) shares that name across files and needs the Bold flag to pick the bold file;
  // a dedicated weight file carries its style in the name ("Brand Sans Black") and must render
  // with the flag off, or libass emboldens it a second time (same rule as WEIGHT_FACES).
  const dedicated = String(f.fontName || '').trim().toLowerCase() !== String(f.family || '').trim().toLowerCase();
  const finalBold = (!dedicated && weight >= 600) ? '-1' : '0';
  return {
    baseFontName: f.family,
    fontName: f.fontName || f.family,
    finalBold,
    requestedWeight: weight,
    useWeightFace: dedicated,
    isDevaFace: false,
    isBalooFace: false,
    isDevaScriptFace: false,
    script: null,
    indic: null,
    indicFace: null,
    indicKind: null,
    bold: weight >= 600,
    estCharTable: m.charTable && Object.keys(m.charTable).length ? m.charTable : null,
    estFontScale: m.renderRatio || 0.64,
    sizeCompensation: m.sizeCompensation || 1,
    normalLineHeight: typeof m.normalLineHeight === 'number' ? m.normalLineHeight : DEFAULT_NORMAL_LINE_HEIGHT,
    baselineDrop: typeof m.baselineDrop === 'number' ? m.baselineDrop : null,
    capCenterDrop: typeof m.capCenterDrop === 'number' ? m.capCenterDrop : null,
    custom: true,
    customId: f.id || null,
  };
};

const resolveFontFace = (style, scriptArg) => {
  const s = style || {};
  const script = indicScript(scriptArg);
  const custom = pickCustomFont(s, script);
  if (custom) return customFace(custom, s);
  const fontFamily = s.fontFamily || null;
  const fontName = s.fontName || 'Arial';
  const isBold = (s.bold === undefined) ? null : s.bold;
  const Bold = s.Bold === undefined ? '-1' : s.Bold;
  const fontWeight = (s.fontWeight === undefined) ? null : s.fontWeight;

  // Use the font family name directly — libass/DirectWrite resolves bold/regular via the
  // Bold flag in the ASS Style header (e.g. Bold:-1), NOT via a "FamilyName-Bold" suffix.
  // Using "Montserrat-Bold" as Fontname causes libass to look for a family literally named
  // "Montserrat-Bold" which doesn't exist, resulting in a fallback to the system default font.
  const baseFontName = fontFamily || fontName;
  let actualFontName = baseFontName;

  let finalBold = isBold !== null ? (isBold ? '-1' : '0') : Bold;

  const requestedWeight = (fontWeight !== null && fontWeight !== undefined && !isNaN(parseInt(fontWeight)))
    ? parseInt(fontWeight)
    : (finalBold === '-1' ? 700 : 400);
  let useWeightFace = false;
  const famKey = (baseFontName || '').toLowerCase();
  for (const key in WEIGHT_FACES) {
    if (famKey.includes(key)) {
      const faces = WEIGHT_FACES[key];
      if (requestedWeight >= 900 && faces[900]) { actualFontName = faces[900]; useWeightFace = true; }
      else if (requestedWeight >= 800 && faces[800]) { actualFontName = faces[800]; useWeightFace = true; }
      else if (faces[requestedWeight]) { actualFontName = faces[requestedWeight]; useWeightFace = true; }
      break;
    }
  }
  // An explicit weight of 600+ with no dedicated face for it is Bold: the editor's CSS resolves
  // it to the family's 700 file (every caption family is loaded at 400 and 700 at least), so the
  // burn sets the Bold flag rather than leaving the regular face.
  if (!useWeightFace && fontWeight !== null && requestedWeight >= 600) finalBold = '-1';
  // ── Indic routing ── a caption in script S whose family lacks S's glyphs goes to the bundled
  // Noto face for S (its Latin glyphs cover the mixed Roman words too), or to S's Baloo sibling
  // for the comic templates (Bangers, any Baloo) so they keep a display look. A family that
  // already is S's Noto or Baloo face stays. The preview mirrors this in its CSS font stack
  // (captionStyles.indicFallbackFamily). A Latin caption set in one of these faces still gets
  // that face's measured metrics.
  const familyScript = (() => {
    for (const id of INDIC_SCRIPT_IDS) {
      const e = INDIC_SCRIPTS[id];
      if (famKey.includes(e.noto.key)) return { script: id, kind: 'noto' };
      if (famKey.includes(e.display.key)) return { script: id, kind: 'display' };
    }
    return null;
  })();
  let routed = familyScript;
  if (script && INDIC_SCRIPTS[script] && !(familyScript && familyScript.script === script)) {
    const e = INDIC_SCRIPTS[script];
    const kind = (famKey.includes('bangers') || famKey.includes('baloo')) ? 'display' : 'noto';
    const f = e[kind];
    if (requestedWeight >= 900 && f.faces[900]) { actualFontName = f.faces[900]; useWeightFace = true; }
    else if (requestedWeight >= 800 && f.faces[800]) { actualFontName = f.faces[800]; useWeightFace = true; }
    else {
      actualFontName = f.family; useWeightFace = false;
      // Same rule as above for the routed face: weight 600 (Soft Stack) rendered Regular here
      // while the preview showed Noto's Bold.
      if (requestedWeight >= 600) finalBold = '-1';
    }
    routed = { script, kind };
  }
  const indic = routed ? INDIC_SCRIPTS[routed.script] : null;
  const indicFace = routed ? indic[routed.kind] : null;
  const indicKind = routed ? routed.kind : null;
  const routedTo = (id, kind) => !!routed && routed.script === id && routed.kind === kind;
  const isDevaFace = routedTo('devanagari', 'noto');
  const isBalooFace = routedTo('devanagari', 'display');
  const isBengFace = routedTo('bengali', 'noto');
  const isBalooDaFace = routedTo('bengali', 'display');
  const isTelFace = routedTo('telugu', 'noto');
  const isBalooTammuduFace = routedTo('telugu', 'display');
  const isTamFace = routedTo('tamil', 'noto');
  const isBalooThambiFace = routedTo('tamil', 'display');
  const isGujFace = routedTo('gujarati', 'noto');
  const isBalooBhaiFace = routedTo('gujarati', 'display');
  const isArabFace = routedTo('arabic', 'noto');
  const isBalooBhaijaanFace = routedTo('arabic', 'display');
  const isPlayfairFace = actualFontName.toLowerCase().includes('playfair');
  const isKalamFace = actualFontName.toLowerCase().includes('kalam');
  const isCaveatBrushFace = actualFontName.toLowerCase().includes('caveat brush');
  const isSatisfyFace = actualFontName.toLowerCase().includes('satisfy');
  const isAntonFace = actualFontName.toLowerCase().includes('anton');

  // Inter and Noto Sans Devanagari have exact measured advance tables — use them instead of the
  // Montserrat-proportioned approximation. Inter has a table per bundled face (Regular, SemiBold,
  // Bold, ExtraBold, Black), picked by the same weight that routes the face; the Devanagari table is
  // Regular advances with a per-weight width factor folded into the scale. Every other font —
  // including the dedicated weight faces — has a measured entry in FONT_WIDTH_SCALE keyed by
  // face name.
  // A face whose Latin glyphs grow differently from its letters with weight (the Arabic faces)
  // measures with a per-weight table (weightTable); the others use the one measured table.
  const estCharTable = indicFace
    ? (indicFace.weightTable ? indicFace.weightTable(requestedWeight) : indicFace.charTable)
    : isPlayfairFace
      ? PLAYFAIR_ITALIC_CHAR_WIDTHS
      : isKalamFace
        ? (finalBold === '-1' || requestedWeight >= 700 ? KALAM_CHAR_WIDTHS.bold : KALAM_CHAR_WIDTHS.regular)
      : isCaveatBrushFace
        ? CAVEAT_BRUSH_CHAR_WIDTHS
      : isSatisfyFace
        ? SATISFY_CHAR_WIDTHS
      : (famKey.includes('inter')
        ? (requestedWeight >= 900 ? INTER_CHAR_WIDTHS.black
          : requestedWeight >= 800 ? INTER_CHAR_WIDTHS.extrabold
            : (finalBold === '-1' || requestedWeight >= 700) ? INTER_CHAR_WIDTHS.bold
              : requestedWeight >= 600 ? INTER_CHAR_WIDTHS.semibold : INTER_CHAR_WIDTHS.regular)
        : null);
  const estFontScale = indicFace
    ? indicFace.renderRatio * indicFace.widthFactor(requestedWeight)
      : isPlayfairFace
        ? PLAYFAIR_RENDER_RATIO
        : isKalamFace
          ? KALAM_RENDER_RATIO
          : isCaveatBrushFace
            ? CAVEAT_BRUSH_RENDER_RATIO
            : isSatisfyFace
              ? SATISFY_RENDER_RATIO
            : (estCharTable ? INTER_RENDER_RATIO : fontWidthScale(actualFontName));

  return {
    baseFontName,
    fontName: actualFontName,
    finalBold,
    requestedWeight,
    useWeightFace,
    isDevaFace,
    isBalooFace,
    isDevaScriptFace: isDevaFace || isBalooFace,
    isBengFace,
    isBalooDaFace,
    isBengScriptFace: isBengFace || isBalooDaFace,
    isTelFace,
    isBalooTammuduFace,
    isTelScriptFace: isTelFace || isBalooTammuduFace,
    isTamFace,
    isBalooThambiFace,
    isTamScriptFace: isTamFace || isBalooThambiFace,
    isGujFace,
    isBalooBhaiFace,
    isGujScriptFace: isGujFace || isBalooBhaiFace,
    isArabFace,
    isBalooBhaijaanFace,
    isArabScriptFace: isArabFace || isBalooBhaijaanFace,
    // The script this face serves (an INDIC_SCRIPTS id), its registry entry, the face entry
    // (noto | display) and which kind it is; null for a Latin face.
    script: routed ? routed.script : null,
    indic,
    indicFace,
    indicKind,
    // Any Indic face: no italic, headline-tight box padding.
    isIndicFace: !!indicFace,
    bold: finalBold === '-1' || useWeightFace,
    estCharTable,
    estFontScale,
    sizeCompensation: indicFace ? indicFace.sizeCompensation
      : isPlayfairFace ? PLAYFAIR_SIZE_COMPENSATION : isKalamFace ? KALAM_SIZE_COMPENSATION
        : isCaveatBrushFace ? CAVEAT_BRUSH_SIZE_COMPENSATION : isSatisfyFace ? SATISFY_SIZE_COMPENSATION
          : isAntonFace ? ANTON_SIZE_COMPENSATION : latinSizeCompensation(actualFontName),
    // The face's CSS `line-height: normal`, the karaoke line advance when the style sets none.
    normalLineHeight: indicFace ? indicFace.normalLineHeight : normalLineHeightOf(actualFontName),
    // How far below the vertical centre of the font box the baseline sits, as a fraction of the
    // libass Fontsize: (usWinAscent - usWinDescent) / 2 / (usWinAscent + usWinDescent). Only the
    // template faces that mix on stacked lines are measured; null elsewhere (no correction).
    baselineDrop: isKalamFace ? 0.1669 : isCaveatBrushFace ? 0.2619 : isPlayfairFace ? 0.3220
      : isSatisfyFace ? 0.1564 : isAntonFace ? 0.3101 : null,
    // How far below the font box centre the centre of a capital sits, as a fraction of the libass
    // Fontsize (baselineDrop - capHeight / 2 / (usWinAscent + usWinDescent)); a word behind the text
    // is lifted by this so its capitals centre on the line. Anton: 0.3101 - 1760 / 2 / 3550.
    capCenterDrop: isAntonFace ? 0.0622 : null,
  };
};

/**
 * Extra multiplier on renderScale for everything drawn at text size (font, outline, shadow, blur,
 * letter spacing, em-based widths). Sizes are designed on a 1080-high canvas, which suits classic
 * subtitles on any aspect ratio. Reel-style typography (style.sizeBasis = 'width') is instead meant
 * to fill the same share of the frame WIDTH as on a vertical video: on a landscape frame W×H its
 * text grows by (W/H)², i.e. ×3.16 on 16:9, so a template tuned on 9:16 looks the same on 16:9.
 * Portrait and square frames, and every other template, get 1. Margins and padding never use it.
 */
const TEXT_SCALE_MAX = 4;
const textScaleFactor = (style, videoWidth, videoHeight) => {
  const w = parseFloat(videoWidth) || 0;
  const h = parseFloat(videoHeight) || 0;
  if (!style || style.sizeBasis !== 'width' || !(w > h) || !(h > 0)) return 1;
  return Math.min(TEXT_SCALE_MAX, (w / h) * (w / h));
};

/** The libass Fontsize (video px) for a design-space fontSize. */
const burnFontSize = (fontSize, renderScale, face) =>
  Math.round((parseFloat(fontSize) || 24) * renderScale * LIBASS_FONT_COMPENSATION * (face ? face.sizeCompensation : 1));

/** Apply a CSS-like text-transform. */
const transformText = (txt, mode) => {
  if (!txt) return '';
  if (mode === 'uppercase') return txt.toUpperCase();
  if (mode === 'lowercase') return txt.toLowerCase();
  if (mode === 'capitalize') return txt.replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())));
  return txt;
};

/**
 * Decide how a caption's background is rendered.
 * @returns {{ hasBackground, resolvedBackground, borderRadius, useDrawnBox, actualBorderStyle,
 *             useBoxPadding, wantsBoxShadow, isLinesBox }}
 *   resolvedBackground — backgroundColor with backgroundOpacity folded in (rgba string) so the
 *                        ASS colour formatter can encode the alpha
 *   useDrawnBox        — vector rounded-rect (\p1) box on its own layer (rounded corners, or a
 *                        box drop shadow, which only the drawn box can carry)
 *   actualBorderStyle  — '3' = libass's own per-event box (sharp corners), else '1'
 *   useBoxPadding      — BorderStyle=3 box: the outline doubles as the box padding
 */
const resolveBoxStrategy = (style) => {
  const s = style || {};
  const rawBackground = s.backgroundColor === undefined ? '&H00000000' : s.backgroundColor;
  const backgroundOpacity = s.backgroundOpacity === undefined ? 100 : s.backgroundOpacity;
  const boxMode = !!s.boxMode;
  const BorderStyle = s.BorderStyle === undefined ? '1' : s.BorderStyle;

  // Apply backgroundOpacity: convert hex → rgba so formatColor can encode alpha in ASS
  let resolvedBackground = rawBackground;
  if (rawBackground && !rawBackground.startsWith('&H') && backgroundOpacity < 100) {
    const h = rawBackground.replace('#', '');
    if (h.length === 6) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      resolvedBackground = `rgba(${r}, ${g}, ${b}, ${(backgroundOpacity / 100).toFixed(2)})`;
    }
  }

  const hasBackground = !!(resolvedBackground && resolvedBackground !== '&H00000000' && resolvedBackground !== 'transparent' && resolvedBackground !== 'rgba(0,0,0,0)');
  // Match the live preview (VideoPlayer.jsx) which defaults a box's corner radius to 8px when
  // a background is added without an explicit radius. Without this, a custom background burned
  // with borderRadius=0 took the sharp BorderStyle=3 path and looked different from the preview.
  const borderRadius = (s.borderRadius !== undefined && s.borderRadius !== null)
    ? (parseFloat(s.borderRadius) || 0)
    : ((hasBackground || boxMode) ? 8 : 0);

  // Box rendering strategy:
  // - borderRadius > 0: Use vector drawing (\p1) for rounded rectangle on layer 0, text on layer 1
  // - borderRadius = 0: Use BorderStyle=3 for sharp rectangular box with outline-as-padding
  let actualBorderStyle = BorderStyle;
  let useBoxPadding = false;
  let useDrawnBox = false;

  // A box drop shadow can only be rendered on the drawn (vector) box, so a shadow request
  // forces the drawn path even at borderRadius=0 (it draws a plain sharp rectangle then).
  const wantsBoxShadow = Math.max(0, parseFloat(s.boxShadowDepth) || 0) > 0;

  if (hasBackground || boxMode) {
    if (borderRadius > 0 || wantsBoxShadow) {
      // Use vector drawing for rounded/sharp corners — covers both boxType='lines' (per-line boxes)
      // and standard boxMode (whole-segment box). borderRadius=0 draws a plain rectangle.
      useDrawnBox = true;
      actualBorderStyle = '1';
    } else if (s.boxType !== 'lines') {
      // Non-lines box: use BorderStyle=3 so each text event gets a background rect
      actualBorderStyle = '3';
      useBoxPadding = true;
    }
    // boxType === 'lines' with borderRadius=0: no drawn box; per-line rendering via
    // boxMode=true in the event loop gives each line a separate event with BorderStyle=1
  }

  return {
    hasBackground,
    resolvedBackground,
    borderRadius,
    useDrawnBox,
    actualBorderStyle,
    useBoxPadding,
    wantsBoxShadow,
    isLinesBox: useDrawnBox && s.boxType === 'lines',
  };
};

/**
 * Margins and anchor for a caption block, in video px.
 * @returns {{ position, alignment, marginV, marginH, marginL, marginR, baseAlign }}
 *   baseAlign — the ASS \an (1..9) numpad alignment for position × alignment
 */
const resolveMargins = (style, renderScale) => {
  const s = style || {};
  const position = s.position || 'bottom';
  const alignment = (s.textAlign !== undefined && s.textAlign !== null) ? s.textAlign : (s.Alignment || 'center');
  const parsedMarginV = (s.marginV !== null && s.marginV !== undefined) ? parseFloat(s.marginV) : parseFloat(s.MarginV || 80);
  const parsedMarginH = (s.marginH !== null && s.marginH !== undefined) ? parseFloat(s.marginH) : parseFloat(s.MarginH || 10);
  const marginV = Math.round(parsedMarginV * renderScale);
  const marginH = Math.round(parsedMarginH * renderScale);

  let baseAlign = 2; // Bottom-Center default
  if (position === 'middle') baseAlign = alignment === 'left' ? 4 : alignment === 'right' ? 6 : 5;
  else if (position === 'top') baseAlign = alignment === 'left' ? 7 : alignment === 'right' ? 9 : 8;
  else baseAlign = alignment === 'left' ? 1 : alignment === 'right' ? 3 : 2;

  // The unconstrained side of a left/right-aligned caption gets a default small margin.
  const defaultSmallMargin = Math.round(10 * renderScale);
  let marginL = marginH;
  let marginR = marginH;
  if (alignment === 'left') marginR = defaultSmallMargin;
  else if (alignment === 'right') marginL = defaultSmallMargin;

  return { position, alignment, marginV, marginH, marginL, marginR, baseAlign };
};

/**
 * Greedy word wrap by measured width. Returns lines as arrays of word INDICES into `texts`.
 * `measure(text, index)` gets the word index too, so callers can honour per-word scale.
 * NOTE: the running width deliberately keeps the leading-space width of the word that opened a
 * new line (the burn has always wrapped this way, and changing it would move existing line
 * breaks). It only makes lines wrap a hair early, never overflow.
 */
const wrapWordsByWidth = (texts, availWidth, measure, spaceWidthPx) => {
  const out = [];
  let curLine = [];
  let curWidth = 0;
  texts.forEach((t, i) => {
    const wordWidth = measure(t, i);
    const spaceWidth = curLine.length > 0 ? spaceWidthPx : 0;
    if (curWidth + spaceWidth + wordWidth > availWidth && curLine.length > 0) {
      out.push(curLine);
      curLine = [];
      curWidth = 0;
    }
    curLine.push(i);
    curWidth += spaceWidth + wordWidth;
  });
  if (curLine.length > 0) out.push(curLine);
  return out;
};

/**
 * Like wrapWordsByWidth, with the same number of lines, but evened out: rather than filling each
 * line greedily and leaving a lone word on the last one ("kya aapke saath / bhi"), it wraps at the
 * narrowest width that still needs no extra line.
 */
const wrapWordsBalanced = (texts, availWidth, measure, spaceWidthPx) => {
  const greedy = wrapWordsByWidth(texts, availWidth, measure, spaceWidthPx);
  if (greedy.length < 2) return greedy;
  const widest = texts.reduce((m, t, i) => Math.max(m, measure(t, i)), 0);
  let lo = Math.min(widest, availWidth);
  let hi = availWidth;
  for (let step = 0; step < 16 && hi - lo > 1; step++) {
    const mid = (lo + hi) / 2;
    if (wrapWordsByWidth(texts, mid, measure, spaceWidthPx).length <= greedy.length) hi = mid; else lo = mid;
  }
  return wrapWordsByWidth(texts, hi, measure, spaceWidthPx);
};

// ── Free layout ("playful" templates): every word gets its own spot on the frame ──
//
// style.layout = 'free' turns a caption into individually placed words. Per-word overrides live
// ON the word object so they survive edits (see wordSync/segmentation tests):
//   fx, fy     — centre of the word as a FRACTION of the frame (0..1), resolution-independent
//   scale      — multiplier on the caption fontSize
//   rotation   — degrees, clockwise (CSS convention; the burn negates for ASS)
//   color      — CSS colour for this word
//   hero       — true/false forces this word in or out of the hero set; absent = the rules decide
// Words without fx/fy are auto-placed by style.wordRules, deterministically (seeded by the
// caption's start + text) so the preview and the burn land every word in the same place.
const DEFAULT_WORD_RULES = {
  placement: 'stairs', // 'stairs' | 'zigzag' | 'scatter'
  hold: 'stack',       // 'stack' = words accumulate until the caption ends; 'pop' = only the spoken word
  heroEvery: 0,        // one hero (bigger, heroColor) per N words; 0 = no automatic heroes
  heroScale: 1.6,
  heroColor: null,
  asideScale: 1,
  jitter: 0.06,        // random offset as a fraction of the frame
  band: [0.18, 0.82],  // vertical band (fractions) words are kept inside
  sideMargin: 0.06,    // horizontal safe margin (fraction)
};
// Per-word size multipliers are clamped everywhere (burn, preview, layout) so bad or hand-edited
// data can never produce an invisible or absurdly large word. The editor's own controls stay
// within 0.4-4.
const WORD_SCALE_MIN = 0.2;
const WORD_SCALE_MAX = 6;
const clampWordScale = (value, fallback = 1) => {
  const v = parseFloat(value);
  if (!Number.isFinite(v) || v <= 0) return fallback;
  return Math.min(WORD_SCALE_MAX, Math.max(WORD_SCALE_MIN, v));
};
// Shrink a requested word scale just enough that the word fits maxWidth (never grows it).
// width1x is the word's measured width at scale 1.
const fitScaleToWidth = (requested, width1x, maxWidth) => {
  if (!(width1x > 0) || !(maxWidth > 0) || width1x * requested <= maxWidth) return requested;
  return Math.max(WORD_SCALE_MIN, maxWidth / width1x);
};
const finiteOr = (value, fallback) => {
  const v = parseFloat(value);
  return Number.isFinite(v) ? v : fallback;
};
const clamp01 = (v) => Math.min(1, Math.max(0, v));

// Template rules are data (and saved on the job), so validate every field instead of trusting it:
// a missing/garbled band or a string where an object belongs must degrade to defaults, never crash
// the burn.
const resolveWordRules = (style) => {
  const raw = style && style.wordRules && typeof style.wordRules === 'object' && !Array.isArray(style.wordRules) ? style.wordRules : {};
  const d = DEFAULT_WORD_RULES;
  const band = Array.isArray(raw.band) && raw.band.length === 2
    && Number.isFinite(parseFloat(raw.band[0])) && Number.isFinite(parseFloat(raw.band[1]))
    && clamp01(parseFloat(raw.band[0])) < clamp01(parseFloat(raw.band[1]))
    ? [clamp01(parseFloat(raw.band[0])), clamp01(parseFloat(raw.band[1]))]
    : d.band;
  return {
    placement: ['stairs', 'zigzag', 'scatter'].includes(raw.placement) ? raw.placement : d.placement,
    hold: raw.hold === 'pop' || raw.hold === 'stack' ? raw.hold : d.hold,
    heroEvery: Math.max(0, Math.floor(finiteOr(raw.heroEvery, d.heroEvery))),
    heroScale: clampWordScale(raw.heroScale, d.heroScale),
    heroColor: typeof raw.heroColor === 'string' && raw.heroColor ? raw.heroColor : d.heroColor,
    asideScale: clampWordScale(raw.asideScale, d.asideScale),
    jitter: Math.min(0.5, Math.max(0, finiteOr(raw.jitter, d.jitter))),
    band,
    sideMargin: Math.min(0.4, Math.max(0, finiteOr(raw.sideMargin, d.sideMargin))),
  };
};

// FNV-1a string hash + mulberry32 PRNG: cheap, dependency-free, identical in Node and browsers.
const hashString = (str) => {
  let h = 2166136261;
  for (const ch of String(str)) { h ^= ch.codePointAt(0); h = Math.imul(h, 16777619); }
  return h >>> 0;
};
const mulberry32 = (seed) => () => {
  seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Small words that make a silly hero ("a", "hai", "ki"): English, Roman Hinglish and Devanagari.
const HERO_FILLER_WORDS = new Set([
  'a', 'an', 'the', 'is', 'am', 'are', 'was', 'were', 'be', 'to', 'of', 'in', 'on', 'at', 'for',
  'and', 'or', 'but', 'so', 'it', 'its', 'i', 'you', 'we', 'he', 'she', 'they', 'me', 'us', 'my',
  'your', 'our', 'this', 'that', 'with', 'as', 'by', 'do', 'if', 'not', 'no',
  'hai', 'hain', 'ho', 'ka', 'ki', 'ke', 'ko', 'se', 'me', 'mein', 'mai', 'main', 'par', 'pe',
  'toh', 'to', 'bhi', 'hi', 'ek', 'aur', 'ya', 'na', 'tha', 'thi', 'the', 'kya', 'jo', 'ye', 'yeh',
  'wo', 'woh', 'vo', 'hum', 'tum', 'aap', 'ab', 'kar', 'ne',
  // Banglish function words and helpers.
  'ami', 'tumi', 'apni', 'amra', 'tomra', 'apnara', 'amar', 'tomar', 'apnar', 'ache', 'achhe',
  'achi', 'acho', 'achen', 'hoy', 'hobe', 'holo', 'hocche', 'chilo', 'kore', 'korbo', 'korchi',
  'korte', 'kora', 'ki', 'je', 'jodi', 'tahole', 'kintu', 'ar', 'o', 'ba', 'ebong', 'ei', 'oi',
  'ta', 'ekta', 'onek', 'khub', 'ekhon', 'tokhon', 'ekhane', 'okhane', 'kothay', 'keno', 'kemon',
  'kobe', 'kivabe', 'jonno', 'theke', 'sathe', 'kache', 'diye', 'niye', 'noy', 'nei', 'na',
  // Connectors, helpers and vague words that read as filler when blown up big.
  'lekin', 'magar', 'aisa', 'aisi', 'aise', 'vaisa', 'vahan', 'wahan', 'yahan', 'jaise', 'jab', 'tab',
  'kuch', 'kaise', 'kyun', 'kyu', 'kyunki', 'agar', 'phir', 'fir', 'abhi', 'raha', 'rahi', 'rahe',
  'hota', 'hoti', 'hote', 'hua', 'hui', 'hue', 'kiya', 'kiye', 'liye', 'lag', 'gaya', 'gayi', 'diya',
  'wala', 'wali', 'wale', 'sab', 'bahut', 'nahi', 'nahin', 'koi', 'kisi', 'apna', 'apni', 'apne',
  'iss', 'uss', 'is', 'us', 'un', 'vah', 'kaun', 'kab', 'kahan',
  'just', 'like', 'have', 'has', 'had', 'will', 'can', 'get', 'got', 'very', 'really', 'then',
  'there', 'here', 'when', 'what', 'which', 'who', 'how', 'why', 'from', 'about', 'into', 'out',
  'up', 'all', 'some', 'any', 'also', 'only', 'even', 'still', 'been', 'did', 'does', 'done', 'them',
  'है', 'हैं', 'हो', 'का', 'की', 'के', 'को', 'से', 'में', 'पर', 'तो', 'भी', 'ही', 'और', 'एक', 'था',
  'थी', 'थे', 'ये', 'यह', 'वो', 'वह', 'क्या', 'जो', 'ने', 'मैं', 'हम', 'तुम', 'आप', 'ना', 'या',
  // Bengali-script function words.
  'আমি', 'তুমি', 'আপনি', 'আমরা', 'তোমরা', 'আমার', 'তোমার', 'আপনার', 'আছে', 'আছি', 'আছো', 'হয়', 'হবে',
  'হলো', 'ছিল', 'করে', 'করবো', 'করছি', 'কি', 'যে', 'যদি', 'তাহলে', 'কিন্তু', 'আর', 'ও', 'বা', 'এবং',
  'এই', 'ওই', 'টা', 'একটা', 'অনেক', 'খুব', 'এখন', 'তখন', 'এখানে', 'ওখানে', 'কোথায়', 'কেন', 'কেমন',
  'জন্য', 'থেকে', 'সাথে', 'কাছে', 'দিয়ে', 'নিয়ে', 'নয়', 'নেই', 'না', 'এর', 'তার', 'সে', 'তারা',
  // Telugu-script function words.
  'నేను', 'మీరు', 'నువ్వు', 'మనం', 'మేము', 'వాళ్ళు', 'అతను', 'ఆమె', 'ఇది', 'అది', 'ఈ', 'ఆ', 'ఒక',
  'కూడా', 'లో', 'కి', 'కు', 'నుంచి', 'నుండి', 'తో', 'కోసం', 'గురించి', 'మరియు', 'కానీ', 'అని', 'అంటే',
  'ఉంది', 'ఉన్నాయి', 'ఉన్నారు', 'ఉన్నాను', 'లేదు', 'కాదు', 'అవును', 'ఇప్పుడు', 'అప్పుడు', 'ఇక్కడ',
  'అక్కడ', 'ఎలా', 'ఏమిటి', 'ఎందుకు', 'ఎక్కడ', 'చాలా', 'ఇంకా', 'మళ్ళీ', 'నా', 'మీ', 'మా', 'వాళ్ళ',
  'అతని', 'దాని', 'చేసి', 'అయితే', 'కదా', 'అంతే', 'మరి', 'ఆ', 'ఏ', 'ఐతే',
  // Tamil-script function words (spoken forms).
  'நான்', 'நீ', 'நீங்க', 'நீங்கள்', 'நாம', 'நாங்க', 'அவன்', 'அவள்', 'அவங்க', 'அவர்', 'அது', 'இது',
  'இந்த', 'அந்த', 'ஒரு', 'ஒன்னு', 'கூட', 'மட்டும்', 'தான்', 'இல்ல', 'இல்லை', 'இருக்கு', 'இருக்கும்',
  'இருந்தது', 'ஆனா', 'ஆனால்', 'அப்புறம்', 'இப்போ', 'அப்போ', 'இங்க', 'அங்க', 'எப்படி', 'என்ன',
  'ஏன்', 'எங்க', 'ரொம்ப', 'இன்னும்', 'என்', 'உன்', 'உங்க', 'அவர்', 'ஓகே', 'சரி', 'ஆமா', 'வேணும்',
  'பண்ண', 'பண்ணி', 'செஞ்சு', 'போய்', 'வந்து', 'ல', 'கு', 'ஆ', 'ஓ', 'டா', 'டி', 'மா',
  // Gujlish function words and helpers (never an ordinary English word: the set is shared by
  // every mode, so "have" or "okay" here would demote English heroes too).
  'chhe', 'che', 'chhu', 'chu', 'chho', 'cho', 'chhiye', 'chhie', 'hato', 'hati', 'hatu', 'hata', 'hu', 'hun',
  'tame', 'tu', 'ame', 'teo', 'tene', 'temne', 'mane', 'tamne', 'amne', 'maru', 'mari', 'mara', 'tamaru',
  'tamari', 'tamara', 'pan', 'ne', 'ni', 'nu', 'no', 'maate', 'sathe', 'ke', 'ane', 'athva', 'athwa', 'jo',
  'jyare', 'tyare', 'kem', 'kevi', 'kevu', 'kyare', 'su', 'shu', 'kon', 'kai', 'kaik', 'badhu', 'badha',
  'bahu', 'khub', 'ghanu', 'thodu', 'pachi', 'pachhi', 'haji', 'aje', 'atyare', 'ahiya', 'ahi', 'tya', 'tyan',
  'nathi', 'nahi', 'karo', 'karu', 'karvu', 'karyu', 'kari', 'kare', 'thay', 'thayu', 'thai', 'joine', 'jovu',
  'levu', 'aavo', 'aavu', 'avo', 'javu', 'gayo', 'gaya', 'rahyo', 'rahi', 'rahya', 'majama', 'saru', 'sarun',
  // Gujarati-script function words.
  'છે', 'છું', 'છો', 'છીએ', 'હતો', 'હતી', 'હતું', 'હતા', 'હું', 'તમે', 'તું', 'અમે', 'આપણે', 'તેઓ', 'તેને',
  'તેમને', 'મને', 'તમને', 'અમને', 'મારું', 'મારી', 'મારા', 'તમારું', 'તમારી', 'તમારા', 'આ', 'એ', 'પણ', 'ને',
  'ના', 'ની', 'નું', 'નો', 'માં', 'થી', 'માટે', 'સાથે', 'કે', 'અને', 'અથવા', 'તો', 'જો', 'જ્યારે', 'ત્યારે',
  'કેમ', 'કેવી', 'કેવું', 'ક્યાં', 'ક્યારે', 'શું', 'કોણ', 'કંઈ', 'કંઈક', 'બધું', 'બધા', 'બહુ', 'ખૂબ', 'ઘણું',
  'થોડું', 'હવે', 'પછી', 'હજી', 'આજે', 'કાલે', 'અત્યારે', 'અહીંયા', 'અહીં', 'ત્યાં', 'જ', 'નથી', 'નહીં', 'હા',
  'કરો', 'કરું', 'કરવું', 'કર્યું', 'કરી', 'કરે', 'થાય', 'થયું', 'થઈ', 'જોઈ', 'જોઈને', 'જોવું', 'લેવું', 'આવો',
  'આવું', 'જવું', 'ગયો', 'ગયા', 'રહ્યો', 'રહી', 'રહ્યા', 'ઓકે', 'મજામાં', 'સારું',
  // Roman Urdu function words the Hinglish list lacks (never an ordinary English word: "hay", "kay"
  // stay out; "bilkul", "zaroor", "acha" are content words a speaker can stress, so they stay out too).
  'nai', 'ap', 'mujhe', 'tumhe', 'humein', 'hamein', 'unhe', 'unhen', 'inhe', 'kyunke', 'kyunkay', 'bohat', 'bohot',
  'yaar', 'bhai', 'jee', 'haan', 'bas', 'wahi', 'yahi',
  // Urdu-script function words and helpers.
  'ہے', 'ہیں', 'ہو', 'ہوں', 'تھا', 'تھی', 'تھے', 'کا', 'کی', 'کے', 'کو', 'سے', 'میں', 'پر', 'تو', 'بھی', 'ہی', 'اور',
  'ایک', 'یہ', 'وہ', 'کیا', 'جو', 'نے', 'ہم', 'تم', 'آپ', 'نہ', 'نہیں', 'یا', 'کہ', 'اس', 'ان', 'یہی', 'وہی', 'کچھ',
  'سب', 'بہت', 'کر', 'لیے', 'لئے', 'ساتھ', 'بعد', 'پھر', 'ابھی', 'اب', 'جب', 'تب', 'کیوں', 'کیسے', 'کہاں', 'کون',
  'مجھے', 'تمہیں', 'ہمیں', 'انہیں', 'اپنا', 'اپنی', 'اپنے', 'میرا', 'میری', 'میرے', 'لیکن', 'مگر', 'اگر', 'یار', 'جی',
  'ہاں', 'رہا', 'رہی', 'رہے', 'گیا', 'گئی', 'گئے', 'ہوا', 'ہوئی', 'ہوئے', 'والا', 'والی', 'والے', 'بس', 'تھوڑا',
]);
// How strong a hero a word would make: its letter count, or -1 for filler words.
const heroStrength = (text) => {
  const core = String(text || '').toLowerCase().replace(/[^\p{L}\p{M}\p{N}]/gu, '');
  if (!core) return -2;
  if (HERO_FILLER_WORDS.has(core)) return -1;
  return Array.from(core).length;
};
// A word the transcription flagged as the one to remember (`emphasis`, set by Gemini from what the
// speaker stressed and what carries the meaning) outranks every unflagged word, filler list
// included: "NAHI" can be the punchline. The flag is a number 0..1 or a boolean.
const isEmphasised = (w) => {
  const e = w && w.emphasis;
  return e === true || (typeof e === 'number' && e >= 0.5) || (typeof e === 'string' && parseFloat(e) >= 0.5);
};
const EMPHASIS_BONUS = 1000;
const wordStrength = (w) => {
  const base = heroStrength(w && w.word);
  if (!isEmphasised(w)) return base;
  return EMPHASIS_BONUS + Math.max(0, base);
};

/**
 * Pick the automatic hero words for a caption: one per heroEvery words (at least one), each the
 * strongest word of its stretch of the caption, so heroes stay spread out but never land on
 * "a" or "hai" when a real word is nearby. Ties go to the later word. Returns a Set of indices.
 */
const pickHeroWords = (texts, heroEvery, words = null) => {
  const heroes = new Set();
  const n = texts.length;
  if (!(heroEvery > 0) || n === 0) return heroes;
  const strength = (k) => (Array.isArray(words) && words[k] ? wordStrength({ ...words[k], word: texts[k] }) : heroStrength(texts[k]));
  const count = Math.max(1, Math.floor(n / heroEvery));
  for (let c = 0; c < count; c++) {
    const from = Math.round((c * n) / count);
    const to = Math.round(((c + 1) * n) / count);
    let best = from;
    for (let k = from; k < to; k++) if (strength(k) >= strength(best)) best = k;
    heroes.add(best);
  }
  return heroes;
};

// ── Word rules for flow (classic) templates: hero words and accent words ──
// A template opts in to heroes with style.heroEvery and to accents with style.accentEvery (plus
// style.accentColor). Both are picked across the WHOLE transcript, not per caption, so the rhythm
// is the same whether captions hold 2 words or 8: about one pick per `every` spoken words (the
// strongest word of each stretch, never a filler word), never two picks closer than half a
// stretch, at most one automatic hero per caption, and at most half of a caption's words
// highlighted. A word's own `hero` flag wins (heroEvery 0 = only starred words); heroes are drawn
// at style.heroScale in style.heroColor, accents in accentColor, and a word's own scale/colour win.
const DEFAULT_FLOW_HERO_SCALE = 1.5;
const transcriptWordRules = (transcript, style) => {
  const s = style || {};
  const flowOn = s.layout !== 'free';
  const heroOn = flowOn && s.heroEvery !== undefined && s.heroEvery !== null;
  const heroEvery = heroOn ? Math.max(0, Math.floor(finiteOr(s.heroEvery, 0))) : 0;
  const accentEvery = flowOn && typeof s.accentColor === 'string' && s.accentColor
    ? Math.max(0, Math.floor(finiteOr(s.accentEvery, 0))) : 0;
  const perCaption = (Array.isArray(transcript) ? transcript : []).map((seg) => {
    const words = seg && Array.isArray(seg.words) && seg.words.length > 0
      ? seg.words
      : ((seg && seg.text) || '').trim().split(/\s+/).filter(Boolean).map((word) => ({ word }));
    const off = () => words.map(() => false);
    return { words, autoHeroes: off(), heroes: off(), accents: off() };
  });
  const flat = [];
  perCaption.forEach((c, ci) => c.words.forEach((w, wi) => flat.push({ ci, wi, strength: wordStrength(w) })));
  // `preferred` (optional) steers the pick: the strongest preferred word of a stretch wins, and
  // only a stretch with no preferred word falls back to the strongest eligible one.
  const pickAcross = (every, eligible, take, preferred = null) => {
    if (!every) return;
    const gap = Math.ceil(every / 2);
    let last = -Infinity;
    for (let from = 0; from < flat.length; from += every) {
      let best = -1;
      let bestPreferred = -1;
      for (let k = from; k < Math.min(flat.length, from + every); k++) {
        if (k - last < gap || flat[k].strength < 0 || !eligible(flat[k])) continue;
        if (best < 0 || flat[k].strength >= flat[best].strength) best = k;
        if (preferred && preferred(flat[k]) && (bestPreferred < 0 || flat[k].strength >= flat[bestPreferred].strength)) bestPreferred = k;
      }
      const pick = bestPreferred >= 0 ? bestPreferred : best;
      if (pick >= 0) { take(flat[pick]); last = pick; }
    }
  };
  // Stagger puts a hero on a line of its own, so a hero second or second-to-last in a caption of
  // 3+ words would strand one word on a line ("a / SCREEN / and the"). Prefer any other spot.
  const heroPreferred = s.layout === 'stagger' && s.heroOwnLine !== false
    ? (f) => { const n = perCaption[f.ci].words.length; return n < 3 || (f.wi !== 1 && f.wi !== n - 2); }
    : null;
  pickAcross(heroEvery, (f) => !perCaption[f.ci].autoHeroes.some(Boolean), (f) => { perCaption[f.ci].autoHeroes[f.wi] = true; }, heroPreferred);
  perCaption.forEach((c) => c.words.forEach((w, i) => {
    c.heroes[i] = heroOn && (w && typeof w.hero === 'boolean' ? w.hero : c.autoHeroes[i]);
  }));
  const highlightCap = (c) => Math.max(1, Math.floor(c.words.length / 2));
  const highlighted = (c) => c.heroes.filter(Boolean).length + c.accents.filter(Boolean).length;
  pickAcross(accentEvery, (f) => {
    const c = perCaption[f.ci];
    return !c.heroes[f.wi] && highlighted(c) < highlightCap(c);
  }, (f) => { perCaption[f.ci].accents[f.wi] = true; });
  return perCaption.map(({ autoHeroes, heroes, accents }) => ({ autoHeroes, heroes, accents }));
};

// Resolved rules for one caption. `picks` is that caption's entry from transcriptWordRules; without
// it the caption is treated as a transcript of its own.
const flowWordRules = (words, texts, style, picks = null) => {
  const s = style || {};
  const n = texts.length;
  const usable = picks && Array.isArray(picks.heroes) && picks.heroes.length === n;
  const own = usable ? picks : transcriptWordRules([{ words: (Array.isArray(words) && words.length === n) ? words : texts.map((word) => ({ word })) }], s)[0];
  return {
    heroes: own.heroes,
    autoHeroes: own.autoHeroes,
    accents: own.accents,
    heroScale: clampWordScale(s.heroScale, DEFAULT_FLOW_HERO_SCALE),
    heroColor: typeof s.heroColor === 'string' && s.heroColor ? s.heroColor : null,
    accentColor: typeof s.accentColor === 'string' && s.accentColor ? s.accentColor : null,
  };
};

// ── Word highlight hold ──
// A spoken word stays highlighted until the next word begins, so the caption never flickers
// through the tiny gaps inside continuous speech. But a highlight that runs to the next word
// regardless of the gap makes the word look stretched whenever the speaker pauses, or when the
// transcript missed speech after it: the aligner's word may end at 1.2 s while the next word
// begins at 4.0 s, and the old word sat lit for three seconds. So the highlight now lets go
// WORD_HIGHLIGHT_HOLD_SECONDS after the word's own end, or at the next word's start, whichever
// comes first. The caption itself stays; only the "being spoken" state ends. Gaps up to the hold
// still bridge seamlessly, which covers the sub-0.3 s breaths between words in normal speech.
const WORD_HIGHLIGHT_HOLD_SECONDS = 0.3;

/**
 * When word highlighting ends, in seconds. `start`/`end` are the word's own times and
 * `nextStart` the following word's start (null/undefined for the caption's last word: the caller
 * caps the result at the caption end). A missing or backwards end counts as the word's start.
 */
const wordHighlightEnd = (start, end, nextStart) => {
  const s = Number.isFinite(start) ? start : (Number.isFinite(end) ? end : 0);
  const e = Number.isFinite(end) && end > s ? end : s;
  const lit = e + WORD_HIGHLIGHT_HOLD_SECONDS;
  return Number.isFinite(nextStart) ? Math.min(nextStart, lit) : lit;
};

// ── Typewriter animation (style.animation = 'typewriter') ──
// Each word types out while it is spoken: its characters appear one step apart, the step being
// the word's duration split over its characters but never slower than TYPEWRITER_MAX_CHAR_SECONDS.
// One soft grey block (the typing cursor) sits behind the newest character: it moves on when the
// next character appears and leaves TYPEWRITER_BOX_LAST_HOLD_SECONDS after a word's last letter.
const TYPEWRITER_MAX_CHAR_SECONDS = 0.06;
const TYPEWRITER_BOX_LAST_HOLD_SECONDS = 0.12;
const TYPEWRITER_BOX_OPACITY = 0.44;
const TYPEWRITER_FADE_OUT_MS = 150;

// User-perceived characters (a Devanagari syllable with its matras is one), so a letter never
// appears without its vowel sign.
const splitGraphemes = (text) => {
  const s = String(text || '');
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(s), (part) => part.segment);
  }
  return Array.from(s);
};

// Seconds after the word's start at which each of its `count` characters appears.
const typewriterCharOffsets = (wordStart, wordEnd, count) => {
  const duration = Math.max(0, finiteOr(wordEnd, 0) - finiteOr(wordStart, 0));
  const step = count > 0 ? Math.min(TYPEWRITER_MAX_CHAR_SECONDS, duration / count) : 0;
  return Array.from({ length: Math.max(0, count) }, (_, k) => k * step);
};

/**
 * The whole typing timeline of a caption. words: [{ text, start, end }] (seconds, text already
 * transformed). Returns one array per word of { c, at, boxEnd } in seconds: `at` when the
 * character appears, `boxEnd` when the typing block leaves it (never before `at`).
 */
const typewriterTimeline = (words) => {
  const perWord = (words || []).map((w) => {
    const chars = splitGraphemes(w && w.text);
    const start = finiteOr(w && w.start, 0);
    const offsets = typewriterCharOffsets(start, w && w.end, chars.length);
    return chars.map((c, k) => ({ c, at: start + offsets[k], boxEnd: start + offsets[k] }));
  });
  const flat = [];
  perWord.forEach((chars) => chars.forEach((ch, k) => flat.push({ ch, last: k === chars.length - 1 })));
  flat.forEach(({ ch, last }, k) => {
    const next = k + 1 < flat.length ? flat[k + 1].ch.at : Infinity;
    const leave = last ? Math.min(next, ch.at + TYPEWRITER_BOX_LAST_HOLD_SECONDS) : next;
    ch.boxEnd = Math.max(ch.at, Number.isFinite(leave) ? leave : ch.at + TYPEWRITER_BOX_LAST_HOLD_SECONDS);
  });
  return perWord;
};

/**
 * Place a caption's words individually. Returns { hold, rules, placed } where placed[k] =
 * { index, text, hero, autoHero, scale, fontSize, width, height, rotation, color, fx, fy, x, y, pinned }
 * (fontSize/width/height/x/y in video px; fx/fy fractions of the frame).
 */
const layoutFreeWords = (segment, style, frame, ctx) => {
  const { videoWidth, videoHeight, fontSize, face, texts } = ctx;
  const rules = resolveWordRules(style);
  const words = (segment && Array.isArray(segment.words)) ? segment.words : [];
  // Each word draws from its own random stream (caption seed + word index), so editing one word
  // (size, hero, a drag) never reshuffles its neighbours: they only move if they would now collide.
  const seedBase = `${(segment && segment.start) || ''}|${texts.join(' ')}`;
  const randFor = (index) => mulberry32(hashString(`${seedBase}|${index}`));

  const autoHeroes = pickHeroWords(texts, rules.heroEvery, words);

  const sideMarginFrac = rules.sideMargin;
  const placed = texts.map((text, k) => {
    const w = words[k] || {};
    const autoHero = autoHeroes.has(k);
    // The word's own hero flag wins; anything but a real boolean leaves it to the rules.
    const hero = typeof w.hero === 'boolean' ? w.hero : autoHero;
    // Pinned only with two real numbers; kept inside the frame whatever was stored.
    const pinned = w.fx !== undefined && w.fx !== null && w.fy !== undefined && w.fy !== null
      && Number.isFinite(parseFloat(w.fx)) && Number.isFinite(parseFloat(w.fy));
    const baseWidth = estimateTextWidth(text, fontSize, face.bold, face.estFontScale, face.estCharTable);
    // A word may never be wider than the frame allows: auto-placed words keep the side margins,
    // pinned words use almost the full width. Too-wide words (a long hero word at 2x) shrink to fit.
    const maxWidth = videoWidth * (pinned ? 0.96 : Math.max(0.2, 1 - 2 * sideMarginFrac));
    const scale = fitScaleToWidth(clampWordScale(w.scale, hero ? rules.heroScale : rules.asideScale), baseWidth, maxWidth);
    const fs = fontSize * scale;
    const width = baseWidth * scale;
    const height = fs * 0.75; // glyph box, roughly cap height + descender
    return {
      index: k, text, hero, autoHero, scale, fontSize: fs, width, height, baseWidth, maxWidth,
      rotation: finiteOr(w.rotation, 0),
      // Only a non-empty string is a colour; anything else stored on the word is ignored.
      color: (typeof w.color === 'string' && w.color) || (hero ? rules.heroColor : null) || null,
      fx: pinned ? clamp01(parseFloat(w.fx)) : null,
      fy: pinned ? clamp01(parseFloat(w.fy)) : null,
      pinned,
    };
  });

  const [b0, b1] = rules.band;
  const sm = rules.sideMargin;
  const halfW = (p) => p.width / 2 / videoWidth;
  const halfH = (p) => p.height / 2 / videoHeight;
  // A word too wide (or tall) for the safe area is centred on that axis rather than pushed off-frame.
  const clampTo = (p, fx, fy) => ({
    fx: 2 * halfW(p) >= 1 - 2 * sm ? 0.5 : Math.min(1 - sm - halfW(p), Math.max(sm + halfW(p), fx)),
    fy: 2 * halfH(p) >= b1 - b0 ? (b0 + b1) / 2 : Math.min(b1 - halfH(p), Math.max(b0 + halfH(p), fy)),
  });
  const boxOf = (p, fx, fy) => ({ x0: fx - halfW(p), x1: fx + halfW(p), y0: fy - halfH(p), y1: fy + halfH(p) });
  const overlaps = (a, b) => !(a.x1 < b.x0 || a.x0 > b.x1 || a.y1 < b.y0 || a.y0 > b.y1);

  // Occupied regions: pinned words plus caller-supplied no-go boxes (e.g. a detected face),
  // each { fx, fy, fw, fh } as fractions.
  // A pinned word keeps the user's position but always stays whole inside the frame (centred on an
  // axis it is too big for). Auto-placed words additionally respect the band and side margins.
  placed.forEach((p) => {
    if (!p.pinned) return;
    const hw = halfW(p);
    const hh = halfH(p);
    p.fx = hw >= 0.5 ? 0.5 : Math.min(1 - hw, Math.max(hw, p.fx));
    p.fy = hh >= 0.5 ? 0.5 : Math.min(1 - hh, Math.max(hh, p.fy));
  });
  const occupied = placed.filter(p => p.pinned).map(p => boxOf(p, p.fx, p.fy));
  ((frame && frame.avoid) || []).forEach(a => {
    if (a && isFinite(a.fx) && isFinite(a.fy)) occupied.push({ x0: a.fx - (a.fw || 0) / 2, x1: a.fx + (a.fw || 0) / 2, y0: a.fy - (a.fh || 0) / 2, y1: a.fy + (a.fh || 0) / 2 });
  });

  const stacked = rules.hold === 'stack';
  const unpinned = placed.filter(p => !p.pinned);
  // Vertical rhythm for stacked words: a column of word heights centred in the band. Pop words
  // are shown one at a time, so they hover around the band centre instead.
  const rowOf = (p) => (p.height / videoHeight) * 1.35;
  const column = unpinned.reduce((acc, p) => acc + rowOf(p), 0);
  let y = (b0 + b1) / 2 - column / 2;

  unpinned.forEach((p, k) => {
    const rand = randFor(p.index);
    const row = rowOf(p);
    const cy = stacked ? y + row / 2 : (b0 + b1) / 2 + (rand() * 2 - 1) * rules.jitter * 2;
    y += row;
    const j = (rand() * 2 - 1) * rules.jitter;
    let fx;
    let fy = cy;
    if (rules.placement === 'scatter') {
      let chosen = null;
      for (let attempt = 0; attempt < 24; attempt++) {
        const cand = clampTo(p, sm + rand() * (1 - 2 * sm), b0 + rand() * (b1 - b0));
        if (!occupied.some(b => overlaps(boxOf(p, cand.fx, cand.fy), b))) { chosen = cand; break; }
        if (!chosen) chosen = cand;
      }
      fx = chosen.fx; fy = chosen.fy;
    } else if (rules.placement === 'zigzag') {
      fx = (k % 2 === 0 ? 0.3 : 0.7) + j;
    } else { // stairs
      fx = 0.5 + (k % 2 === 0 ? -1 : 1) * (0.14 + Math.abs(j));
    }
    const c = clampTo(p, fx, fy);
    p.fx = c.fx; p.fy = c.fy;
    occupied.push(boxOf(p, c.fx, c.fy));
  });

  placed.forEach(p => { p.x = p.fx * videoWidth; p.y = p.fy * videoHeight; });
  return { hold: stacked ? 'stack' : 'pop', rules, placed };
};

// ── Stagger layout (style.layout = 'stagger') ──
// Lines are left-aligned and step to the right like a staircase: each line starts lineStagger of
// the previous line's width further right, the step shrinking evenly when the staircase would not
// fit the frame. A hero word gets a line to itself. Lines sit tight, overlapping by
// STAGGER_LINE_OVERLAP (or style.lineOverlap) of the smaller line's height. Words fade in as they
// are spoken; the caption fades out at the end.
// Options (all off by default, used by the Editorial template):
//   heroOwnLine: false  — the hero shares its line with the words around it
//   leadWordAlone: true — the first word sits on a line of its own, above the rest
//   lineAlign: 'zigzag' — lines are centred and nudged alternately left and right by lineStagger
//                         of the widest line, instead of stepping right
//   wordEntrance: 'blur'— words blur in as well as fade in
// Used by the Handwritten template:
//   fillLines: true         — words fill each line up to the wrap width (no line-count target)
//   lineAlign: 'heroCenter' — lines sit left-aligned in a text box lineMaxWidth of the frame wide,
//                             centred in the frame, and a hero line is centred in that box
const STAGGER_LINE_OVERLAP = 0.12;
const STAGGER_WORD_FADE_MS = 160;
const STAGGER_FADE_OUT_MS = 150;
const STAGGER_BLUR_IN_MS = 220;
// How fast a heroBehind word appears once spoken (it lands almost at once, like a cut).
const STAGGER_BEHIND_IN_MS = 80;
// Starting blur of a blurring-in word, as a fraction of the caption's visual font size.
const STAGGER_BLUR_EM = 0.12;

// How many lines a stagger caption of n words aims for, hero lines included, so the staircase
// shows whatever words-per-caption the user picked: 1 word stays one line, 2 words step over two
// lines, 3-4 words take two lines plus the hero's, and longer captions three. With the first word
// alone on its line, 3 words take two lines (1 + 2) and 4+ words three. A caption whose words
// don't fit that few lines at the wrap width takes more.
const staggerLineTarget = (n, heroCount, leadAlone = false) => {
  if (n <= 1) return n;
  if (leadAlone) return Math.min(n, (n <= 3 ? 2 : 3) + heroCount);
  return n <= 4 ? Math.min(n, 2 + heroCount) : 3;
};

/**
 * Split words into exactly `k` consecutive lines with the narrowest widest line, ties going to the
 * most even split. widths[k] is each word's width; returns lines of positions into `widths`.
 */
const partitionLines = (widths, k, spaceWidthPx) => {
  const m = widths.length;
  const lines = Math.max(1, Math.min(k, m));
  const prefix = [0];
  widths.forEach((w) => prefix.push(prefix[prefix.length - 1] + w));
  const span = (a, b) => prefix[b] - prefix[a] + (b - a - 1) * spaceWidthPx;
  const better = (x, y) => (Math.abs(x.max - y.max) > 0.5 ? x.max < y.max : x.sq < y.sq);
  // best[j][b]: first b words in j lines; from[j][b]: where its last line starts.
  const best = Array.from({ length: lines + 1 }, () => new Array(m + 1).fill(null));
  const from = Array.from({ length: lines + 1 }, () => new Array(m + 1).fill(0));
  best[0][0] = { max: 0, sq: 0 };
  for (let j = 1; j <= lines; j++) {
    for (let b = j; b <= m - (lines - j); b++) {
      for (let a = j - 1; a < b; a++) {
        const prev = best[j - 1][a];
        if (!prev) continue;
        const w = span(a, b);
        const cand = { max: Math.max(prev.max, w), sq: prev.sq + w * w };
        if (!best[j][b] || better(cand, best[j][b])) { best[j][b] = cand; from[j][b] = a; }
      }
    }
  }
  const out = [];
  for (let j = lines, b = m; j > 0; j--) {
    const a = from[j][b];
    out.unshift(Array.from({ length: b - a }, (_, i) => a + i));
    b = a;
  }
  return out;
};

/**
 * Word indices per line: each hero word alone, the words between heroes (runs) spread over the
 * caption's line target (staggerLineTarget). A run never gets fewer lines than it needs at
 * `wrapWidth`; spare lines go one at a time to the run whose lines are widest on average.
 * measure(text, wordIndex) is the word's drawn width. opts.heroOwnLine (default true),
 * opts.leadAlone and opts.fill (default false) are the style's heroOwnLine / leadWordAlone /
 * fillLines; with fill, runs take only the lines they need.
 */
const staggerLineBreaks = (texts, heroes, measure, spaceWidthPx, wrapWidth, opts = {}) => {
  const heroOwnLine = opts.heroOwnLine !== false;
  const leadAlone = !!opts.leadAlone;
  const items = [];
  let run = null;
  texts.forEach((_, i) => {
    // heroBehind: the hero is drawn behind the text, so it takes no place in the lines at all.
    if (opts.heroBehind && heroes[i]) return;
    if (heroOwnLine && heroes[i]) { run = null; items.push({ hero: i }); } else {
      if (!run) { run = []; items.push({ run }); }
      run.push(i);
    }
  });
  if (leadAlone && items[0] && items[0].run && items[0].run.length > 1) {
    const [first, ...rest] = items[0].run;
    items.splice(0, 1, { run: [first] }, { run: rest });
  }
  const runs = items.filter((it) => it.run).map((it) => it.run);
  const heroCount = items.length - runs.length;
  const widths = runs.map((r) => r.map((i) => measure(texts[i], i)));
  const runWidth = (r) => widths[r].reduce((sum, w) => sum + w, 0) + (widths[r].length - 1) * spaceWidthPx;
  const counts = runs.map((r, ri) => wrapWordsByWidth(r.map((i) => texts[i]), wrapWidth, (t, k) => widths[ri][k], spaceWidthPx).length);
  let spare = opts.fill ? 0 : staggerLineTarget(texts.length, heroCount, leadAlone) - heroCount - counts.reduce((sum, c) => sum + c, 0);
  while (spare > 0) {
    let pick = -1;
    runs.forEach((r, ri) => {
      if (counts[ri] < r.length && (pick < 0 || runWidth(ri) / counts[ri] > runWidth(pick) / counts[pick])) pick = ri;
    });
    if (pick < 0) break;
    counts[pick]++;
    spare--;
  }
  const out = [];
  let ri = 0;
  items.forEach((it) => {
    if (!it.run) { out.push([it.hero]); return; }
    const r = ri++;
    partitionLines(widths[r], counts[r], spaceWidthPx).forEach((line) => out.push(line.map((k) => it.run[k])));
  });
  return out;
};

/**
 * Position stagger lines in video px. Each line: { words, x (left edge), y (vertical centre),
 * width, height, hero }. Heights use the visual font size (fontSize × renderScale, what the
 * preview's CSS draws) × lineHeight × the line's largest word scale.
 */
const placeStaggerLines = ({ s, lines, lineWidths, wordScales, heroes, availableWidth, videoWidth, videoHeight, renderScale, margins, seed = 0, boxWidth = availableWidth, heroBaselineShift = null, behind = [] }) => {
  const em = (parseFloat(s.fontSize) || 48) * renderScale;
  const lineHeight = parseFloat(s.lineHeight) || 1;
  const overlap = Math.min(0.6, Math.max(0, finiteOr(s.lineOverlap, STAGGER_LINE_OVERLAP)));
  const starts = [];
  const isHeroLine = (li) => lines[li].length === 1 && !!heroes[lines[li][0]];
  let textBox = 0; // heroCenter: the text box width the block keeps even when its lines are shorter
  // Words drawn behind the text (heroBehind): { index, width, scale }. They widen the block and are
  // centred on it; the text block is centred on them vertically.
  const behindWidest = behind.reduce((m, b) => Math.max(m, b.width), 0);
  if (s.lineAlign === 'center') {
    const widest = Math.max(behindWidest, lineWidths.reduce((m, w) => Math.max(m, w), 0));
    lineWidths.forEach((width) => { starts.push((widest - width) / 2); });
    textBox = widest;
  } else if (s.lineAlign === 'heroCenter') {
    // Words left-aligned in a text box as wide as the caption's widest line, hero lines centred in
    // it; the box is what gets centred in the frame. The box used to keep the full wrap width
    // (boxWidth) even for a short caption, which parked a three-word caption entirely left of
    // centre on 9:16 frames. Lines still wrap at boxWidth (see staggerWrapWidth), so long captions
    // are unchanged.
    textBox = Math.min(availableWidth, lineWidths.reduce((m, w) => Math.max(m, w), 0));
    lineWidths.forEach((width, li) => { starts.push(isHeroLine(li) ? Math.max(0, (textBox - width) / 2) : 0); });
  } else if (s.lineAlign === 'zigzag') {
    // Each line centred on the widest, nudged alternately right and left (which side comes first
    // is seeded by the caption, so neighbouring captions don't all lean the same way). The nudge
    // shrinks so the block stays inside the available width.
    const widest = lineWidths.reduce((m, w) => Math.max(m, w), 0);
    const nudge = Math.max(0, Math.min(
      Math.min(1, Math.max(0, finiteOr(s.lineStagger, 0.1))) * widest,
      (availableWidth - widest) / 2,
    ));
    const firstSign = seed % 2 === 0 ? 1 : -1;
    lineWidths.forEach((width, li) => {
      starts.push((widest - width) / 2 + (lineWidths.length > 1 ? (li % 2 === 0 ? firstSign : -firstSign) * nudge : 0));
    });
    const minStart = starts.reduce((m, x) => Math.min(m, x), Infinity);
    starts.forEach((x, li) => { starts[li] = x - minStart; });
  } else {
    // Line i starts step × (the widths of the lines above it). The step is lineStagger unless that
    // would push a line past the available width; then it shrinks just enough for every line to
    // fit, so the staircase keeps its shape instead of snapping back to the left edge.
    let step = Math.min(1, Math.max(0, finiteOr(s.lineStagger, 0.5)));
    let above = 0;
    lineWidths.forEach((width, li) => {
      if (li > 0 && above > 0) step = Math.min(step, (availableWidth - width) / above);
      above += width;
    });
    step = Math.max(0, step);
    lineWidths.forEach((width, li) => {
      starts.push(li > 0 ? starts[li - 1] + lineWidths[li - 1] * step : 0);
    });
  }
  const placed = [];
  lines.forEach((line, li) => {
    const width = lineWidths[li];
    const prev = placed[li - 1];
    const height = em * lineHeight * line.reduce((m, i) => Math.max(m, wordScales[i] || 1), 1);
    const top = prev ? prev.top + prev.height - overlap * Math.min(prev.height, height) : 0;
    placed.push({ words: line, start: starts[li], width, height, top, hero: line.length === 1 && !!heroes[line[0]] });
  });
  const blockWidth = placed.reduce((m, l) => Math.max(m, l.start + l.width), Math.max(textBox, behindWidest));
  const last = placed[placed.length - 1];
  const textHeight = last ? last.top + last.height : 0;
  // A word behind the text is em × its scale tall (the height of its capitals' font box).
  const behindTallest = behind.reduce((m, b) => Math.max(m, em * (b.scale || 1)), 0);
  const blockHeight = Math.max(textHeight, behindTallest);
  const textTop = (blockHeight - textHeight) / 2;
  const blockLeft = margins.alignment === 'left' ? margins.marginL
    : margins.alignment === 'right' ? videoWidth - margins.marginR - blockWidth
      : (videoWidth - blockWidth) / 2;
  const blockTop = margins.position === 'middle' ? (videoHeight - blockHeight) / 2
    : margins.position === 'top' ? margins.marginV
      : videoHeight - margins.marginV - blockHeight;
  return {
    blockLeft, blockTop, blockWidth, blockHeight,
    // A hero line is nudged so its baseline sits where an unscaled line in the caption's own face
    // would put it, which keeps baseline-to-baseline gaps even around a bigger word in another face
    // (heroBaselineShift(scale) from layoutCaption; 0 without measured faces).
    lines: placed.map((l) => ({
      words: l.words,
      x: blockLeft + l.start,
      y: blockTop + textTop + l.top + l.height / 2 + (l.hero && heroBaselineShift ? heroBaselineShift(wordScales[l.words[0]] || 1) : 0),
      width: l.width,
      height: l.height,
      hero: l.hero,
    })),
    // Words behind the text: x = left edge, y = where the centre of their capitals goes (the middle
    // of the block), height = their font box.
    behind: behind.map((b) => ({
      index: b.index,
      x: blockLeft + (blockWidth - b.width) / 2,
      y: blockTop + blockHeight / 2,
      width: b.width,
      height: em * (b.scale || 1),
      scale: b.scale || 1,
    })),
  };
};

/**
 * Lay a caption's words out into lines exactly as the burn will render them.
 *
 * @param {object} segment - { text, words: [{ word, start, end }] } (words optional)
 * @param {object} style   - caption style (a template's `style` + user tweaks)
 * @param {object} frame   - { videoWidth, videoHeight, hasDevanagari? }
 * @returns {{
 *   renderScale, fontSize, face, box, margins, availableWidth, spaceWidth,
 *   texts:      string[]                       — display text per word (text-transform applied)
 *   lines:      number[][]                     — word indices per wrapped line
 *   lineWidths: number[]                       — measured width per line (video px)
 *   words:      { index, text, line, x, width }[]  — x = offset from the line's left edge
 *   layout:     'flow' | 'free'
 *   hold, placed — free layout only (see layoutFreeWords)
 * }}
 * Everything is in VIDEO px; the preview converts with (renderedHeight / videoHeight).
 */
// A caption's start in tenths of a second, read from segment.start or its first word's start
// (seconds, or "hh:mm:ss.mmm" / "mm:ss,mmm" strings); null when neither is usable.
const captionStartKey = (segment) => {
  const raw = segment && (segment.start ?? (Array.isArray(segment.words) && segment.words[0] ? segment.words[0].start : undefined));
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.round(raw * 10) : null;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const secs = raw.replace(',', '.').split(':').reduce((acc, part) => acc * 60 + parseFloat(part), 0);
  return Number.isFinite(secs) ? Math.round(secs * 10) : null;
};

const layoutCaption = (segment, style, frame) => {
  const s = normalizeCaptionStyle(style);
  const videoHeight = (frame && frame.videoHeight) || 1080;
  const videoWidth = (frame && frame.videoWidth) || 1920;
  const renderScale = videoHeight / 1080;
  // Text-size scale (renderScale × textScaleFactor): font sizes and em-based widths use it,
  // margins and padding keep renderScale.
  const textScale = renderScale * textScaleFactor(s, videoWidth, videoHeight);
  // frame.hasDevanagari carries the caption script (an INDIC_SCRIPTS id, see captionScript),
  // the legacy true (Devanagari) or false (none). Detected from the segment when absent.
  const script = (frame && frame.hasDevanagari !== undefined) ? indicScript(frame.hasDevanagari) : captionScript([segment]);

  const face = resolveFontFace(s, script);
  const fontSize = burnFontSize(s.fontSize, textScale, face);
  const box = resolveBoxStrategy(s);
  const margins = resolveMargins(s, renderScale);
  const scaledPadding = Math.round((parseFloat(s.padding) || 10) * renderScale);

  // Box styles wrap within the box interior (video width minus margins and box padding);
  // plain styles wrap within ~90% of the video width (the preview caps the overlay at 90cqw).
  const availableWidth = box.useDrawnBox
    ? (videoWidth - margins.marginL - margins.marginR - scaledPadding * 4)
    : (Math.round(videoWidth * 0.9) - margins.marginL - margins.marginR);

  const transform = (s.textTransform !== undefined && s.textTransform !== null) ? s.textTransform : (s.TextTransform || 'none');
  const rawWords = (segment && Array.isArray(segment.words) && segment.words.length > 0)
    ? segment.words.map(w => (w && w.word) || '')
    : ((segment && segment.text) || '').trim().split(/\s+/).filter(Boolean);
  const texts = rawWords.map(t => transformText(t, transform));

  const measure = (t) => estimateTextWidth(t, fontSize, face.bold, face.estFontScale, face.estCharTable);
  // A word's own `scale` (set by clicking it in the editor) widens it; the burn wraps the same way.
  const wordList = (segment && Array.isArray(segment.words)) ? segment.words : [];
  // A word with its own scale never grows wider than a whole line (it shrinks to fit). Words
  // without an override keep scale 1 exactly, so classic captions are unaffected.
  // Hero words (flow word rules) get the hero scale unless they carry their own.
  const hasOwnScale = (i) => wordList[i] && wordList[i].scale !== undefined && wordList[i].scale !== null;
  const wordRules = flowWordRules(wordList, texts, s, frame && frame.rulePicks);
  // Stagger layout: a hero word can take its own case, weight, family and italic
  // (style.heroUppercase or heroTextTransform, heroWeight, heroFontFamily, heroItalic), so it is
  // measured in that face.
  const isStagger = s.layout === 'stagger';
  let heroFace = null;
  let heroFontSize = fontSize;
  let heroItalic = false;
  if (isStagger) {
    if (typeof s.heroTextTransform === 'string') {
      texts.forEach((_, i) => { if (wordRules.heroes[i]) texts[i] = transformText(rawWords[i], s.heroTextTransform); });
    } else if (s.heroUppercase) {
      texts.forEach((t, i) => { if (wordRules.heroes[i]) texts[i] = transformText(t, 'uppercase'); });
    }
    const hasHeroWeight = s.heroWeight !== undefined && s.heroWeight !== null;
    const heroFamily = typeof s.heroFontFamily === 'string' && s.heroFontFamily ? s.heroFontFamily : null;
    if (hasHeroWeight || heroFamily || s.heroItalic) {
      heroFace = resolveFontFace({
        ...s,
        ...(heroFamily ? { fontFamily: heroFamily } : {}),
        ...(hasHeroWeight ? { fontWeight: s.heroWeight, bold: parseInt(s.heroWeight, 10) >= 700 } : {}),
      }, script);
      heroFontSize = burnFontSize(s.fontSize, textScale, heroFace);
      // Indic captions route to Noto / Baloo, which have no italic face.
      heroItalic = !!s.heroItalic && !heroFace.isIndicFace;
    }
  }
  // heroBehind words can be squeezed horizontally (style.heroScaleX, e.g. 0.72 for an ultra-condensed look).
  const behindScaleX = isStagger && s.heroBehind ? Math.min(1, Math.max(0.3, finiteOr(s.heroScaleX, 1))) : 1;
  const measureOf = (i) => (isStagger && heroFace && wordRules.heroes[i]
    ? estimateTextWidth(texts[i], heroFontSize, heroFace.bold, heroFace.estFontScale, heroFace.estCharTable) * behindScaleX
    : measure(texts[i]));
  const scaleOf = (i) => {
    const requested = hasOwnScale(i) ? wordList[i].scale : (wordRules.heroes[i] ? wordRules.heroScale : null);
    return requested === null ? 1 : fitScaleToWidth(clampWordScale(requested), measureOf(i), availableWidth);
  };
  const measureScaled = (t, i) => measureOf(i) * scaleOf(i);
  // The inter-word space has always been measured at regular weight (words at their real weight).
  const spaceWidth = estimateTextWidth(' ', fontSize, false, face.estFontScale, face.estCharTable);
  // Stagger wraps no wider than style.lineMaxWidth (a fraction of the frame) so longer captions step
  // across several short lines; the staircase itself may still use the full width.
  // style.lineMaxEm also caps it in caption font sizes, so on a wide frame the lines (and a
  // heroCenter text box) stay in proportion to the text instead of spreading across the video.
  const lineMaxEm = parseFloat(s.lineMaxEm);
  const staggerWrapWidth = Math.min(
    availableWidth,
    videoWidth * Math.min(1, Math.max(0.2, finiteOr(s.lineMaxWidth, 1))),
    lineMaxEm > 0 ? lineMaxEm * (parseFloat(s.fontSize) || 48) * textScale : Infinity,
  );
  const lines = isStagger
    ? staggerLineBreaks(texts, wordRules.heroes, measureScaled, spaceWidth, staggerWrapWidth,
      { heroOwnLine: s.heroOwnLine !== false, leadAlone: !!s.leadWordAlone, fill: !!s.fillLines, heroBehind: !!s.heroBehind })
    : wrapWordsByWidth(texts, availableWidth, measureScaled, spaceWidth);

  // Word indices per line in the order they are drawn, left to right: the reading order for a
  // left-to-right caption, the word-level bidi order (visualLineOrder) for a right-to-left one,
  // which is where libass puts the words of a whole-line event and where the previews place
  // their boxes. `lines` keeps the reading order, which is what the line's text is built from.
  const rtl = isRtlScript(script);
  const visualLines = rtl ? lines.map((line) => visualLineOrder(line, texts)) : lines;
  const words = [];
  const lineWidths = lines.map((line, li) => {
    let x = 0;
    visualLines[li].forEach((idx, k) => {
      if (k > 0) x += spaceWidth;
      const width = measureScaled(texts[idx], idx);
      words[idx] = { index: idx, text: texts[idx], line: li, x, width };
      x += width;
    });
    return x;
  });
  // heroBehind: hero words sit on no line (line -1); their width is still measured.
  const behindIndices = isStagger && s.heroBehind ? texts.map((_, i) => i).filter((i) => wordRules.heroes[i]) : [];
  behindIndices.forEach((idx) => { words[idx] = { index: idx, text: texts[idx], line: -1, x: 0, width: measureScaled(texts[idx], idx) }; });

  const wordScales = texts.map((_, i) => scaleOf(i));
  const baseWidths = texts.map((t) => measure(t));
  const { heroes, autoHeroes, accents } = wordRules;
  const out = { renderScale, textScale, fontSize, face, box, margins, availableWidth, spaceWidth, texts, lines, visualLines, rtl, lineWidths, words, wordScales, baseWidths, heroes, autoHeroes, accents, wordRules, layout: 'flow' };
  if (isStagger) {
    out.layout = 'stagger';
    out.stagger = placeStaggerLines({ s, lines, lineWidths, wordScales, heroes, availableWidth, videoWidth, videoHeight, renderScale: textScale, margins, seed: hashString(texts.join(' ')), boxWidth: staggerWrapWidth,
      // Both renderers centre a line vertically on its font box, so its baseline sits
      // drop × Fontsize × scale below that centre, deeper for a bigger word and differently per face.
      // y' = y + base drop × Fontsize − hero drop × hero Fontsize × scale.
      heroBaselineShift: heroFace && face.baselineDrop !== null && heroFace.baselineDrop !== null
        ? (scale) => face.baselineDrop * fontSize - heroFace.baselineDrop * heroFontSize * scale
        : null,
      behind: behindIndices.map((idx) => ({ index: idx, width: words[idx].width, scale: scaleOf(idx) })) });
    // `family` is the CSS family for the preview (the burn uses fontName, a weight-specific face);
    // `indicFamily` is the Noto / Baloo family an Indic caption's hero was routed to, which the
    // preview puts first in the hero's stack (the hero family itself has no Indic glyphs, and
    // without it Chrome would fall back to a system face the burn never uses).
    out.stagger.heroFace = heroFace
      ? {
        fontName: heroFace.fontName,
        family: heroFace.baseFontName,
        indicFamily: heroFace.indicFace ? heroFace.indicFace.family : null,
        bold: !heroFace.useWeightFace && heroFace.finalBold === '-1',
        italic: heroItalic,
        fontSize: heroFontSize,
        weight: heroFace.requestedWeight,
        // Lift (in libass Fontsize units) that centres a behind word's capitals on its y; null = none.
        capCenterDrop: heroFace.capCenterDrop,
        // Horizontal squeeze of a behind word (1 = none).
        scaleX: behindScaleX,
        // Optional sheen fill for the hero: CSS-like colour stops, evenly spaced left to right.
        gradient: Array.isArray(s.heroGradient) && s.heroGradient.length > 1 ? s.heroGradient.filter((c) => typeof c === 'string') : null,
      }
      : null;
    out.stagger.blurIn = s.wordEntrance === 'blur';
    // Positioned word tiles: stable geometry for step-reveal handwriting. Tile centers, label
    // bounds and angles are shared by DOM and ASS, never measured during playback. The whole
    // caption turns about the block centre (CSS degrees, negative rises to the right), so every line
    // climbs the same way and each word leans with it. While spoken, a word grows to
    // activeWordScale inside its label (a hero is already big and keeps its size); afterwards an
    // ordinary word settles back to its own size at pastWordOpacity, a hero stays at full strength.
    // Hand placement, seeded by the caption text so every render agrees: the angle falls between
    // wordTiltMin and wordTilt (either way with wordTiltBothWays, or about level for a wordTiltLevel
    // share of captions), lines after the first slide up to lineJitter em left or
    // right, and captionWander moves the caption to the left, the centre or the right of the room
    // the frame leaves beside it (captionWanderWeights sets how often each).
    if (s.wordTiles) {
      const padX = Math.max(0, finiteOr(s.activeWordPillPadX, 6)) * textScale;
      const padY = Math.max(0, finiteOr(s.activeWordPillPadY, 3)) * textScale;
      // Seeded by when the caption starts, so fixing a typo or punctuation keeps its placement;
      // a caption without timing falls back to its text.
      const startKey = captionStartKey(segment);
      const rand = mulberry32(hashString(startKey === null ? `tiles|${texts.join(' ')}` : `tiles@${startKey}`));
      const steepest = Math.min(20, Math.max(-20, finiteOr(s.wordTilt, 0)));
      const gentlest = Math.min(Math.abs(steepest), Math.max(0, finiteOr(s.wordTiltMin, Math.abs(steepest))));
      // wordTiltLevel: the share of captions that stay about level (within ±1.5°). wordTiltBothWays:
      // the rest rise or fall at random instead of always leaning the way wordTilt points.
      const level = rand() < Math.min(1, Math.max(0, finiteOr(s.wordTiltLevel, 0)));
      const direction = s.wordTiltBothWays ? (rand() < 0.5 ? -1 : 1) : Math.sign(steepest);
      const magnitude = gentlest + rand() * (Math.abs(steepest) - gentlest);
      const angle = Math.round((level ? (rand() * 2 - 1) * Math.min(1.5, magnitude) : direction * magnitude) * 10) / 10;
      const lineJitter = Math.max(0, finiteOr(s.lineJitter, 0)) * (parseFloat(s.fontSize) || 40) * textScale;
      const wander = Math.min(1, Math.max(0, finiteOr(s.captionWander, 0)));
      const lineShift = out.stagger.lines.map((_, li) => (li === 0 ? 0 : (rand() * 2 - 1) * lineJitter));
      // captionWanderWeights: how often a caption goes left, centre or right (equal by default).
      const weights = Array.isArray(s.captionWanderWeights) && s.captionWanderWeights.length === 3
        ? s.captionWanderWeights.map((w) => Math.max(0, finiteOr(w, 0))) : [1, 1, 1];
      const pick = rand() * (weights[0] + weights[1] + weights[2] || 1);
      const slot = pick < weights[0] ? -1 : pick < weights[0] + weights[1] ? 0 : 1;
      const activeScale = Math.min(3, Math.max(1, finiteOr(s.activeWordScale, 1)));
      const pastOpacity = Math.min(1, Math.max(0, finiteOr(s.pastWordOpacity, 1)));
      const st = out.stagger;
      const cx = st.blockLeft + st.blockWidth / 2;
      const cy = st.blockTop + st.blockHeight / 2;
      const rad = (angle * Math.PI) / 180;
      const radius = Math.max(0, finiteOr(s.activeWordPillRadius, 5)) * textScale;
      // Safe area: the frame inset by the side margins (at least 4% of the width) and 4% of the height.
      // A side margin past 40% of the width (the API accepts any number) would turn the safe area
      // inside out and scatter the tiles; it is capped so the area always keeps a fifth of the frame.
      const insetX = Math.min(videoWidth * 0.4, Math.max(margins.marginL, margins.marginR, videoWidth * 0.04));
      const insetY = videoHeight * 0.04;
      const safe = { left: insetX, right: videoWidth - insetX, top: insetY, bottom: videoHeight - insetY };
      st.tiles = st.lines.flatMap((ln, li) => ln.words.map((i) => {
        const word = words[i];
        const scale = wordScales[i];
        // A long word only grows as far as its label still fits across the safe area.
        const grow = heroes[i] ? 1 : Math.max(1, Math.min(activeScale, (safe.right - safe.left - padX * 2) / Math.max(1, word.width)));
        const cssFontSize = (parseFloat(s.fontSize) || 40) * textScale * scale;
        const dx = ln.x + lineShift[li] + word.x + word.width / 2 - cx;
        const dy = ln.y - cy;
        return {
          index: i,
          x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
          y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
          // Label bounds at the spoken size.
          width: word.width * grow + padX * 2, height: cssFontSize * grow * 1.1 + padY * 2,
          radius, angle,
          fontSize: (heroes[i] ? heroFontSize : fontSize) * scale, cssFontSize,
          // A word the user coloured keeps its strength too.
          activeScale: grow, pastOpacity: heroes[i] || (wordList[i] && typeof wordList[i].color === 'string' && wordList[i].color) ? 1 : pastOpacity,
        };
      }));
      // The tilt, the spoken growth and many lines can carry labels past the frame. Shrink the
      // caption about its centre until every turned label fits the safe area, wander within the
      // room left beside it, then slide it inside.
      const cos = Math.abs(Math.cos(rad));
      const sin = Math.abs(Math.sin(rad));
      const bounds = () => st.tiles.reduce((bb, t) => {
        const hx = (t.width / 2) * cos + (t.height / 2) * sin;
        const hy = (t.width / 2) * sin + (t.height / 2) * cos;
        return { minX: Math.min(bb.minX, t.x - hx), maxX: Math.max(bb.maxX, t.x + hx), minY: Math.min(bb.minY, t.y - hy), maxY: Math.max(bb.maxY, t.y + hy) };
      }, { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });
      let b = bounds();
      const fit = Math.min(1, (safe.right - safe.left) / Math.max(1, b.maxX - b.minX), (safe.bottom - safe.top) / Math.max(1, b.maxY - b.minY));
      if (fit < 1) {
        st.tiles.forEach((t) => {
          t.x = cx + (t.x - cx) * fit;
          t.y = cy + (t.y - cy) * fit;
          ['width', 'height', 'radius', 'fontSize', 'cssFontSize'].forEach((k) => { t[k] *= fit; });
        });
        b = bounds();
      }
      const room = Math.max(0, (safe.right - safe.left - (b.maxX - b.minX)) / 2);
      const wanderX = slot * room * wander;
      const shiftX = b.minX + wanderX < safe.left ? safe.left - b.minX : b.maxX + wanderX > safe.right ? safe.right - b.maxX : wanderX;
      const shiftY = b.minY < safe.top ? safe.top - b.minY : b.maxY > safe.bottom ? safe.bottom - b.maxY : 0;
      if (shiftX || shiftY) st.tiles.forEach((t) => { t.x += shiftX; t.y += shiftY; });
    }
  }
  if (s.layout === 'free') {
    const free = layoutFreeWords(segment, s, frame, { videoWidth, videoHeight, fontSize, face, texts });
    out.layout = 'free';
    out.hold = free.hold;
    out.rules = free.rules;
    out.placed = free.placed;
  }
  return out;
};


export {
  LIBASS_FONT_COMPENSATION,
  WEIGHT_FACES,
  FONT_WIDTH_SCALE,
  fontWidthScale,
  INTER_RENDER_RATIO,
  INTER_CHAR_WIDTHS,
  NOTO_DEVA_CHAR_WIDTHS,
  NOTO_DEVA_RENDER_RATIO,
  NOTO_DEVA_SIZE_COMPENSATION,
  notoDevaWidthFactor,
  BALOO_RENDER_RATIO,
  BALOO_SIZE_COMPENSATION,
  NOTO_DEVA_WIN_CENTRE_EM,
  NOTO_DEVA_TYPO_CENTRE_EM,
  BALOO_WIN_CENTRE_EM,
  BALOO_TYPO_CENTRE_EM,
  devanagariInkExtents,
  NOTO_BENG_CHAR_WIDTHS,
  NOTO_BENG_RENDER_RATIO,
  NOTO_BENG_SIZE_COMPENSATION,
  notoBengWidthFactor,
  BALOO_DA_RENDER_RATIO,
  BALOO_DA_SIZE_COMPENSATION,
  NOTO_BENG_WIN_CENTRE_EM,
  NOTO_BENG_TYPO_CENTRE_EM,
  BALOO_DA_WIN_CENTRE_EM,
  BALOO_DA_TYPO_CENTRE_EM,
  bengaliInkExtents,
  NOTO_TEL_CHAR_WIDTHS,
  NOTO_TEL_RENDER_RATIO,
  NOTO_TEL_SIZE_COMPENSATION,
  notoTelWidthFactor,
  BALOO_TAMMUDU_RENDER_RATIO,
  BALOO_TAMMUDU_SIZE_COMPENSATION,
  balooTammuduWidthFactor,
  NOTO_TEL_WIN_CENTRE_EM,
  NOTO_TEL_TYPO_CENTRE_EM,
  BALOO_TAMMUDU_WIN_CENTRE_EM,
  BALOO_TAMMUDU_TYPO_CENTRE_EM,
  teluguInkExtents,
  NOTO_TAM_CHAR_WIDTHS,
  TAM_U_LIGATURE_WIDTHS,
  TAM_UU_LIGATURE_WIDTHS,
  NOTO_TAM_RENDER_RATIO,
  NOTO_TAM_SIZE_COMPENSATION,
  notoTamWidthFactor,
  BALOO_THAMBI_RENDER_RATIO,
  BALOO_THAMBI_SIZE_COMPENSATION,
  balooThambiWidthFactor,
  NOTO_TAM_WIN_CENTRE_EM,
  NOTO_TAM_TYPO_CENTRE_EM,
  BALOO_THAMBI_WIN_CENTRE_EM,
  BALOO_THAMBI_TYPO_CENTRE_EM,
  tamilInkExtents,
  NOTO_GUJ_CHAR_WIDTHS,
  GUJ_HALF_WIDTHS,
  GUJ_LIGATURE_WIDTHS,
  GUJ_RU_WIDTH,
  NOTO_GUJ_RENDER_RATIO,
  NOTO_GUJ_SIZE_COMPENSATION,
  notoGujWidthFactor,
  BALOO_BHAI_RENDER_RATIO,
  BALOO_BHAI_SIZE_COMPENSATION,
  balooBhaiWidthFactor,
  NOTO_GUJ_WIN_CENTRE_EM,
  NOTO_GUJ_TYPO_CENTRE_EM,
  BALOO_BHAI_WIN_CENTRE_EM,
  BALOO_BHAI_TYPO_CENTRE_EM,
  gujaratiInkExtents,
  NOTO_ARAB_CHAR_WIDTHS,
  NOTO_ARAB_INK_CODES,
  NOTO_ARAB_RENDER_RATIO,
  NOTO_ARAB_SIZE_COMPENSATION,
  notoArabWidthFactor,
  NOTO_ARAB_WIN_CENTRE_EM,
  NOTO_ARAB_TYPO_CENTRE_EM,
  BALOO_BHAIJAAN_CHAR_WIDTHS,
  BALOO_BHAIJAAN_INK_CODES,
  BALOO_BHAIJAAN_RENDER_RATIO,
  BALOO_BHAIJAAN_SIZE_COMPENSATION,
  balooBhaijaanWidthFactor,
  BALOO_BHAIJAAN_WIN_CENTRE_EM,
  BALOO_BHAIJAAN_TYPO_CENTRE_EM,
  arabicFormIndex,
  arabicInkExtents,
  INDIC_SCRIPTS,
  INDIC_SCRIPT_IDS,
  estimateTextWidth,
  normalizeCaptionStyle,
  hasDevanagariText,
  hasBengaliText,
  hasTeluguText,
  hasTamilText,
  hasGujaratiText,
  hasArabicText,
  captionScript,
  indicScript,
  isRtlScript,
  wordBidiClass,
  visualLineOrder,
  resolveFontFace,
  burnFontSize,
  textScaleFactor,
  transformText,
  resolveBoxStrategy,
  resolveMargins,
  wrapWordsByWidth,
  wrapWordsBalanced,
  WORD_SCALE_MIN,
  WORD_SCALE_MAX,
  clampWordScale,
  fitScaleToWidth,
  DEFAULT_WORD_RULES,
  resolveWordRules,
  pickHeroWords,
  transcriptWordRules,
  TYPEWRITER_MAX_CHAR_SECONDS,
  flowWordRules,
  TYPEWRITER_BOX_LAST_HOLD_SECONDS,
  TYPEWRITER_BOX_OPACITY,
  TYPEWRITER_FADE_OUT_MS,
  splitGraphemes,
  typewriterCharOffsets,
  typewriterTimeline,
  STAGGER_LINE_OVERLAP,
  STAGGER_WORD_FADE_MS,
  STAGGER_FADE_OUT_MS,
  STAGGER_BLUR_IN_MS,
  STAGGER_BLUR_EM,
  STAGGER_BEHIND_IN_MS,
  staggerLineBreaks,
  staggerLineTarget,
  partitionLines,
  layoutFreeWords,
  layoutCaption,
  WORD_HIGHLIGHT_HOLD_SECONDS,
  wordHighlightEnd,
};
